import React, { useState, useRef, useCallback, useEffect, useMemo, memo } from 'react';

/**
 * CONVERSATION CHALLENGE FLOW EDITOR
 * Visual drag-and-drop editor for Darija learning conversations
 * 
 * Features: Story Mode, Audio, Achievements, Branching, Tips, Vocabulary Cards
 * 
 * Accessibility:
 * - ARIA labels for all interactive elements
 * - Keyboard navigation support
 * - Screen reader announcements
 * - Focus management
 * 
 * Performance:
 * - Memoized components
 * - Optimized canvas rendering
 * - Debounced saves
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Node Types
const NODE_TYPES = {
  START: 'start',
  MESSAGE: 'message',
  RESPONSE: 'response',
  TIP: 'tip',
  ACHIEVEMENT: 'achievement',
  AUDIO: 'audio',
  VOCABULARY: 'vocabulary',
  DELAY: 'delay',
  CHALLENGE: 'challenge',
  BRANCH: 'branch',
  END: 'end'
};

// Node colors by type
const NODE_COLORS = {
  [NODE_TYPES.START]: { bg: '#10B981', border: '#059669', icon: '▶️', label: 'Start' },
  [NODE_TYPES.MESSAGE]: { bg: '#3B82F6', border: '#2563EB', icon: '💬', label: 'Message' },
  [NODE_TYPES.RESPONSE]: { bg: '#F59E0B', border: '#D97706', icon: '❓', label: 'Response' },
  [NODE_TYPES.TIP]: { bg: '#8B5CF6', border: '#7C3AED', icon: '💡', label: 'Tip' },
  [NODE_TYPES.ACHIEVEMENT]: { bg: '#EC4899', border: '#DB2777', icon: '🏆', label: 'Achievement' },
  [NODE_TYPES.AUDIO]: { bg: '#14B8A6', border: '#0D9488', icon: '🔊', label: 'Audio' },
  [NODE_TYPES.VOCABULARY]: { bg: '#6366F1', border: '#4F46E5', icon: '📚', label: 'Vocabulary' },
  [NODE_TYPES.DELAY]: { bg: '#64748B', border: '#475569', icon: '⏱️', label: 'Delay' },
  [NODE_TYPES.CHALLENGE]: { bg: '#EF4444', border: '#DC2626', icon: '🎯', label: 'Challenge' },
  [NODE_TYPES.BRANCH]: { bg: '#22D3EE', border: '#06B6D4', icon: '🔀', label: 'Branch' },
  [NODE_TYPES.END]: { bg: '#F43F5E', border: '#E11D48', icon: '🔚', label: 'End' }
};

// Generate unique ID
const generateId = () => `node_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// URL validation for media fields
const ALLOWED_MEDIA_DOMAINS = [
  'dl.dropboxusercontent.com', 'www.dropbox.com', 'dropbox.com',
  'media.giphy.com', 'media0.giphy.com', 'media1.giphy.com',
  'media2.giphy.com', 'media3.giphy.com', 'media4.giphy.com',
  'i.giphy.com', 'giphy.com',
  'tenor.com', 'media.tenor.com', 'c.tenor.com',
  'i.imgur.com', 'imgur.com'
];
const isValidMediaUrl = (url) => {
  if (!url || !url.trim()) return true;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false;
    return ALLOWED_MEDIA_DOMAINS.some(d => parsed.hostname === d || parsed.hostname.endsWith('.' + d));
  } catch { return false; }
};

// Default node data by type
const getDefaultNodeData = (type) => {
  switch (type) {
    case NODE_TYPES.START:
      return { label: 'Start' };
    case NODE_TYPES.MESSAGE:
      return {
        speaker: 'bot',
        text_darija: '',
        text_arabic: '',
        translation: '',
        emotion: 'neutral',
        audioUrl: '',
        imageUrl: '',
        gifUrl: '',
        gifUrls: [],
        nextNodeId: null
      };
    case NODE_TYPES.RESPONSE:
      return {
        prompt: 'What do you say?',
        inputType: 'choice', // choice, text
        options: [
          { id: generateId(), text: 'Option 1', text_arabic: '', isCorrect: true, points: 10, nextNodeId: null },
          { id: generateId(), text: 'Option 2', text_arabic: '', isCorrect: false, points: 0, nextNodeId: null }
        ],
        acceptAny: false,
        mustInclude: '',
        wrongPathNodeId: null
      };
    case NODE_TYPES.TIP:
      return {
        tipText: 'Helpful tip here...',
        tipText_arabic: '',
        tipType: 'info', // info, warning, success
        nextNodeId: null
      };
    case NODE_TYPES.ACHIEVEMENT:
      return {
        achievementName: 'New Achievement!',
        achievementIcon: '🏆',
        points: 10,
        message: 'You earned an achievement!',
        nextNodeId: null
      };
    case NODE_TYPES.AUDIO:
      return {
        audioUrl: '',
        autoPlay: true,
        nextNodeId: null
      };
    case NODE_TYPES.VOCABULARY:
      return {
        wordDarija: '',
        wordArabic: '',
        translation: '',
        pronunciation: '',
        example: '',
        audioUrl: '',
        nextNodeId: null
      };
    case NODE_TYPES.DELAY:
      return {
        delayMs: 1500,
        showTyping: true,
        nextNodeId: null
      };
    case NODE_TYPES.CHALLENGE:
      return {
        challengeType: 'choice', // choice, unscramble, fill_blank
        question: 'Challenge question...',
        question_arabic: '',
        options: ['Option A', 'Option B', 'Option C'],
        correctAnswer: 'Option A',
        points: 15,
        correctPathNodeId: null,
        wrongPathNodeId: null
      };
    case NODE_TYPES.BRANCH:
      return {
        randomize: true,
        branches: [
          { id: generateId(), label: 'Path A', weight: 1, nextNodeId: null },
          { id: generateId(), label: 'Path B', weight: 1, nextNodeId: null }
        ]
      };
    case NODE_TYPES.END:
      return {
        message: 'Conversation complete!',
        summary: []
      };
    default:
      return {};
  }
};

// ============================================
// MAIN COMPONENT
// ============================================
const ConversationChallengeEditor = ({ isDarkMode = true }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('flows'); // flows, stories, bots, settings
  
  // Flow states
  const [flows, setFlows] = useState([]);
  const [selectedFlow, setSelectedFlow] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Story states
  const [stories, setStories] = useState([]);
  const [selectedStory, setSelectedStory] = useState(null);
  const [showNewStoryForm, setShowNewStoryForm] = useState(false);
  const [newStoryData, setNewStoryData] = useState({
    name: '',
    description: '',
    bot_gender: 'female',
    script_type: 'latin',
    difficulty: 'Beginner',
    is_free: true
  });
  
  // Bot states - now supports multiple bots with CRUD
  const [bots, setBots] = useState([]);
  const [showBotForm, setShowBotForm] = useState(false);
  const [editingBot, setEditingBot] = useState(null);
  const [newBotData, setNewBotData] = useState({
    id: '',
    name: '',
    icon: '👤',
    avatarUrl: '', // Dropbox link for avatar image (priority over icon)
    gender: 'female',
    color: 'blue',
    personality: 'friendly',
    greeting: '',
    background: '',
    is_smart_bot: false
  });

  // Story editing state
  const [editingStory, setEditingStory] = useState(null);

  // Smart Bot Intents states
  const [intents, setIntents] = useState([]);
  const [showIntentForm, setShowIntentForm] = useState(false);
  const [editingIntent, setEditingIntent] = useState(null);
  const [newIntentData, setNewIntentData] = useState({
    name: '',
    display_name: '',
    description: '',
    phrases: [],
    is_active: true
  });
  const [newPhrase, setNewPhrase] = useState('');

  // Train Bot states
  const [trainedConversations, setTrainedConversations] = useState([]);
  const [trainingMode, setTrainingMode] = useState(false);
  const [trainingChat, setTrainingChat] = useState([]);
  const [trainingSteps, setTrainingSteps] = useState([]);
  const [trainingPhase, setTrainingPhase] = useState('topic');
  const [trainingInput, setTrainingInput] = useState('');
  const [currentTrainingData, setCurrentTrainingData] = useState({});
  const [editingTrainedConv, setEditingTrainedConv] = useState(null);
  const [nodeEditForm, setNodeEditForm] = useState(null); // { type, fields... } — when a node button is clicked, show edit form
  const [editingStepIndex, setEditingStepIndex] = useState(null);
  const [showStepsList, setShowStepsList] = useState(false);
  const [trainingBotId, setTrainingBotId] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [trainingPhaseHistory, setTrainingPhaseHistory] = useState([]); // undo stack
  const [trainingGenderMode, setTrainingGenderMode] = useState('auto'); // auto or ask or neutral

  // Fullscreen canvas state
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    audio_enabled: true,
    achievements_enabled: true,
    story_mode_enabled: true,
    randomization_enabled: true,
    hints_enabled: true,
    streak_tracking: true,
    time_challenges_enabled: false,
    default_hint_delay: 2
  });
  
  // Game visibility settings (like Morocco Journey)
  const [gameSettings, setGameSettings] = useState({
    is_visible: true,
    is_pro: false,
    name: 'Conversation Challenge',
    description: 'Practice real conversations in Darija'
  });
  const [savingGameCard, setSavingGameCard] = useState(false);
  
  // User avatars state (admin-managed avatars for player selection)
  const [userAvatars, setUserAvatars] = useState({
    male: [
      { name: '', url: '' },
      { name: '', url: '' },
      { name: '', url: '' }
    ],
    female: [
      { name: '', url: '' },
      { name: '', url: '' },
      { name: '', url: '' }
    ]
  });
  
  // Groq AI Dictionary state
  const [groqEnabled, setGroqEnabled] = useState(false);
  const [groqApiKeySet, setGroqApiKeySet] = useState(false);
  const [groqApiKeyMasked, setGroqApiKeyMasked] = useState('');
  const [groqApiKeyInput, setGroqApiKeyInput] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);
  const [groqSearchWord, setGroqSearchWord] = useState('');
  const [groqSearchResult, setGroqSearchResult] = useState(null);
  const [groqSearching, setGroqSearching] = useState(false);
  const [groqCachedWords, setGroqCachedWords] = useState([]);
  const [savedVocabulary, setSavedVocabulary] = useState([]);
  const [editingAiEntry, setEditingAiEntry] = useState(null); // For editing AI-generated entries
  const [pydodaStats, setPydodaStats] = useState(null); // PyDODa library stats
  
  // Vocabulary Table Management
  const [vocabTableSearch, setVocabTableSearch] = useState('');
  const [vocabTablePage, setVocabTablePage] = useState(1);
  const [vocabTablePerPage, setVocabTablePerPage] = useState(20);
  const [vocabTableSort, setVocabTableSort] = useState({ field: 'darija', direction: 'asc' });
  const [editingVocabWord, setEditingVocabWord] = useState(null); // For editing vocabulary popup
  const [showAddWordModal, setShowAddWordModal] = useState(false); // For adding new word
  const [newWord, setNewWord] = useState({
    darija: '', arabic: '', english: '', meaning: '', phonetic: '', wordType: '',
    grammarNote: '', conjugation: '', genderNote: '', culturalNote: '',
    examples: [], relatedWords: [], category: '', audioUrl: ''
  });
  const [confirmDeleteAll, setConfirmDeleteAll] = useState(null); // 'vocabulary' | 'cache' | null
  const [confirmAction, setConfirmAction] = useState(null); // { message, onConfirm } for styled confirm modal
  const [selectedVocabWords, setSelectedVocabWords] = useState([]); // For multi-select delete
  
  // Bulk Upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadText, setBulkUploadText] = useState('');
  const [bulkUploadPreview, setBulkUploadPreview] = useState([]);
  const [bulkUploadError, setBulkUploadError] = useState('');
  
  // Delete confirmation state
  const [deleteConfirmWord, setDeleteConfirmWord] = useState(null);

  // Fuzzy Match settings state
  const [fuzzySettings, setFuzzySettings] = useState({
    enabled: false, threshold: 75, show_tip: true,
    tip_template: 'Correct! Common spelling: {answer}',
    equivalences: []
  });
  const [fuzzyLoaded, setFuzzyLoaded] = useState(false);
  const [fuzzyTestInput, setFuzzyTestInput] = useState('');
  const [fuzzyTestAnswers, setFuzzyTestAnswers] = useState('');
  const [fuzzyTestResult, setFuzzyTestResult] = useState(null);
  const [fuzzyTesting, setFuzzyTesting] = useState(false);
  const [fuzzyNewFrom, setFuzzyNewFrom] = useState('');
  const [fuzzyNewTo, setFuzzyNewTo] = useState('');
  const [fuzzyEditingIdx, setFuzzyEditingIdx] = useState(null);
  const [fuzzyEditFrom, setFuzzyEditFrom] = useState('');
  const [fuzzyEditTo, setFuzzyEditTo] = useState('');
  const [spellingGroups, setSpellingGroups] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [editGroupVariants, setEditGroupVariants] = useState('');
  const [editGroupMainWord, setEditGroupMainWord] = useState('');

  // Canvas state
  const canvasRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [draggingNode, setDraggingNode] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  
  // New flow form
  const [showNewFlowForm, setShowNewFlowForm] = useState(false);
  const [newFlowData, setNewFlowData] = useState({
    name: '',
    description: '',
    bot_id: '',  // Required: specific bot ID
    bot_gender: 'female',
    script_type: 'latin',
    difficulty: 'Beginner',
    category: 'General',
    is_free: true,
    vocabulary: [],  // Dictionary words: [{darija, meaning, phonetic, category, audioUrl}]
    quiz_enabled: true,
    quiz_difficulty: 'auto',
    custom_quiz_questions: []
  });
  
  // Dictionary management states
  const [vocabSearchQuery, setVocabSearchQuery] = useState('');
  const [vocabBulkInput, setVocabBulkInput] = useState('');
  const [showVocabManager, setShowVocabManager] = useState(false);
  const [vocabCategories] = useState(['Greetings', 'Food', 'Shopping', 'Travel', 'Family', 'Numbers', 'Colors', 'Time', 'Weather', 'Other']);
  
  // Quiz editor states
  const [showQuizEditor, setShowQuizEditor] = useState(false);
  const [editingQuizQuestion, setEditingQuizQuestion] = useState(null);
  
  // Filter state
  const [flowFilter, setFlowFilter] = useState('all'); // all, female, male
  
  // Notification
  const [notification, setNotification] = useState(null);
  
  // Show notification
  const showNotification = (message, type = 'info') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };
  
  // Define functions first
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load flows
      const flowsRes = await fetch(`${API_URL}/api/conversation-challenge/flows`);
      const flowsData = await flowsRes.json();
      setFlows(flowsData.flows || []);
      
      // Load stories
      const storiesRes = await fetch(`${API_URL}/api/conversation-challenge/stories`);
      const storiesData = await storiesRes.json();
      setStories(storiesData.stories || []);
      
      // Load settings
      const settingsRes = await fetch(`${API_URL}/api/conversation-challenge/settings`);
      const settingsData = await settingsRes.json();
      setSettings(prev => ({ ...prev, ...settingsData }));
      
    } catch (error) {
      console.error('Error loading data:', error);
      showNotification('Failed to load data', 'error');
    }
    setLoading(false);
  }, []);
  
  // Load bots configuration - handles both array and object format
  const loadBots = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/bots`);
      if (response.ok) {
        const data = await response.json();
        if (data.bots) {
          // If it's an array, use as is
          if (Array.isArray(data.bots)) {
            setBots(data.bots);
          } else {
            // Convert old object format to array
            const botsArray = Object.entries(data.bots).map(([key, value]) => ({
              id: key,
              ...value,
              gender: key
            }));
            setBots(botsArray);
          }
        }
      }
    } catch (error) {
      console.log('Using default bots configuration');
      // Set default bots
      setBots([
        { id: 'female', name: 'Fatima', icon: '👩', gender: 'female', color: 'pink', personality: 'friendly', greeting: 'Salam! Ana Fatima 😊' },
        { id: 'male', name: 'Ahmed', icon: '👨', gender: 'male', color: 'blue', personality: 'teacher', greeting: 'Ahlan! Ana Ahmed' }
      ]);
    }
  }, []);
  
  // Load game visibility settings
  const loadGameSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/game-settings`);
      if (response.ok) {
        const data = await response.json();
        setGameSettings(data);
      }
    } catch (error) {
      console.log('Using default game settings');
    }
  }, []);
  
  // Load smart bot intents
  const loadIntents = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/intents`);
      if (response.ok) {
        const data = await response.json();
        setIntents(data.intents || []);
      }
    } catch (error) {
      console.log('Error loading intents:', error);
    }
  }, []);

  // Load user avatars
  const loadUserAvatars = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/user-avatars`);
      if (response.ok) {
        const data = await response.json();
        if (data.avatars) {
          // Merge with defaults to ensure we always have 3 slots for each gender
          const defaultAvatars = {
            male: [{ name: '', url: '' }, { name: '', url: '' }, { name: '', url: '' }],
            female: [{ name: '', url: '' }, { name: '', url: '' }, { name: '', url: '' }]
          };
          const mergedAvatars = {
            male: (data.avatars.male || []).length > 0 
              ? data.avatars.male.concat(defaultAvatars.male).slice(0, 3)
              : defaultAvatars.male,
            female: (data.avatars.female || []).length > 0 
              ? data.avatars.female.concat(defaultAvatars.female).slice(0, 3)
              : defaultAvatars.female
          };
          setUserAvatars(mergedAvatars);
        }
      }
    } catch (error) {
      console.log('Using default user avatars');
    }
  }, []);
  
  // Save user avatars
  const saveUserAvatars = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/user-avatars`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ avatars: userAvatars })
      });
      if (response.ok) {
        showNotification('User avatars saved!', 'success');
      }
    } catch (error) {
      showNotification('Error saving user avatars', 'error');
    }
  };
  
  // Load trained conversations
  const loadTrainedConversations = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/trained-conversations`);
      if (response.ok) {
        const data = await response.json();
        setTrainedConversations(data.conversations || []);
      }
    } catch (error) {
      console.log('Error loading trained conversations:', error);
    }
  }, []);

  // Detect intent via backend (used during training)
  const detectIntentAPI = async (phrase) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/detect-intent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ phrase })
      });
      if (response.ok) return await response.json();
    } catch (error) {
      console.log('Error detecting intent:', error);
    }
    return { intent: null };
  };

  // Check if any step has male variants (for auto-detect gender mode)
  const stepsHaveMaleVariants = (steps) => {
    return steps.some(s =>
      s.bot_message_male || s.correct_response_male || s.text_male ||
      s.bot_message_arabic_male || s.correct_response_arabic_male ||
      (s.branches && s.branches.some(b => b.response_male))
    );
  };

  // Resolve gender mode: auto → ask (if male variants) or neutral (if none)
  const resolveGenderMode = (steps) => {
    if (trainingGenderMode === 'ask') return 'ask';
    if (trainingGenderMode === 'neutral') return 'neutral';
    // auto: detect from content
    return stepsHaveMaleVariants(steps) ? 'ask' : 'neutral';
  };

  // Save trained conversation to backend
  const saveTrainedConversation = async (title, description, steps) => {
    try {
      const gender_mode = resolveGenderMode(steps);
      const response = await fetch(`${API_URL}/api/conversation-challenge/trained-conversations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, description, steps, bot_id: trainingBotId || 'default', is_active: true, is_free: true, difficulty: 'beginner', gender_mode })
      });
      if (response.ok) {
        showNotification('Trained conversation saved!', 'success');
        loadTrainedConversations();
        return true;
      }
    } catch (error) {
      showNotification('Error saving trained conversation', 'error');
    }
    return false;
  };

  // Update existing trained conversation (PUT)
  const updateTrainedConversation = async (convId, title, steps) => {
    try {
      const gender_mode = resolveGenderMode(steps);
      const response = await fetch(`${API_URL}/api/conversation-challenge/trained-conversations/${convId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, steps, bot_id: trainingBotId || 'default', gender_mode })
      });
      if (response.ok) {
        showNotification('Conversation updated!', 'success');
        loadTrainedConversations();
        return true;
      }
    } catch (error) {
      showNotification('Error updating conversation', 'error');
    }
    return false;
  };

  // Delete trained conversation
  const deleteTrainedConversation = (id) => {
    setConfirmAction({
      message: 'Delete this trained conversation?',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await fetch(`${API_URL}/api/conversation-challenge/trained-conversations/${id}`, { method: 'DELETE' });
          if (response.ok) {
            showNotification('Deleted!', 'success');
            loadTrainedConversations();
          }
        } catch (error) {
          showNotification('Error deleting', 'error');
        }
      }
    });
  };

  // Toggle trained conversation active state
  const toggleTrainedActive = async (conv) => {
    try {
      await fetch(`${API_URL}/api/conversation-challenge/trained-conversations/${conv._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_active: !conv.is_active })
      });
      loadTrainedConversations();
    } catch (error) {
      showNotification('Error updating', 'error');
    }
  };

  // Training chat — handle admin input based on current phase
  // Helper: add chat bubble
  const addTrainingChat = (sender, msg) => setTrainingChat(prev => [...prev, { sender, text: msg }]);

  // Save a completed step and pause for "continue or end"
  const saveTrainingStep = (stepData) => {
    const step = { ...stepData, step_number: trainingSteps.length + 1 };
    if (!step.type) step.type = 'exchange';
    setTrainingSteps(prev => [...prev, step]);
    setCurrentTrainingData(prev => ({ title: prev.title })); // Keep title, clear rest
    const typeLabel = step.type === 'exchange' ? 'Exchange' : step.type.charAt(0).toUpperCase() + step.type.slice(1);
    addTrainingChat('bot', `✅ Step ${step.step_number} saved! (${typeLabel})`);
    setTrainingPhase('choose_next'); // Pause — user picks "continue" or "end & save"
  };

  // Undo last training step
  const undoLastStep = () => {
    if (trainingSteps.length === 0) return;
    setTrainingSteps(prev => prev.slice(0, -1));
    // Remove chat messages back to last "Step X saved" or start
    setTrainingChat(prev => {
      const lastSaveIdx = prev.map((m, i) => m.text?.includes('Step ') && m.text?.includes('saved') ? i : -1).filter(i => i >= 0);
      if (lastSaveIdx.length >= 2) {
        return prev.slice(0, lastSaveIdx[lastSaveIdx.length - 2] + 1);
      } else if (lastSaveIdx.length === 1) {
        // Only one "saved" message — go back to after topic
        const topicIdx = prev.findIndex(m => m.text?.includes('Topic:'));
        return topicIdx >= 0 ? prev.slice(0, topicIdx + 1) : prev.slice(0, 1);
      }
      return prev;
    });
    setTrainingPhase('bot_message');
    addTrainingChat('bot', `↩️ Last step removed. ${trainingSteps.length - 1} step(s) remaining. 🤖 BOT's turn — type message, click a node, or "done".`);
  };

  // Edit a specific step — loads it into the right panel form
  const startEditStep = (idx) => {
    const step = trainingSteps[idx];
    if (!step) return;
    setEditingStepIndex(idx);
    if (step.type === 'exchange' && step.branches && step.branches.length > 0) {
      // Branching exchange — open as branch_exchange form
      setNodeEditForm({
        type: 'branch_exchange',
        id: step.id || '',
        bot_message: step.bot_message || '',
        bot_message_male: step.bot_message_male || '',
        bot_message_arabic: step.bot_message_arabic || '',
        bot_message_arabic_male: step.bot_message_arabic_male || '',
        branches: step.branches.map(b => ({
          id: b.id || generateId(),
          keywords: Array.isArray(b.keywords) ? b.keywords.join(', ') : (b.keywords || ''),
          intent: b.intent || '',
          response: b.response || '',
          response_male: b.response_male || '',
          hint: b.hint || '',
          points: b.points || 10,
          next_step_id: b.next_step_id || ''
        })),
        // Fallback
        fallback_response: step.fallback_response || '',
        fallback_response_male: step.fallback_response_male || '',
        // Media fields
        audioUrl: step.audioUrl || '',
        audioUrl_male: step.audioUrl_male || '',
        imageUrl: step.imageUrl || '',
        gifUrl: step.gifUrl || '',
        listenPrompt: step.listenPrompt || false,
        listenPromptText: step.listenPromptText || '',
        listenPromptText_arabic: step.listenPromptText_arabic || '',
        listenAudioUrl: step.listenAudioUrl || '',
        listenAudioUrl_male: step.listenAudioUrl_male || '',
      });
    } else if (step.type === 'exchange') {
      setNodeEditForm({
        type: 'exchange',
        bot_message: step.bot_message || '',
        bot_message_male: step.bot_message_male || '',
        bot_message_arabic: step.bot_message_arabic || '',
        bot_message_arabic_male: step.bot_message_arabic_male || '',
        expected_intent: step.expected_intent || '',
        expected_keywords: step.expected_keywords || [],
        correct_response: step.correct_response || '',
        correct_response_male: step.correct_response_male || '',
        correct_response_arabic: step.correct_response_arabic || '',
        correct_response_arabic_male: step.correct_response_arabic_male || '',
        hint: step.hint || '',
        points: step.points || 10,
        // Media fields
        audioUrl: step.audioUrl || '',
        audioUrl_male: step.audioUrl_male || '',
        imageUrl: step.imageUrl || '',
        gifUrl: step.gifUrl || '',
        correctResponseAudioUrl: step.correctResponseAudioUrl || '',
        correctResponseAudioUrl_male: step.correctResponseAudioUrl_male || '',
        // Listen prompt
        listenPrompt: step.listenPrompt || false,
        listenPromptText: step.listenPromptText || '',
        listenPromptText_arabic: step.listenPromptText_arabic || '',
        listenAudioUrl: step.listenAudioUrl || '',
        listenAudioUrl_male: step.listenAudioUrl_male || '',
      });
    } else {
      setNodeEditForm({ ...step });
    }
  };

  // Delete a specific step by index
  const deleteStep = (idx) => {
    setTrainingSteps(prev => {
      const newSteps = prev.filter((_, i) => i !== idx);
      return newSteps.map((s, i) => ({ ...s, step_number: i + 1 }));
    });
    if (editingStepIndex === idx) {
      setEditingStepIndex(null);
      setNodeEditForm(null);
    }
    addTrainingChat('bot', `🗑️ Step ${idx + 1} deleted.`);
  };

  // Click intent from toolkit panel — works for BOTH user_input and bot_message phases
  const handleIntentClick = (intent) => {
    const name = intent.display_name || intent.name;
    const phraseCount = intent.phrases?.length || 0;
    if (trainingPhase === 'user_input') {
      addTrainingChat('admin', `[Intent: ${name}]`);
      setCurrentTrainingData(prev => ({
        ...prev,
        detected_intent: intent.id || intent.name,
        detected_display: name,
        custom_keywords: intent.phrases?.slice(0, 5) || []
      }));
      addTrainingChat('bot', `👤 USER intent: [${name}] selected (${phraseCount} phrases). 🤖 What should the BOT reply when correct?`);
      setTrainingPhase('bot_response');
      setTrainingInput('');
    } else if (trainingPhase === 'bot_message') {
      // Bot uses this intent — auto-fill first phrase as bot message
      const firstPhrase = intent.phrases?.[0] || name;
      addTrainingChat('admin', `[Bot uses intent: ${name}] → "${firstPhrase}"`);
      setCurrentTrainingData(prev => ({ ...prev, bot_message: firstPhrase }));
      addTrainingChat('bot', `🤖 Bot will say: "${firstPhrase}" (from [${name}]). 👤 Now: what should the USER say back?`);
      setTrainingPhase('user_input');
      setTrainingInput('');
    }
  };

  // Click node type from toolkit panel — opens edit form on the right
  const handleNodeClick = (nodeType) => {
    if (trainingPhase === 'topic') {
      addTrainingChat('bot', 'Set the topic first, then you can add nodes.');
      return;
    }
    // Open node edit form with default fields for each type
    const defaults = {
      achievement: { type: 'achievement', name: '', icon: '🏆', points: 10, message: '' },
      tip: { type: 'tip', text: '', tipText_arabic: '', tipType: 'info', audioUrl: '' },
      vocabulary: { type: 'vocabulary', darija: '', english: '', arabic: '', phonetic: '', example: '', audioUrl: '' },
      audio: { type: 'audio', audioUrl: '', autoPlay: true },
      quiz: { type: 'quiz', challengeType: 'choice', question: '', question_arabic: '', correctAnswer: '', points: 15 },
      message: { type: 'message', text: '', text_arabic: '', translation: '', vocabulary: [], emotion: 'neutral', audioUrl: '', imageUrl: '', gifUrls: [] },
      response: { type: 'response', prompt: 'What do you say?', prompt_arabic: '', inputType: 'choice', options: [
        { id: generateId(), text: 'Option 1', text_arabic: '', isCorrect: true, points: 10 },
        { id: generateId(), text: 'Option 2', text_arabic: '', isCorrect: false, points: 0 }
      ] },
      branch_exchange: { type: 'branch_exchange', bot_message: '', bot_message_male: '', branches: [
        { id: generateId(), keywords: '', intent: '', response: '', response_male: '', hint: '', points: 10 },
        { id: generateId(), keywords: '', intent: '', response: '', response_male: '', hint: '', points: 10 }
      ] },
      delay: { type: 'delay', delayMs: 1500, showTyping: true },
      end: { type: 'end', message: '', summary: [] }
    };
    setNodeEditForm(defaults[nodeType] || { type: nodeType });
  };

  // Save node from edit form (or update existing step)
  const saveNodeFromForm = () => {
    if (!nodeEditForm) return;
    let step = { ...nodeEditForm };
    if (step.type === 'achievement' && !step.message) step.message = `You earned: ${step.name}!`;
    if (step.gifUrls) step.gifUrls = step.gifUrls.filter(u => u && u.trim());
    if (step.vocabulary) step.vocabulary = step.vocabulary.filter(w => w.darija || w.meaning);
    if (step.type === 'end' && !step.message) step.message = 'Great job! Conversation complete!';

    // Convert branch_exchange to exchange with branches array
    if (step.type === 'branch_exchange') {
      step.type = 'exchange';
      step.id = step.id || generateId();
      step.branches = (step.branches || []).map(b => ({
        id: b.id || generateId(),
        keywords: (b.keywords || '').split(',').map(k => k.trim()).filter(Boolean),
        intent: b.intent || null,
        response: b.response || '',
        response_male: b.response_male || null,
        hint: b.hint || '',
        points: b.points || 10,
        next_step_id: b.next_step_id || null
      })).filter(b => b.keywords.length > 0 || b.intent || b.response);
      // Remove single-response fields since we use branches
      delete step.expected_intent;
      delete step.expected_keywords;
      delete step.correct_response;
      delete step.correct_response_male;
    }

    const label = step.name || step.text || step.bot_message || step.question || step.darija || step.message || step.label || `${step.delayMs || ''}ms`;

    if (editingStepIndex !== null) {
      // Overwrite existing step
      setTrainingSteps(prev => prev.map((s, i) => i === editingStepIndex ? { ...step, step_number: i + 1 } : s));
      addTrainingChat('bot', `✏️ Step ${editingStepIndex + 1} updated! (${step.type}${step.branches ? ` — ${step.branches.length} branches` : ''})`);
      setEditingStepIndex(null);
    } else {
      // Add new step
      const branchInfo = step.branches ? ` (${step.branches.length} branches)` : '';
      addTrainingChat('admin', `[Added ${step.type}${branchInfo}: ${label}]`);
      saveTrainingStep(step);
    }
    setNodeEditForm(null);
  };

  // Push current state to undo stack before changing phase
  const pushPhaseHistory = () => {
    setTrainingPhaseHistory(prev => [...prev, {
      phase: trainingPhase,
      data: { ...currentTrainingData },
      chatLen: trainingChat.length,
      stepsLen: trainingSteps.length
    }]);
  };

  // Reset ALL actions — go back to the very beginning of training
  const resetAllActions = () => {
    if (trainingPhaseHistory.length === 0 && trainingSteps.length === 0) return;
    if (trainingPhaseHistory.length > 0) {
      const first = trainingPhaseHistory[0];
      setTrainingPhaseHistory([]);
      setTrainingPhase(first.phase);
      setCurrentTrainingData(first.data);
      setTrainingChat(c => c.slice(0, first.chatLen));
      setTrainingSteps(s => s.slice(0, first.stepsLen));
    } else if (trainingSteps.length > 0) {
      setTrainingSteps([]);
      setTrainingPhase('bot_message');
      setCurrentTrainingData({});
      setTrainingChat(c => [c[0]].filter(Boolean));
    }
  };

  const handleTrainingInput = async () => {
    const text = trainingInput.trim();
    if (!text) return;
    setTrainingInput('');
    addTrainingChat('admin', text);

    // Push history before phase change (for undo)
    pushPhaseHistory();

    // ── CHOOSE NEXT (after step saved) ──
    if (trainingPhase === 'choose_next') {
      // User typed instead of clicking buttons — treat as next bot message
      setCurrentTrainingData(prev => ({ ...prev, bot_message: text, bot_message_male: null }));
      addTrainingChat('bot', '👤 Now: what should the USER say back? Type an example, or click an intent.');
      setTrainingPhase('user_input');
    }
    // ── TOPIC ──
    else if (trainingPhase === 'topic') {
      setCurrentTrainingData(prev => ({ ...prev, title: text }));
      addTrainingChat('bot', `Topic: "${text}". 🤖 Now type what the BOT should say FIRST, or click a node from the left panel.`);
      setTrainingPhase('bot_message');
    }
    // ── BOT MESSAGE ──
    else if (trainingPhase === 'bot_message') {
      setCurrentTrainingData(prev => ({ ...prev, bot_message: text, bot_message_male: null }));
      addTrainingChat('bot', '👤 How should the user respond? Choose: Single answer or Multiple answers (branching).');
      setTrainingPhase('choose_response_mode');
    }
    // ── CHOOSE RESPONSE MODE (single vs branching) ──
    else if (trainingPhase === 'choose_response_mode') {
      // User typed instead of clicking buttons — treat as single user answer
      setCurrentTrainingData(prev => ({ ...prev, user_example: text }));
      const result = await detectIntentAPI(text);
      if (result.intent) {
        setCurrentTrainingData(prev => ({ ...prev, detected_intent: result.intent, detected_display: result.display_name }));
        addTrainingChat('bot', `I detect: [${result.display_name}]. 🤖 What should the bot reply when correct?`);
      } else {
        setCurrentTrainingData(prev => ({ ...prev, detected_intent: null, custom_keywords: text.toLowerCase().split(/\s+/) }));
        addTrainingChat('bot', `No intent found — using "${text}" as keywords. 🤖 What should the bot reply?`);
      }
      setTrainingPhase('bot_response');
    }
    // ── USER INPUT ──
    else if (trainingPhase === 'user_input') {
      setCurrentTrainingData(prev => ({ ...prev, user_example: text }));
      const result = await detectIntentAPI(text);
      if (result.intent) {
        setCurrentTrainingData(prev => ({ ...prev, detected_intent: result.intent, detected_display: result.display_name }));
        addTrainingChat('bot', `I detect: [${result.display_name}]. 🤖 What should the bot reply when correct?`);
      } else {
        setCurrentTrainingData(prev => ({ ...prev, detected_intent: null, custom_keywords: text.toLowerCase().split(/\s+/) }));
        addTrainingChat('bot', `No intent found — using "${text}" as keywords. 🤖 What should the bot reply?`);
      }
      setTrainingPhase('bot_response');
    }
    // ── BOT RESPONSE → save step immediately ──
    else if (trainingPhase === 'bot_response') {
      setCurrentTrainingData(prev => ({ ...prev, correct_response: text, correct_response_male: null }));
      const d = { ...currentTrainingData, correct_response: text };
      saveTrainingStep({
        type: 'exchange',
        bot_message: d.bot_message,
        bot_message_male: d.bot_message_male || null,
        expected_intent: d.detected_intent || null,
        expected_keywords: d.custom_keywords || [d.user_example?.toLowerCase()],
        correct_response: text,
        correct_response_male: d.correct_response_male || null,
        hint: d.detected_intent
          ? `Try a ${d.detected_display || d.detected_intent}!`
          : `Try saying something like: "${d.user_example}"`,
        points: 10
      });
    }
    // Node types are now handled via edit form (nodeEditForm), no chat phases needed
  };

  // Finish training and save
  const finishTraining = async () => {
    let stepsToSave = [...trainingSteps];

    // Auto-save open node form if any
    if (nodeEditForm) {
      let step = { ...nodeEditForm, step_number: stepsToSave.length + 1 };
      if (!step.type) step.type = 'exchange';
      // Convert branch_exchange to exchange with branches
      if (step.type === 'branch_exchange') {
        step.type = 'exchange';
        step.id = step.id || generateId();
        step.branches = (step.branches || []).map(b => ({
          id: b.id || generateId(),
          keywords: typeof b.keywords === 'string' ? b.keywords.split(',').map(k => k.trim()).filter(Boolean) : (b.keywords || []),
          intent: b.intent || null,
          response: b.response || '',
          response_male: b.response_male || null,
          hint: b.hint || '',
          points: b.points || 10,
          next_step_id: b.next_step_id || null
        })).filter(b => (b.keywords || []).length > 0 || b.intent || b.response);
      }
      if (step.type === 'achievement' && !step.message) step.message = `You earned: ${step.name}!`;
      if (step.gifUrls) step.gifUrls = step.gifUrls.filter(u => u && u.trim());
      if (step.vocabulary) step.vocabulary = step.vocabulary.filter(w => w.darija || w.meaning);
      if (step.type === 'end' && !step.message) step.message = 'Great job! Conversation complete!';
      stepsToSave.push(step);
      setNodeEditForm(null);
    }

    // Auto-save partial exchange if user is mid-flow
    if (stepsToSave.length === 0 && currentTrainingData.bot_message) {
      const partial = {
        type: 'exchange',
        bot_message: currentTrainingData.bot_message,
        bot_message_male: currentTrainingData.bot_message_male || null,
        expected_intent: currentTrainingData.detected_intent || null,
        expected_keywords: currentTrainingData.custom_keywords || (currentTrainingData.user_example ? [currentTrainingData.user_example.toLowerCase()] : []),
        correct_response: currentTrainingData.correct_response || currentTrainingData.bot_message,
        correct_response_male: null,
        hint: currentTrainingData.user_example ? `Try saying: "${currentTrainingData.user_example}"` : '',
        points: 10,
        step_number: 1
      };
      stepsToSave.push(partial);
    }

    if (stepsToSave.length === 0) {
      showNotification('Add at least one step! Complete the exchange: bot message → user reply → bot response.', 'error');
      return;
    }
    const title = currentTrainingData.title || trainingChat.find(m => m.sender === 'admin')?.text || 'Untitled';
    let saved = false;
    if (editingTrainedConv) {
      saved = await updateTrainedConversation(editingTrainedConv, title, stepsToSave);
    } else {
      saved = await saveTrainedConversation(title, '', stepsToSave);
    }
    if (saved) {
      setTrainingMode(false);
      setTrainingChat([]);
      setTrainingSteps([]);
      setTrainingPhase('topic');
      setCurrentTrainingData({});
      setEditingTrainedConv(null);
      setTrainingBotId('');
    }
  };

  // Start new training session
  const startTraining = () => {
    setTrainingMode(true);
    setTrainingChat([{ sender: 'bot', text: 'What\'s this conversation about? (e.g., "Ordering at a café", "Asking for directions")' }]);
    setTrainingSteps([]);
    setTrainingPhase('topic');
    setCurrentTrainingData({});
    setTrainingInput('');
    setTrainingBotId('');
    setEditingTitle(false);
    setTrainingPhaseHistory([]);
    setTrainingGenderMode('auto');
  };

  // Edit an existing trained conversation
  const editTrainedConversation = (conv) => {
    setEditingTrainedConv(conv._id);
    setTrainingMode(true);
    setTrainingSteps(conv.steps || []);
    setCurrentTrainingData({ title: conv.title });
    setTrainingPhase('choose_next');
    setTrainingInput('');
    setNodeEditForm(null);
    setEditingStepIndex(null);
    setShowStepsList(false);
    setTrainingBotId(conv.bot_id || '');
    setEditingTitle(false);
    setTrainingPhaseHistory([]);
    // Map old gender_mode values to new system: ask stays ask, everything else auto
    const gm = conv.gender_mode;
    setTrainingGenderMode(gm === 'ask' ? 'ask' : 'auto');
    // Rebuild chat history from steps
    const chat = [
      { sender: 'bot', text: 'What\'s this conversation about?' },
      { sender: 'admin', text: conv.title },
      { sender: 'bot', text: `Topic set: "${conv.title}" — Loading ${conv.steps?.length || 0} saved steps...` }
    ];
    (conv.steps || []).forEach((step, i) => {
      if (step.type === 'exchange') {
        chat.push({ sender: 'bot', text: `Step ${i + 1}: 🔄 Exchange — "${step.bot_message || ''}"` });
      } else {
        const label = step.name || step.text || step.question || step.darija || step.message || step.label || step.type;
        chat.push({ sender: 'bot', text: `Step ${i + 1}: ${step.type} — "${label}"` });
      }
    });
    chat.push({ sender: 'bot', text: '✅ All steps loaded. You can edit steps, add more, or click "Update & Finish" when done.' });
    setTrainingChat(chat);
  };

  // Load data on mount
  useEffect(() => {
    loadData();
    loadBots();
    loadGameSettings();
    loadUserAvatars();
    loadIntents();
    loadTrainedConversations();
  }, [loadData, loadBots, loadGameSettings, loadUserAvatars, loadIntents, loadTrainedConversations]);
  
  // Toggle game visibility
  const toggleGameVisibility = async (isVisible) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/toggle-visibility?is_visible=${isVisible}`, {
        method: 'POST'
      });
      if (response.ok) {
        setGameSettings(prev => ({ ...prev, is_visible: isVisible }));
        showNotification(isVisible ? 'Conversation Challenge is now visible' : 'Conversation Challenge is now hidden', 'success');
      }
    } catch (error) {
      showNotification('Failed to update visibility', 'error');
    }
  };
  
  // Toggle game Pro status
  const toggleGamePro = async (isPro) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/toggle-pro?is_pro=${isPro}`, {
        method: 'POST'
      });
      if (response.ok) {
        setGameSettings(prev => ({ ...prev, is_pro: isPro }));
        showNotification(isPro ? 'Conversation Challenge is now Pro-only' : 'Conversation Challenge is now Free', 'success');
      }
    } catch (error) {
      showNotification('Failed to update access level', 'error');
    }
  };
  
  // ============================================
  // AI BRAIN FUNCTIONS
  // ============================================

  // ============================================
  // GROQ AI DICTIONARY FUNCTIONS
  // ============================================
  
  // Load Groq settings
  const loadGroqSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/settings`);
      if (response.ok) {
        const data = await response.json();
        setGroqEnabled(data.enabled || false);
        setGroqApiKeySet(data.api_key_set || false);
        setGroqApiKeyMasked(data.api_key_masked || '');
      }
      // Also load PyDODa stats
      const statsRes = await fetch(`${API_URL}/api/conversation-challenge/pydoda/stats`);
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setPydodaStats(statsData);
      }
    } catch (error) {
      console.error('Failed to load Groq settings:', error);
    }
  }, []);

  // Toggle Groq AI
  const toggleGroqEnabled = async (enabled) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/settings?enabled=${enabled}`, {
        method: 'POST'
      });
      if (response.ok) {
        setGroqEnabled(enabled);
        showNotification(enabled ? 'Groq AI Dictionary enabled' : 'Groq AI Dictionary disabled', 'success');
      }
    } catch (error) {
      showNotification('Failed to update Groq settings', 'error');
    }
  };

  // Save Groq API key
  const saveGroqApiKey = async () => {
    if (!groqApiKeyInput.trim()) {
      showNotification('Please enter an API key', 'error');
      return;
    }
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/settings?enabled=${groqEnabled}&api_key=${encodeURIComponent(groqApiKeyInput.trim())}`, {
        method: 'POST'
      });
      if (response.ok) {
        setGroqApiKeySet(true);
        setGroqApiKeyMasked(`***${groqApiKeyInput.slice(-4)}`);
        setGroqApiKeyInput('');
        setShowApiKeyInput(false);
        showNotification('Groq API key saved successfully', 'success');
      }
    } catch (error) {
      showNotification('Failed to save API key', 'error');
    }
  };

  // Clear Groq API key
  const clearGroqApiKey = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/settings?enabled=${groqEnabled}&api_key=`, {
        method: 'POST'
      });
      if (response.ok) {
        setGroqApiKeySet(false);
        setGroqApiKeyMasked('');
        showNotification('Groq API key cleared', 'success');
      }
    } catch (error) {
      showNotification('Failed to clear API key', 'error');
    }
  };
  
  // Search word using Groq
  const searchGroqWord = async () => {
    if (!groqSearchWord.trim()) return;
    
    setGroqSearching(true);
    setGroqSearchResult(null);
    
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/search/${encodeURIComponent(groqSearchWord.trim())}`);
      const data = await response.json();
      setGroqSearchResult(data);
      
      if (data.error) {
        showNotification(data.error, 'error');
      }
    } catch (error) {
      showNotification('Search failed', 'error');
    } finally {
      setGroqSearching(false);
    }
  };
  
  // Save Groq result to vocabulary (with optional edits)
  const saveGroqToVocabulary = async (word, editedData = null) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/save/${encodeURIComponent(word)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: editedData ? JSON.stringify(editedData) : null
      });
      const data = await response.json();
      
      if (data.success) {
        showNotification(`✅ "${word}" saved to your vocabulary!`, 'success');
        setEditingAiEntry(null);
        loadSavedVocabulary();
        loadGroqCache();
      } else {
        showNotification(data.error || 'Failed to save', 'error');
      }
    } catch (error) {
      showNotification('Failed to save word', 'error');
    }
  };
  
  // Edit AI-generated entry before saving
  const startEditingAiEntry = (word, data) => {
    setEditingAiEntry({
      word: word,
      data: {
        darija: data.darija || data.word || word,
        arabic: data.arabic || '',
        pronunciation: data.pronunciation || data.phonetic || '',
        type: data.type || data.wordType || '',
        meanings: Array.isArray(data.meanings) ? data.meanings : (data.meaning ? [data.meaning] : []),
        grammar_note: data.grammar_note || data.grammarNote || '',
        conjugation: data.conjugation || '',
        gender_note: data.gender_note || data.genderNote || '',
        examples: data.examples || [],
        cultural_note: data.cultural_note || data.culturalNote || '',
        related_words: data.related_words || data.relatedWords || []
      }
    });
  };
  
  // Update editing AI entry field
  const updateEditingField = (field, value) => {
    setEditingAiEntry(prev => ({
      ...prev,
      data: { ...prev.data, [field]: value }
    }));
  };
  
  // Load Groq cache
  const loadGroqCache = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/cache`);
      if (response.ok) {
        const data = await response.json();
        setGroqCachedWords(data.words || []);
      }
    } catch (error) {
      console.error('Failed to load Groq cache:', error);
    }
  }, []);
  
  // Load fuzzy match settings
  const loadFuzzySettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/fuzzy-settings`);
      if (response.ok) {
        const data = await response.json();
        setFuzzySettings(data);
        setFuzzyLoaded(true);
      }
    } catch (error) {
      console.error('Failed to load fuzzy settings:', error);
    }
  }, []);

  // Save fuzzy match settings
  const saveFuzzySettings = async (updates) => {
    try {
      const newSettings = { ...fuzzySettings, ...updates };
      const response = await fetch(`${API_URL}/api/conversation-challenge/fuzzy-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newSettings)
      });
      if (response.ok) {
        setFuzzySettings(newSettings);
        showNotification('Fuzzy match settings saved', 'success');
      }
    } catch (error) {
      showNotification('Failed to save fuzzy settings', 'error');
    }
  };

  // Test fuzzy match (supports multiple user inputs)
  const testFuzzyMatch = async () => {
    if (!fuzzyTestInput.trim() || !fuzzyTestAnswers.trim()) return;
    setFuzzyTesting(true);
    setFuzzyTestResult(null);
    try {
      const userInputs = fuzzyTestInput.split(',').map(a => a.trim()).filter(Boolean);
      const answers = fuzzyTestAnswers.split(',').map(a => a.trim()).filter(Boolean);

      // Test each user input against the accepted answers
      const allResults = [];
      for (const input of userInputs) {
        const response = await fetch(`${API_URL}/api/conversation-challenge/fuzzy-test`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_input: input, accepted_answers: answers })
        });
        if (response.ok) {
          const data = await response.json();
          allResults.push(data);
        }
      }
      setFuzzyTestResult({ multi: true, tests: allResults });
    } catch (error) {
      showNotification('Fuzzy test failed', 'error');
    }
    setFuzzyTesting(false);
  };

  // Add equivalence
  const addEquivalence = () => {
    if (!fuzzyNewFrom.trim() || !fuzzyNewTo.trim()) return;
    const updated = [...fuzzySettings.equivalences, { from: fuzzyNewFrom.toLowerCase(), to: fuzzyNewTo.toLowerCase() }];
    saveFuzzySettings({ equivalences: updated });
    setFuzzyNewFrom('');
    setFuzzyNewTo('');
  };

  // Remove equivalence
  const removeEquivalence = (index) => {
    const updated = fuzzySettings.equivalences.filter((_, i) => i !== index);
    saveFuzzySettings({ equivalences: updated });
  };

  // Start editing equivalence
  const startEditEquivalence = (index) => {
    const eq = fuzzySettings.equivalences[index];
    setFuzzyEditingIdx(index);
    setFuzzyEditFrom(eq.from);
    setFuzzyEditTo(eq.to);
  };

  // Save edited equivalence
  const saveEditEquivalence = () => {
    if (fuzzyEditingIdx === null || !fuzzyEditFrom.trim() || !fuzzyEditTo.trim()) return;
    const updated = [...fuzzySettings.equivalences];
    updated[fuzzyEditingIdx] = { from: fuzzyEditFrom.toLowerCase(), to: fuzzyEditTo.toLowerCase() };
    saveFuzzySettings({ equivalences: updated });
    setFuzzyEditingIdx(null);
    setFuzzyEditFrom('');
    setFuzzyEditTo('');
  };

  // Load spelling groups
  const loadSpellingGroups = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/spelling-groups`);
      if (response.ok) {
        const data = await response.json();
        setSpellingGroups(data.groups || []);
      }
    } catch (error) {
      console.error('Failed to load spelling groups:', error);
    }
  }, []);

  // Save test results as spelling group
  const saveAsSpellingGroup = async (mainWord, variants) => {
    if (!mainWord) { showNotification('No main word to save', 'error'); return; }
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/spelling-groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ main_word: mainWord, variants })
      });
      const data = await response.json();
      if (response.ok && data.success) {
        showNotification(data.message || 'Spelling group saved', 'success');
        loadSpellingGroups();
      } else {
        showNotification(data.error || data.detail || 'Failed to save', 'error');
        console.error('Save spelling group error:', data);
      }
    } catch (error) {
      showNotification('Failed to save spelling group: ' + error.message, 'error');
      console.error('Save spelling group error:', error);
    }
  };

  // Update spelling group
  const updateSpellingGroup = async (mainWord, updates) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/spelling-groups/${encodeURIComponent(mainWord)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      if (response.ok) {
        showNotification('Group updated', 'success');
        loadSpellingGroups();
        setEditingGroup(null);
      }
    } catch (error) {
      showNotification('Failed to update group', 'error');
    }
  };

  // Delete spelling group
  const deleteSpellingGroup = async (mainWord) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/spelling-groups/${encodeURIComponent(mainWord)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        showNotification(`Deleted group "${mainWord}"`, 'success');
        loadSpellingGroups();
      }
    } catch (error) {
      showNotification('Failed to delete group', 'error');
    }
  };

  // Load saved vocabulary
  const loadSavedVocabulary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary`);
      if (response.ok) {
        const data = await response.json();
        setSavedVocabulary(data.vocabulary || []);
      }
    } catch (error) {
      console.error('Failed to load vocabulary:', error);
    }
  }, []);
  
  // Delete from Groq cache
  const deleteFromGroqCache = async (word) => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/cache/${encodeURIComponent(word)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        showNotification(`Deleted "${word}" from cache`, 'success');
        loadGroqCache();
      }
    } catch (error) {
      showNotification('Failed to delete', 'error');
    }
  };
  
  // Delete from saved vocabulary
  const deleteFromVocabulary = async (word) => {
    if (!word) {
      showNotification('No word to delete', 'error');
      return;
    }
    
    try {
      // Use POST endpoint to handle special characters like /
      const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary/delete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ darija: word })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          showNotification(`Deleted "${word}" from vocabulary`, 'success');
          setDeleteConfirmWord(null);
          loadSavedVocabulary();
        } else {
          showNotification(data.error || 'Word not found', 'error');
        }
      } else {
        showNotification('Failed to delete - server error', 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      showNotification('Failed to delete: ' + error.message, 'error');
    }
  };
  
  // Delete ALL from vocabulary
  const deleteAllVocabulary = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary/all`, {
        method: 'DELETE'
      });
      if (response.ok) {
        showNotification('All vocabulary deleted', 'success');
        loadSavedVocabulary();
        setConfirmDeleteAll(null);
        setSelectedVocabWords([]); // Clear selection
      }
    } catch (error) {
      showNotification('Failed to delete all', 'error');
    }
  };
  
  // Delete SELECTED vocabulary words
  const deleteSelectedVocabulary = async () => {
    if (selectedVocabWords.length === 0) return;
    
    try {
      let successCount = 0;
      for (const word of selectedVocabWords) {
        const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary/${encodeURIComponent(word)}`, {
          method: 'DELETE'
        });
        if (response.ok) successCount++;
      }
      
      showNotification(`Deleted ${successCount} of ${selectedVocabWords.length} selected words`, 'success');
      setSelectedVocabWords([]); // Clear selection
      loadSavedVocabulary();
    } catch (error) {
      showNotification('Failed to delete some words', 'error');
    }
  };
  
  // Toggle word selection
  const toggleWordSelection = (darija) => {
    setSelectedVocabWords(prev => 
      prev.includes(darija) 
        ? prev.filter(w => w !== darija) 
        : [...prev, darija]
    );
  };
  
  // Select/deselect all words on current page
  const toggleSelectAllOnPage = (items) => {
    const pageWords = items.map(w => w.darija);
    const allSelected = pageWords.every(w => selectedVocabWords.includes(w));
    
    if (allSelected) {
      // Deselect all on current page
      setSelectedVocabWords(prev => prev.filter(w => !pageWords.includes(w)));
    } else {
      // Select all on current page
      setSelectedVocabWords(prev => [...new Set([...prev, ...pageWords])]);
    }
  };
  
  // Delete ALL from cache
  const deleteAllCache = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/groq/cache/all`, {
        method: 'DELETE'
      });
      if (response.ok) {
        showNotification('All cache deleted', 'success');
        loadGroqCache();
        setConfirmDeleteAll(null);
      }
    } catch (error) {
      showNotification('Failed to delete all cache', 'error');
    }
  };
  
  // Add new word manually
  const addNewWord = async () => {
    if (!newWord.darija || !newWord.meaning) {
      showNotification('Darija and Meaning are required', 'error');
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newWord)
      });
      
      if (response.ok) {
        showNotification(`Added "${newWord.darija}" to vocabulary`, 'success');
        setShowAddWordModal(false);
        setNewWord({
          darija: '', arabic: '', english: '', meaning: '', phonetic: '', wordType: '',
          grammarNote: '', conjugation: '', genderNote: '', culturalNote: '',
          examples: [], relatedWords: [], category: ''
        });
        loadSavedVocabulary();
      } else {
        showNotification('Failed to add word', 'error');
      }
    } catch (error) {
      showNotification('Failed to add word', 'error');
    }
  };
  
  // Update existing vocabulary word
  const updateVocabWord = async () => {
    if (!editingVocabWord) return;
    
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary/${encodeURIComponent(editingVocabWord.originalDarija || editingVocabWord.darija)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingVocabWord)
      });
      
      const data = await response.json();
      
      if (data.success) {
        showNotification(`Updated "${editingVocabWord.darija}"`, 'success');
        setEditingVocabWord(null);
        loadSavedVocabulary();
      } else {
        showNotification(data.error || 'Failed to update word', 'error');
      }
    } catch (error) {
      console.error('Update error:', error);
      showNotification('Failed to update word: ' + error.message, 'error');
    }
  };
  
  // Reset pagination when search changes
  useEffect(() => { setVocabTablePage(1); }, [vocabTableSearch]);

  // Filter and sort vocabulary for table
  const getFilteredVocabulary = () => {
    let filtered = [...savedVocabulary];
    
    // Search filter
    if (vocabTableSearch) {
      const searchLower = vocabTableSearch.toLowerCase();
      filtered = filtered.filter(v => 
        v.darija?.toLowerCase().includes(searchLower) ||
        v.meaning?.toLowerCase().includes(searchLower) ||
        v.phonetic?.toLowerCase().includes(searchLower) ||
        v.wordType?.toLowerCase().includes(searchLower) ||
        v.category?.toLowerCase().includes(searchLower)
      );
    }
    
    // Sort
    filtered.sort((a, b) => {
      const aVal = (a[vocabTableSort.field] || '').toLowerCase();
      const bVal = (b[vocabTableSort.field] || '').toLowerCase();
      if (vocabTableSort.direction === 'asc') {
        return aVal.localeCompare(bVal);
      } else {
        return bVal.localeCompare(aVal);
      }
    });
    
    return filtered;
  };
  
  // Get paginated vocabulary
  const getPaginatedVocabulary = () => {
    const filtered = getFilteredVocabulary();
    const startIndex = (vocabTablePage - 1) * vocabTablePerPage;
    return {
      items: filtered.slice(startIndex, startIndex + vocabTablePerPage),
      total: filtered.length,
      totalPages: Math.ceil(filtered.length / vocabTablePerPage)
    };
  };
  
  // Handle sort click
  const handleSort = (field) => {
    setVocabTableSort(prev => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };
  
  // Parse bulk upload text (block format)
  const parseBulkUpload = (text) => {
    setBulkUploadError('');
    const blocks = text.split('---').filter(block => block.trim());
    const parsed = [];
    
    for (let i = 0; i < blocks.length; i++) {
      const block = blocks[i].trim();
      if (!block) continue;
      
      const word = {
        english: '',
        darija: '',
        arabic: '',
        meaning: '',
        phonetic: '',
        wordType: '',
        category: '',
        genderNote: '',
        grammarNote: '',
        culturalNote: ''
      };
      
      const lines = block.split('\n');
      for (const line of lines) {
        const colonIndex = line.indexOf(':');
        if (colonIndex === -1) continue;
        
        const key = line.substring(0, colonIndex).trim().toLowerCase();
        const value = line.substring(colonIndex + 1).trim();
        
        // Support multiple variations of field names
        if (key === 'english') word.english = value;
        else if (key === 'darija' || key === 'darija (latin)') word.darija = value;
        else if (key === 'arabic' || key === 'arabic script') word.arabic = value;
        else if (key === 'meaning') word.meaning = value;
        else if (key === 'pronunciation') word.phonetic = value;
        else if (key === 'type' || key === 'word type') word.wordType = value.toLowerCase();
        else if (key === 'category') word.category = value;
        else if (key === 'gender note') word.genderNote = value;
        else if (key === 'grammar note') word.grammarNote = value;
        else if (key === 'cultural note') word.culturalNote = value;
      }
      
      // Validate required fields
      if (word.english && word.darija) {
        parsed.push(word);
      }
    }
    
    setBulkUploadPreview(parsed);
    if (parsed.length === 0 && text.trim()) {
      setBulkUploadError('No valid words found. Make sure each block has at least English and Darija.');
    }
  };
  
  // Import bulk upload words
  const importBulkUpload = async () => {
    if (bulkUploadPreview.length === 0) return;
    
    let added = 0;
    let failed = 0;
    
    for (const word of bulkUploadPreview) {
      try {
        const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(word)
        });
        if (response.ok) {
          added++;
        } else {
          failed++;
        }
      } catch {
        failed++;
      }
    }
    
    showNotification(`Imported ${added} words${failed > 0 ? `, ${failed} failed` : ''}`, added > 0 ? 'success' : 'error');
    setShowBulkUploadModal(false);
    setBulkUploadText('');
    setBulkUploadPreview([]);
    loadSavedVocabulary();
  };
  
  // Download template
  const downloadTemplate = () => {
    const template = `---
English: hello
Darija (Latin): salam
Arabic Script: سلام
Meaning: greeting to say hi
Pronunciation: sa-LAM
Word Type: greeting
Category: Greetings
Gender Note: 
Grammar Note: common greeting
Cultural Note: very common
---
English: goodbye
Darija (Latin): bslama
Arabic Script: بسلامة
Meaning: farewell
Pronunciation: bs-LA-ma
Word Type: greeting
Category: Greetings
Gender Note: 
Grammar Note: used when leaving
Cultural Note: 
---
English: 
Darija (Latin): 
Arabic Script: 
Meaning: 
Pronunciation: 
Word Type: 
Category: 
Gender Note: 
Grammar Note: 
Cultural Note: 
---`;
    
    const blob = new Blob([template], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'vocabulary_template.txt';
    a.click();
    URL.revokeObjectURL(url);
  };
  
  // Load Groq data when settings or dictionary tab is active
  useEffect(() => {
    if (activeTab === 'settings' || activeTab === 'dictionary') {
      loadGroqSettings();
      loadGroqCache();
      loadSavedVocabulary();
    }
    if (activeTab === 'fuzzy') {
      if (!fuzzyLoaded) loadFuzzySettings();
      loadSpellingGroups();
    }
  }, [activeTab, loadGroqSettings, loadGroqCache, loadSavedVocabulary, loadFuzzySettings, fuzzyLoaded, loadSpellingGroups]);
  
  // Prevent page scroll when canvas is focused
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const preventScroll = (e) => {
      if (selectedFlow) {
        e.preventDefault();
      }
    };
    
    canvas.addEventListener('wheel', preventScroll, { passive: false });
    return () => canvas.removeEventListener('wheel', preventScroll);
  }, [selectedFlow]);

  // Global mouse event handler for dragging nodes
  // This ensures drag continues even when mouse leaves the node element
  useEffect(() => {
    if (!draggingNode) return;

    const handleGlobalMouseMove = (e) => {
      const dx = (e.clientX - draggingNode.startX) / zoom;
      const dy = (e.clientY - draggingNode.startY) / zoom;
      
      setFlows(prevFlows => prevFlows.map(flow => {
        if (flow.id !== selectedFlow?.id) return flow;
        return {
          ...flow,
          nodes: flow.nodes.map(node => {
            if (node.id !== draggingNode.id) return node;
            return {
              ...node,
              position: {
                x: Math.max(0, draggingNode.nodeX + dx),
                y: Math.max(0, draggingNode.nodeY + dy)
              }
            };
          })
        };
      }));
      
      // Also update selectedFlow directly
      if (selectedFlow) {
        setSelectedFlow(prev => ({
          ...prev,
          nodes: prev.nodes.map(node => {
            if (node.id !== draggingNode.id) return node;
            return {
              ...node,
              position: {
                x: Math.max(0, draggingNode.nodeX + dx),
                y: Math.max(0, draggingNode.nodeY + dy)
              }
            };
          })
        }));
      }
    };

    const handleGlobalMouseUp = () => {
      setDraggingNode(null);
    };

    // Attach to window to capture events even outside the canvas
    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [draggingNode, zoom, selectedFlow]);
  
  // Save bots configuration
  const saveBots = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/bots`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bots })
      });
      
      if (response.ok) {
        showNotification('Bot settings saved!', 'success');
        await loadBots(); // Reload from backend to stay in sync
      } else {
        showNotification('Failed to save bot settings', 'error');
      }
    } catch (error) {
      console.error('Error saving bots:', error);
      showNotification('Error saving bot settings', 'error');
    }
  };
  
  // Create new story
  const createNewStory = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/stories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...newStoryData,
          chapters: [],
          is_active: true,
          total_duration: 0
        })
      });
      
      if (response.ok) {
        showNotification('Story created!', 'success');
        setShowNewStoryForm(false);
        setNewStoryData({
          name: '',
          description: '',
          bot_gender: 'female',
          script_type: 'latin',
          difficulty: 'Beginner',
          is_free: true
        });
        loadData();
      } else {
        showNotification('Failed to create story', 'error');
      }
    } catch (error) {
      console.error('Error creating story:', error);
      showNotification('Error creating story', 'error');
    }
  };
  
  // Delete story
  const deleteStory = (storyId) => {
    setConfirmAction({
      message: 'Are you sure you want to delete this story?',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await fetch(`${API_URL}/api/conversation-challenge/stories/${storyId}`, {
            method: 'DELETE'
          });
          if (response.ok) {
            showNotification('Story deleted', 'success');
            loadData();
          }
        } catch (error) {
          console.error('Error deleting story:', error);
          showNotification('Error deleting story', 'error');
        }
      }
    });
  };
  
  // Save flow
  const saveFlow = async (flow) => {
    try {
      const method = flow.id && flows.find(f => f.id === flow.id) ? 'PUT' : 'POST';
      const url = method === 'PUT' 
        ? `${API_URL}/api/conversation-challenge/flows/${flow.id}`
        : `${API_URL}/api/conversation-challenge/flows`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flow)
      });
      
      if (response.ok) {
        showNotification('Flow saved successfully!', 'success');
        loadData();
      } else {
        showNotification('Failed to save flow', 'error');
      }
    } catch (error) {
      console.error('Error saving flow:', error);
      showNotification('Error saving flow', 'error');
    }
  };
  
  // Delete flow
  const deleteFlow = (flowId) => {
    setConfirmAction({
      message: 'Are you sure you want to delete this flow?',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await fetch(`${API_URL}/api/conversation-challenge/flows/${flowId}`, {
            method: 'DELETE'
          });
          if (response.ok) {
            showNotification('Flow deleted', 'success');
            setSelectedFlow(null);
            loadData();
          }
        } catch (error) {
          console.error('Error deleting flow:', error);
          showNotification('Error deleting flow', 'error');
        }
      }
    });
  };
  
  // Save settings
  const saveSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        showNotification('Settings saved!', 'success');
        await loadData(); // Reload from backend to stay in sync
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      showNotification('Error saving settings', 'error');
    }
  };
  
  // Create new flow
  const createNewFlow = async () => {
    const flow = {
      ...newFlowData,
      nodes: [
        {
          id: 'start_1',
          type: NODE_TYPES.START,
          position: { x: 400, y: 50 },
          data: getDefaultNodeData(NODE_TYPES.START),
          next_nodes: []
        }
      ],
      edges: [],
      audio_enabled: true,
      is_active: true
    };
    
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/flows`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(flow)
      });
      
      if (response.ok) {
        const data = await response.json();
        showNotification('Flow created!', 'success');
        setShowNewFlowForm(false);
        setNewFlowData({
          name: '',
          description: '',
          bot_id: '',  // Reset: user must select a bot
          bot_gender: 'female',
          script_type: 'latin',
          difficulty: 'Beginner',
          category: 'General',
          is_free: true,
          vocabulary: []
        });
        loadData();
        // Select the new flow
        const flowsRes = await fetch(`${API_URL}/api/conversation-challenge/flows/${data.flow_id}`);
        const newFlow = await flowsRes.json();
        setSelectedFlow(newFlow);
      }
    } catch (error) {
      console.error('Error creating flow:', error);
      showNotification('Error creating flow', 'error');
    }
  };
  
  // Seed example data
  const seedExamples = async () => {
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/seed-examples`, {
        method: 'POST'
      });
      
      if (response.ok) {
        showNotification('Example data created!', 'success');
        loadData();
      }
    } catch (error) {
      console.error('Error seeding examples:', error);
      showNotification('Error creating examples', 'error');
    }
  };
  
  // Add node to flow
  const addNode = (type) => {
    console.log('addNode called with type:', type, 'selectedFlow:', selectedFlow?.id);
    if (!selectedFlow) {
      console.log('No selectedFlow, returning');
      return;
    }
    
    // Calculate position based on existing nodes, spread them out more
    const existingNodes = selectedFlow.nodes || [];
    const maxY = existingNodes.length > 0 
      ? Math.max(...existingNodes.map(n => n.position?.y || 0)) 
      : 0;
    
    const newNode = {
      id: generateId(),
      type,
      position: { x: 300 + Math.random() * 200, y: maxY + 150 },
      data: getDefaultNodeData(type),
      next_nodes: []
    };
    
    console.log('Creating new node:', newNode);
    
    setSelectedFlow(prev => {
      const updated = {
        ...prev,
        nodes: [...(prev.nodes || []), newNode]
      };
      console.log('Updated flow nodes:', updated.nodes.length);
      return updated;
    });
    
    setSelectedNode(newNode);
    showNotification(`Added ${NODE_COLORS[type]?.label || type} node`, 'success');
  };
  
  // Update node
  const updateNode = (nodeId, updates) => {
    setSelectedFlow(prev => ({
      ...prev,
      nodes: prev.nodes.map(node => 
        node.id === nodeId ? { ...node, ...updates } : node
      )
    }));
    
    if (selectedNode?.id === nodeId) {
      setSelectedNode(prev => ({ ...prev, ...updates }));
    }
  };
  
  // Delete node
  const deleteNode = (nodeId) => {
    if (!selectedFlow) return;
    
    setSelectedFlow(prev => ({
      ...prev,
      nodes: prev.nodes.filter(node => node.id !== nodeId),
      edges: prev.edges.filter(edge => edge.source !== nodeId && edge.target !== nodeId)
    }));
    
    if (selectedNode?.id === nodeId) {
      setSelectedNode(null);
    }
  };
  
  // Add edge
  const addEdge = (sourceId, targetId) => {
    if (!selectedFlow || sourceId === targetId) return;
    
    const edgeExists = selectedFlow.edges?.some(
      e => e.source === sourceId && e.target === targetId
    );
    
    if (!edgeExists) {
      setSelectedFlow(prev => ({
        ...prev,
        edges: [...(prev.edges || []), { source: sourceId, target: targetId }]
      }));
      
      // Update source node's next_nodes
      setSelectedFlow(prev => ({
        ...prev,
        nodes: prev.nodes.map(node => 
          node.id === sourceId 
            ? { ...node, next_nodes: [...(node.next_nodes || []), targetId] }
            : node
        )
      }));
    }
  };
  
  // Render node on canvas
  const renderNode = (node) => {
    const colors = NODE_COLORS[node.type] || NODE_COLORS[NODE_TYPES.MESSAGE];
    const isSelected = selectedNode?.id === node.id;
    
    return (
      <div
        key={node.id}
        className={`absolute rounded-lg shadow-lg cursor-move transition-shadow ${isSelected ? 'ring-4 ring-yellow-400' : ''}`}
        style={{
          left: node.position.x * zoom + pan.x,
          top: node.position.y * zoom + pan.y,
          backgroundColor: colors.bg,
          borderColor: colors.border,
          borderWidth: '3px',
          borderStyle: 'solid',
          minWidth: '180px',
          transform: `scale(${zoom})`,
          transformOrigin: 'top left',
          zIndex: isSelected ? 100 : (draggingNode?.id === node.id ? 100 : 10),
          userSelect: 'none'
        }}
        onClick={(e) => {
          e.stopPropagation();
          setSelectedNode(node);
        }}
        onMouseDown={(e) => {
          if (e.button === 0) { // Left mouse button only
            e.stopPropagation();
            e.preventDefault();
            setDraggingNode({ 
              id: node.id, 
              startX: e.clientX, 
              startY: e.clientY, 
              nodeX: node.position.x, 
              nodeY: node.position.y 
            });
          }
        }}
        onMouseUp={(e) => {
          if (e.button === 0) {
            setDraggingNode(null);
          }
        }}
      >
        <div className="p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xl">{colors.icon}</span>
            <span className="text-white font-bold text-sm">{colors.label}</span>
          </div>
          <div className="text-white text-xs opacity-80 truncate max-w-[150px]">
            {node.data?.imageUrl ? '\u{1F5BC}\uFE0F ' : ''}{(node.data?.gifUrls?.length > 0 || node.data?.gifUrl) ? '\u{1F3AC} ' : ''}{node.data?.text_darija || node.data?.prompt || node.data?.tipText || node.data?.message || node.data?.label || '...'}
          </div>
        </div>
        
        {/* Connection point */}
        <div 
          className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full border-2 border-gray-400 cursor-crosshair hover:bg-yellow-300"
          onMouseDown={(e) => {
            e.stopPropagation();
            setConnecting({ sourceId: node.id, x: e.clientX, y: e.clientY });
          }}
        />
        
        {/* Input connection point */}
        {node.type !== NODE_TYPES.START && (
          <div 
            className="absolute -top-2 left-1/2 transform -translate-x-1/2 w-4 h-4 bg-white rounded-full border-2 border-gray-400"
            onMouseUp={() => {
              if (connecting && connecting.sourceId !== node.id) {
                addEdge(connecting.sourceId, node.id);
                setConnecting(null);
              }
            }}
          />
        )}
      </div>
    );
  };
  
  // Render edges
  const renderEdges = () => {
    if (!selectedFlow?.edges || !selectedFlow?.nodes) return null;
    
    return (
      <svg className="absolute inset-0 pointer-events-none" style={{ overflow: 'visible' }}>
        {selectedFlow.edges.map((edge, idx) => {
          const sourceNode = selectedFlow.nodes.find(n => n.id === edge.source);
          const targetNode = selectedFlow.nodes.find(n => n.id === edge.target);
          
          if (!sourceNode || !targetNode) return null;
          
          const x1 = sourceNode.position.x * zoom + pan.x + 90;
          const y1 = sourceNode.position.y * zoom + pan.y + 80;
          const x2 = targetNode.position.x * zoom + pan.x + 90;
          const y2 = targetNode.position.y * zoom + pan.y;
          
          const midY = (y1 + y2) / 2;
          
          return (
            <g key={idx}>
              <path
                d={`M ${x1} ${y1} C ${x1} ${midY}, ${x2} ${midY}, ${x2} ${y2}`}
                stroke="#FFD700"
                strokeWidth="2"
                fill="none"
                markerEnd="url(#arrowhead)"
                opacity="0.8"
              />
            </g>
          );
        })}
        <defs>
          <marker id="arrowhead" markerWidth="6" markerHeight="5" refX="5" refY="2.5" orient="auto">
            <polygon points="0 0, 6 2.5, 0 5" fill="#FFD700" />
          </marker>
        </defs>
      </svg>
    );
  };
  
  // Handle canvas mouse events
  const handleCanvasMouseMove = (e) => {
    // Handle node dragging
    if (draggingNode) {
      const dx = (e.clientX - draggingNode.startX) / zoom;
      const dy = (e.clientY - draggingNode.startY) / zoom;
      
      updateNode(draggingNode.id, {
        position: {
          x: Math.max(0, draggingNode.nodeX + dx),
          y: Math.max(0, draggingNode.nodeY + dy)
        }
      });
    }
    
    // Handle canvas panning (middle mouse button)
    if (isPanning) {
      const dx = e.clientX - panStart.x;
      const dy = e.clientY - panStart.y;
      setPan(prev => ({
        x: prev.x + dx,
        y: prev.y + dy
      }));
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };
  
  const handleCanvasMouseDown = (e) => {
    // Middle mouse button (button 1) for panning
    if (e.button === 1) {
      e.preventDefault();
      setIsPanning(true);
      setPanStart({ x: e.clientX, y: e.clientY });
    }
  };
  
  const handleCanvasMouseUp = (e) => {
    setDraggingNode(null);
    setConnecting(null);
    if (e.button === 1) {
      setIsPanning(false);
    }
  };
  
  // Handle mouse wheel zoom on canvas
  const handleCanvasWheel = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(z => Math.min(2, Math.max(0.3, z + delta)));
  };
  
  // Node property editor
  const renderNodeEditor = () => {
    if (!selectedNode) return null;
    
    const colors = NODE_COLORS[selectedNode.type];
    
    return (
      <div className={`w-80 border-l ${isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'} p-4 overflow-y-auto`}>
        <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-600">
          <span className="text-2xl">{colors?.icon}</span>
          <h3 className="text-lg font-bold text-white">{colors?.label} Node</h3>
        </div>
        
        {/* Common properties */}
        <div className="space-y-4">
          {/* Message Node */}
          {selectedNode.type === NODE_TYPES.MESSAGE && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Darija Text (Latin)</label>
                <textarea
                  value={selectedNode.data?.text_darija || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, text_darija: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                  placeholder="Salam! Labas?"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Arabic Script</label>
                <textarea
                  value={selectedNode.data?.text_arabic || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, text_arabic: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                  dir="rtl"
                  placeholder="سلام! لاباس؟"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">English Translation</label>
                <textarea
                  value={selectedNode.data?.translation || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, translation: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                  placeholder="Hello! How are you?"
                />
              </div>
              
              {/* Dictionary Words for this message */}
              <div className="border-t border-gray-600 pt-3 mt-3">
                <label className="text-sm text-purple-400 block mb-2">📖 Dictionary Words (clickable in chat)</label>
                <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                  {(selectedNode.data?.vocabulary || []).map((word, idx) => (
                    <div key={idx} className="bg-gray-600 rounded p-2 text-xs">
                      <div className="flex justify-between items-center mb-1">
                        <span className="text-purple-300 font-bold">{word.darija || 'Word ' + (idx + 1)}</span>
                        <button
                          onClick={() => {
                            const newVocab = (selectedNode.data?.vocabulary || []).filter((_, i) => i !== idx);
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, vocabulary: newVocab } });
                          }}
                          className="text-red-400 hover:text-red-300"
                        >✕</button>
                      </div>
                      <input
                        type="text"
                        value={word.darija || ''}
                        onChange={(e) => {
                          const newVocab = [...(selectedNode.data?.vocabulary || [])];
                          newVocab[idx] = { ...newVocab[idx], darija: e.target.value };
                          updateNode(selectedNode.id, { data: { ...selectedNode.data, vocabulary: newVocab } });
                        }}
                        placeholder="Darija word (e.g., Salam)"
                        className="w-full bg-gray-700 text-white rounded px-2 py-1 mb-1"
                      />
                      <input
                        type="text"
                        value={word.meaning || ''}
                        onChange={(e) => {
                          const newVocab = [...(selectedNode.data?.vocabulary || [])];
                          newVocab[idx] = { ...newVocab[idx], meaning: e.target.value };
                          updateNode(selectedNode.id, { data: { ...selectedNode.data, vocabulary: newVocab } });
                        }}
                        placeholder="Meaning (e.g., Hello)"
                        className="w-full bg-gray-700 text-white rounded px-2 py-1 mb-1"
                      />
                      <input
                        type="text"
                        value={word.phonetic || ''}
                        onChange={(e) => {
                          const newVocab = [...(selectedNode.data?.vocabulary || [])];
                          newVocab[idx] = { ...newVocab[idx], phonetic: e.target.value };
                          updateNode(selectedNode.id, { data: { ...selectedNode.data, vocabulary: newVocab } });
                        }}
                        placeholder="Phonetic (e.g., sah-LAAM)"
                        className="w-full bg-gray-700 text-white rounded px-2 py-1"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    const newWord = { darija: '', meaning: '', phonetic: '' };
                    const newVocab = [...(selectedNode.data?.vocabulary || []), newWord];
                    updateNode(selectedNode.id, { data: { ...selectedNode.data, vocabulary: newVocab } });
                  }}
                  className="w-full bg-purple-600 hover:bg-purple-500 text-white rounded p-2 text-xs"
                >
                  + Add Dictionary Word
                </button>
              </div>
              
              <div>
                <label className="text-sm text-gray-400 block mb-1">Emotion</label>
                <select
                  value={selectedNode.data?.emotion || 'neutral'}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, emotion: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                >
                  <option value="neutral">😐 Neutral</option>
                  <option value="happy">😊 Happy</option>
                  <option value="excited">🤩 Excited</option>
                  <option value="thinking">🤔 Thinking</option>
                  <option value="surprised">😮 Surprised</option>
                  <option value="sad">😢 Sad</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Audio URL (Dropbox)</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={selectedNode.data?.audioUrl || ''}
                    onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, audioUrl: e.target.value } })}
                    className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                    placeholder="https://dropbox.com/..."
                  />
                  {selectedNode.data?.audioUrl && (
                    <button
                      onClick={() => { const a = new Audio(selectedNode.data.audioUrl); a.play().catch(() => {}); }}
                      className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm"
                      title="Preview audio"
                    >🔊</button>
                  )}
                </div>
                {selectedNode.data?.audioUrl && !isValidMediaUrl(selectedNode.data.audioUrl) && (
                  <p className="text-xs text-red-400 mt-1">URL must be from Dropbox, GIPHY, Tenor, or Imgur</p>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Image URL (Dropbox)</label>
                <input
                  type="text"
                  value={selectedNode.data?.imageUrl || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, imageUrl: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  placeholder="https://dl.dropboxusercontent.com/..."
                />
                {selectedNode.data?.imageUrl && !isValidMediaUrl(selectedNode.data.imageUrl) && (
                  <p className="text-xs text-red-400 mt-1">URL must be from Dropbox, GIPHY, Tenor, or Imgur</p>
                )}
                {selectedNode.data?.imageUrl && isValidMediaUrl(selectedNode.data.imageUrl) && (
                  <div className="mt-2 rounded overflow-hidden border border-gray-600">
                    <img src={selectedNode.data.imageUrl} alt="Preview" className="w-full max-h-32 object-cover"
                      onError={(e) => { e.target.style.display = 'none'; }} />
                  </div>
                )}
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">GIF URLs (GIPHY / Tenor) — Bot picks one randomly</label>
                {(selectedNode.data?.gifUrls?.length > 0
                  ? selectedNode.data.gifUrls
                  : (selectedNode.data?.gifUrl ? [selectedNode.data.gifUrl] : [])
                ).map((url, idx) => (
                  <div key={idx} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      value={url}
                      onChange={(e) => {
                        const urls = [...(selectedNode.data?.gifUrls?.length > 0 ? selectedNode.data.gifUrls : (selectedNode.data?.gifUrl ? [selectedNode.data.gifUrl] : []))];
                        urls[idx] = e.target.value;
                        updateNode(selectedNode.id, { data: { ...selectedNode.data, gifUrls: urls, gifUrl: urls[0] || '' } });
                      }}
                      className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="https://media.giphy.com/..."
                    />
                    <button
                      onClick={() => {
                        const urls = [...(selectedNode.data?.gifUrls?.length > 0 ? selectedNode.data.gifUrls : (selectedNode.data?.gifUrl ? [selectedNode.data.gifUrl] : []))];
                        urls.splice(idx, 1);
                        updateNode(selectedNode.id, { data: { ...selectedNode.data, gifUrls: urls, gifUrl: urls[0] || '' } });
                      }}
                      className="text-red-400 hover:text-red-300 text-sm px-2 py-1"
                      title="Remove"
                    >
                      ✕
                    </button>
                  </div>
                ))}
                {(selectedNode.data?.gifUrls?.length || 0) < 4 && (
                  <button
                    onClick={() => {
                      const urls = [...(selectedNode.data?.gifUrls?.length > 0 ? selectedNode.data.gifUrls : (selectedNode.data?.gifUrl ? [selectedNode.data.gifUrl] : []))];
                      urls.push('');
                      updateNode(selectedNode.id, { data: { ...selectedNode.data, gifUrls: urls, gifUrl: urls[0] || '' } });
                    }}
                    className="text-sm text-blue-400 hover:text-blue-300 mt-1"
                  >
                    + Add GIF URL
                  </button>
                )}
                <p className="text-xs text-gray-500 mt-1">Add 1-4 GIF URLs from giphy.com or tenor.com. Bot randomly picks one each time.</p>
                {(selectedNode.data?.gifUrls || []).some(u => u && !isValidMediaUrl(u)) && (
                  <p className="text-xs text-red-400 mt-1">Some URLs are not from allowed domains (GIPHY, Tenor, Dropbox, Imgur)</p>
                )}
                {(() => {
                  const firstValid = (selectedNode.data?.gifUrls || []).find(u => u && isValidMediaUrl(u));
                  return firstValid ? (
                    <div className="mt-2 rounded overflow-hidden border border-gray-600">
                      <img src={firstValid} alt="GIF Preview" className="w-full max-h-32 object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }} />
                    </div>
                  ) : null;
                })()}
              </div>
            </>
          )}
          
          {/* Response Node */}
          {selectedNode.type === NODE_TYPES.RESPONSE && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Prompt</label>
                <textarea
                  value={selectedNode.data?.prompt || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, prompt: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Input Type</label>
                <select
                  value={selectedNode.data?.inputType || 'choice'}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, inputType: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                >
                  <option value="choice">Multiple Choice</option>
                  <option value="text">Text Input</option>
                </select>
              </div>
              {/* Show options for both 'choice' and 'text' input types - needed for Smart Bot intent matching */}
              {(selectedNode.data?.inputType === 'choice' || selectedNode.data?.inputType === 'text') && (
                <div>
                  <label className="text-sm text-gray-400 block mb-2">
                    {selectedNode.data?.inputType === 'text'
                      ? 'Expected Answers (for Smart Bot matching)'
                      : 'Options (click to set route)'}
                  </label>
                  {selectedNode.data?.inputType === 'text' && (
                    <p className="text-xs text-cyan-400 mb-2">💡 Add expected answers here. Set 🧠 Intent to accept variations like "hello" = "salam"</p>
                  )}
                  {intents.length === 0 && (
                    <p className="text-xs text-yellow-400 mb-2">⚠️ Loading intents... (if this persists, check API connection)</p>
                  )}
                  {(selectedNode.data?.options || []).map((opt, idx) => (
                    <div key={opt.id || idx} className="bg-gray-700 rounded p-2 mb-2">
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={opt.text}
                          onChange={(e) => {
                            const newOptions = [...selectedNode.data.options];
                            newOptions[idx] = { ...newOptions[idx], text: e.target.value };
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                          }}
                          className="flex-1 bg-gray-600 text-white rounded p-2 text-sm"
                          placeholder="Option text (Latin)..."
                        />
                        <input
                          type="text"
                          value={opt.text_arabic || ''}
                          onChange={(e) => {
                            const newOptions = [...selectedNode.data.options];
                            newOptions[idx] = { ...newOptions[idx], text_arabic: e.target.value };
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                          }}
                          className="flex-1 bg-gray-600 text-white rounded p-2 text-sm text-right"
                          dir="rtl"
                          placeholder="Arabic script..."
                        />
                        <label className="flex items-center gap-1 px-2">
                          <input
                            type="checkbox"
                            checked={opt.isCorrect}
                            onChange={(e) => {
                              const newOptions = [...selectedNode.data.options];
                              newOptions[idx] = { ...newOptions[idx], isCorrect: e.target.checked };
                              updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                            }}
                          />
                          <span className="text-xs text-green-400">Correct</span>
                        </label>
                        <button
                          onClick={() => {
                            const newOptions = selectedNode.data.options.filter((_, i) => i !== idx);
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                          }}
                          className="text-red-400 hover:text-red-300 px-2"
                          title="Delete option"
                        >
                          ✕
                        </button>
                      </div>
                      
                      {/* Route selection for branching */}
                      <div className="flex gap-2 items-center">
                        <span className="text-xs text-gray-400">→ Route to:</span>
                        <select
                          value={opt.nextNodeId || ''}
                          onChange={(e) => {
                            const newOptions = [...selectedNode.data.options];
                            newOptions[idx] = { ...newOptions[idx], nextNodeId: e.target.value || null };
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                            
                            // Also add edge if target selected
                            if (e.target.value) {
                              addEdge(selectedNode.id, e.target.value);
                            }
                          }}
                          className="flex-1 bg-gray-600 text-white rounded p-1 text-xs"
                        >
                          <option value="">Select next node...</option>
                          {(selectedFlow?.nodes || [])
                            .filter(n => n.id !== selectedNode.id)
                            .map(n => (
                              <option key={n.id} value={n.id}>
                                {NODE_COLORS[n.type]?.icon} {NODE_COLORS[n.type]?.label}: {(n.data?.text_darija || n.data?.prompt || n.data?.tipText || '...').slice(0, 20)}
                              </option>
                            ))
                          }
                        </select>
                        <input
                          type="number"
                          value={opt.points || 0}
                          onChange={(e) => {
                            const newOptions = [...selectedNode.data.options];
                            newOptions[idx] = { ...newOptions[idx], points: parseInt(e.target.value) || 0 };
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                          }}
                          className="w-16 bg-gray-600 text-white rounded p-1 text-xs text-center"
                          placeholder="Pts"
                          title="Points for this option"
                        />
                        <span className="text-xs text-yellow-400">pts</span>
                      </div>

                      {/* Intent Tag for Smart Matching */}
                      <div className="flex gap-2 items-center mt-2">
                        <span className="text-xs text-gray-400">🧠 Intent:</span>
                        <select
                          value={opt.intent || ''}
                          onChange={(e) => {
                            const newOptions = [...selectedNode.data.options];
                            newOptions[idx] = { ...newOptions[idx], intent: e.target.value || null };
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                          }}
                          className="flex-1 bg-gray-600 text-white rounded p-1 text-xs"
                          title="Smart Intent - accepts any phrase with same meaning"
                        >
                          <option value="">No Intent (text match only)</option>
                          {intents.map(intent => (
                            <option key={intent.id} value={intent.name}>
                              {intent.display_name}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Translation/Meaning for this option */}
                      <div className="mt-2">
                        <input
                          type="text"
                          value={opt.translation || ''}
                          onChange={(e) => {
                            const newOptions = [...selectedNode.data.options];
                            newOptions[idx] = { ...newOptions[idx], translation: e.target.value };
                            updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                          }}
                          className="w-full bg-gray-600 text-white rounded p-1.5 text-xs"
                          placeholder="English meaning (shown to user)"
                        />
                      </div>
                    </div>
                  ))}
                  <button
                    onClick={() => {
                      const newOptions = [...(selectedNode.data.options || []), { id: generateId(), text: 'New Option', text_arabic: '', isCorrect: false, points: 0, nextNodeId: null, translation: '' }];
                      updateNode(selectedNode.id, { data: { ...selectedNode.data, options: newOptions } });
                    }}
                    className="w-full mt-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    + Add Option
                  </button>
                </div>
              )}
            </>
          )}
          
          {/* Tip Node */}
          {selectedNode.type === NODE_TYPES.TIP && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Tip Text (Latin)</label>
                <textarea
                  value={selectedNode.data?.tipText || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, tipText: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={3}
                  placeholder="💡 Helpful tip here..."
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Tip Text (Arabic Script)</label>
                <textarea
                  value={selectedNode.data?.tipText_arabic || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, tipText_arabic: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm text-right"
                  dir="rtl"
                  rows={3}
                  placeholder="Arabic script..."
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Tip Type</label>
                <select
                  value={selectedNode.data?.tipType || 'info'}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, tipType: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                >
                  <option value="info">ℹ️ Info</option>
                  <option value="warning">⚠️ Warning</option>
                  <option value="success">✅ Success</option>
                </select>
              </div>
            </>
          )}
          
          {/* Achievement Node */}
          {selectedNode.type === NODE_TYPES.ACHIEVEMENT && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Achievement Name</label>
                <input
                  type="text"
                  value={selectedNode.data?.achievementName || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, achievementName: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Icon</label>
                <input
                  type="text"
                  value={selectedNode.data?.achievementIcon || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, achievementIcon: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  placeholder="🏆"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Points</label>
                <input
                  type="number"
                  value={selectedNode.data?.points || 0}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, points: parseInt(e.target.value) } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Message</label>
                <textarea
                  value={selectedNode.data?.message || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, message: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                />
              </div>
            </>
          )}
          
          {/* Vocabulary Node */}
          {selectedNode.type === NODE_TYPES.VOCABULARY && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Word (Darija Latin)</label>
                <input
                  type="text"
                  value={selectedNode.data?.wordDarija || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, wordDarija: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Word (Arabic)</label>
                <input
                  type="text"
                  value={selectedNode.data?.wordArabic || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, wordArabic: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  dir="rtl"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Translation</label>
                <input
                  type="text"
                  value={selectedNode.data?.translation || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, translation: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Pronunciation</label>
                <input
                  type="text"
                  value={selectedNode.data?.pronunciation || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, pronunciation: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  placeholder="ah-tie"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Example</label>
                <input
                  type="text"
                  value={selectedNode.data?.example || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, example: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Audio URL</label>
                <input
                  type="text"
                  value={selectedNode.data?.audioUrl || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, audioUrl: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
            </>
          )}
          
          {/* Challenge Node */}
          {selectedNode.type === NODE_TYPES.CHALLENGE && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Challenge Type</label>
                <select
                  value={selectedNode.data?.challengeType || 'choice'}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, challengeType: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                >
                  <option value="choice">Multiple Choice</option>
                  <option value="unscramble">Unscramble</option>
                  <option value="fill_blank">Fill in Blank</option>
                </select>
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Question (Latin)</label>
                <textarea
                  value={selectedNode.data?.question || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, question: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Question (Arabic Script)</label>
                <textarea
                  value={selectedNode.data?.question_arabic || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, question_arabic: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm text-right"
                  dir="rtl"
                  rows={2}
                  placeholder="Arabic script..."
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Correct Answer</label>
                <input
                  type="text"
                  value={selectedNode.data?.correctAnswer || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, correctAnswer: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Points</label>
                <input
                  type="number"
                  value={selectedNode.data?.points || 0}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, points: parseInt(e.target.value) } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                />
              </div>
            </>
          )}
          
          {/* End Node */}
          {selectedNode.type === NODE_TYPES.END && (
            <>
              <div>
                <label className="text-sm text-gray-400 block mb-1">End Message</label>
                <textarea
                  value={selectedNode.data?.message || ''}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, message: e.target.value } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={2}
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Summary (one per line)</label>
                <textarea
                  value={(selectedNode.data?.summary || []).join('\n')}
                  onChange={(e) => updateNode(selectedNode.id, { data: { ...selectedNode.data, summary: e.target.value.split('\n').filter(s => s.trim()) } })}
                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  rows={4}
                  placeholder="Labas - How are you?&#10;Shukran - Thank you"
                />
              </div>
            </>
          )}
          
          {/* Connect to Next Node - for all node types except END and RESPONSE (which has its own routing) */}
          {selectedNode.type !== NODE_TYPES.END && selectedNode.type !== NODE_TYPES.RESPONSE && selectedNode.type !== NODE_TYPES.BRANCH && (
            <div className="border-t border-gray-700 pt-4 mt-4">
              <label className="text-sm text-gray-400 block mb-2">→ Connect to Next Node</label>
              <select
                value={selectedNode.data?.nextNodeId || ''}
                onChange={(e) => {
                  const targetId = e.target.value;
                  updateNode(selectedNode.id, { data: { ...selectedNode.data, nextNodeId: targetId || null } });
                  
                  // Also add visual edge
                  if (targetId) {
                    addEdge(selectedNode.id, targetId);
                  }
                }}
                className="w-full bg-gray-700 text-white rounded p-2 text-sm"
              >
                <option value="">Select next node...</option>
                {(selectedFlow?.nodes || [])
                  .filter(n => n.id !== selectedNode.id)
                  .map(n => (
                    <option key={n.id} value={n.id}>
                      {NODE_COLORS[n.type]?.icon} {NODE_COLORS[n.type]?.label}: {(n.data?.text_darija || n.data?.prompt || n.data?.tipText || n.data?.message || '...').slice(0, 25)}
                    </option>
                  ))
                }
              </select>
              <p className="text-xs text-gray-500 mt-1">Or drag from the connection point below the node</p>
            </div>
          )}
          
          {/* Delete button */}
          {selectedNode.type !== NODE_TYPES.START && (
            <button
              onClick={() => deleteNode(selectedNode.id)}
              className="w-full mt-4 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
            >
              🗑️ Delete Node
            </button>
          )}
        </div>
      </div>
    );
  };
  
  // Get filtered flows - filter ONLY by bot_id for accurate filtering
  const getFilteredFlows = () => {
    if (flowFilter === 'all') return flows;
    // Filter strictly by bot_id only
    return flows.filter(f => f.bot_id === flowFilter);
  };
  
  // Render flows list
  const renderFlowsList = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white">Conversation Flows</h3>
        <div className="flex gap-2">
          <button
            onClick={() => setShowNewFlowForm(true)}
            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
          >
            ➕ New Flow
          </button>
        </div>
      </div>
      
      {/* Filter buttons - show ALL bots */}
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <span className="text-gray-400 text-sm">Filter by Bot:</span>
        <button 
          onClick={() => setFlowFilter('all')}
          className={`px-3 py-1 rounded text-xs text-white transition-all ${flowFilter === 'all' ? 'bg-gray-600 ring-2 ring-yellow-400' : 'bg-gray-700 hover:bg-gray-600'}`}
        >
          All ({flows.length})
        </button>
        {(Array.isArray(bots) ? bots : []).map(bot => {
          const botFlowCount = flows.filter(f => f.bot_id === bot.id).length;
          const colorMap = {
            pink: 'bg-pink-600 ring-pink-400 bg-pink-800 hover:bg-pink-700',
            blue: 'bg-blue-600 ring-blue-400 bg-blue-800 hover:bg-blue-700',
            purple: 'bg-purple-600 ring-purple-400 bg-purple-800 hover:bg-purple-700',
            green: 'bg-green-600 ring-green-400 bg-green-800 hover:bg-green-700',
            orange: 'bg-orange-600 ring-orange-400 bg-orange-800 hover:bg-orange-700',
            teal: 'bg-teal-600 ring-teal-400 bg-teal-800 hover:bg-teal-700'
          };
          const isSelected = flowFilter === bot.id;
          return (
            <button 
              key={bot.id}
              onClick={() => setFlowFilter(bot.id)}
              className={`px-3 py-1 rounded text-xs text-white transition-all flex items-center gap-1 ${
                isSelected 
                  ? `bg-${bot.color || 'gray'}-600 ring-2 ring-${bot.color || 'gray'}-400` 
                  : `bg-${bot.color || 'gray'}-800 hover:bg-${bot.color || 'gray'}-700`
              }`}
              style={{
                backgroundColor: isSelected ? (bot.color === 'pink' ? '#db2777' : bot.color === 'blue' ? '#2563eb' : bot.color === 'purple' ? '#9333ea' : bot.color === 'green' ? '#16a34a' : bot.color === 'orange' ? '#ea580c' : bot.color === 'teal' ? '#0d9488' : '#4b5563') : undefined
              }}
            >
              {bot.avatarUrl ? (
                <img src={bot.avatarUrl} alt={bot.name} className="w-5 h-5 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
              ) : (
                <span>{bot.icon}</span>
              )}
              <span>{bot.name}</span>
              <span className="bg-white/20 px-1.5 rounded-full">{botFlowCount}</span>
            </button>
          );
        })}
      </div>
      
      {/* Flows grid - show bot name on each card */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {getFilteredFlows().map(flow => {
          // Find the bot for this flow - ONLY by bot_id for accuracy
          const flowBot = (Array.isArray(bots) ? bots : []).find(b => b.id === flow.bot_id) 
            || { name: flow.bot_id || 'Unknown', icon: '👤', color: 'gray', avatarUrl: '' };
          
          return (
            <div
              key={flow.id}
              onClick={() => setSelectedFlow(flow)}
              className={`p-4 rounded-lg cursor-pointer transition-all ${
                selectedFlow?.id === flow.id 
                  ? 'bg-blue-600 ring-2 ring-yellow-400' 
                  : 'bg-gray-700 hover:bg-gray-600'
              }`}
            >
              {/* Bot badge at top */}
              <div className="flex items-center justify-between mb-2">
                <span className={`px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1`}
                  style={{ backgroundColor: flowBot.color === 'pink' ? '#be185d' : flowBot.color === 'blue' ? '#1d4ed8' : flowBot.color === 'purple' ? '#7c3aed' : flowBot.color === 'green' ? '#15803d' : flowBot.color === 'orange' ? '#c2410c' : flowBot.color === 'teal' ? '#0f766e' : '#374151' }}
                >
                  {flowBot.avatarUrl ? (
                    <img src={flowBot.avatarUrl} alt={flowBot.name} className="w-4 h-4 rounded-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                  ) : (
                    <span>{flowBot.icon}</span>
                  )}
                  <span className="text-white">{flowBot.name}</span>
                </span>
                <span className="text-xs text-gray-400">{flow.script_type}</span>
              </div>
              
              <h4 className="font-bold text-white text-sm mb-1">{flow.name}</h4>
              <p className="text-xs text-gray-300 truncate mb-2">{flow.description}</p>
              
              <div className="flex gap-1 flex-wrap">
                <span className="px-2 py-0.5 bg-gray-600 rounded text-xs text-gray-300">{flow.difficulty}</span>
                {flow.is_free && <span className="px-2 py-0.5 bg-green-600 rounded text-xs text-white">Free</span>}
                <span className="px-2 py-0.5 bg-gray-600 rounded text-xs text-gray-300">{flow.nodes?.length || 0} nodes</span>
              </div>
            </div>
          );
        })}
      </div>
      
      {getFilteredFlows().length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-400">
            {flows.length === 0 
              ? "No flows yet. Click 'New Flow' to get started!"
              : `No flows assigned to this bot yet. Create one or reassign existing flows!`
            }
          </p>
        </div>
      )}
    </div>
  );
  
  // Render stories
  // Render stories list with edit functionality
  const renderStoriesList = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-bold text-white">📖 Story Mode</h3>
        <button 
          onClick={() => setShowNewStoryForm(true)}
          className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
        >
          ➕ New Story
        </button>
      </div>
      
      {/* Edit Story Modal */}
      {editingStory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">Edit Story</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Name</label>
                <input
                  type="text"
                  value={editingStory.name || ''}
                  onChange={(e) => setEditingStory(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-700 text-white rounded p-2"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Description</label>
                <textarea
                  value={editingStory.description || ''}
                  onChange={(e) => setEditingStory(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-gray-700 text-white rounded p-2"
                  rows={3}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Bot Gender</label>
                  <select
                    value={editingStory.bot_gender || 'female'}
                    onChange={(e) => setEditingStory(prev => ({ ...prev, bot_gender: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="female">Female</option>
                    <option value="male">Male</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Script Type</label>
                  <select
                    value={editingStory.script_type || 'latin'}
                    onChange={(e) => setEditingStory(prev => ({ ...prev, script_type: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="latin">Latin</option>
                    <option value="arabic">Arabic</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Difficulty</label>
                  <select
                    value={editingStory.difficulty || 'Beginner'}
                    onChange={(e) => setEditingStory(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Access</label>
                  <select
                    value={editingStory.is_free ? 'free' : 'pro'}
                    onChange={(e) => setEditingStory(prev => ({ ...prev, is_free: e.target.value === 'free' }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="free">Free</option>
                    <option value="pro">Pro Only</option>
                  </select>
                </div>
              </div>
              
              {/* Chapters Management */}
              <div>
                <label className="text-sm text-gray-400 block mb-2">Chapters ({editingStory.chapters?.length || 0}) - Drag to reorder</label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {editingStory.chapters?.map((chapter, idx) => {
                    // Handle both formats: string (flow_id) or object {flow_id, flow_name, order}
                    const isString = typeof chapter === 'string';
                    const flowId = isString ? chapter : chapter.flow_id;
                    const flowData = flows.find(f => f.id === flowId);
                    const flowName = isString ? (flowData?.name || chapter) : (chapter.flow_name || flowData?.name || chapter.flow_id);
                    
                    return (
                    <div 
                      key={idx} 
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData('chapterIdx', idx.toString());
                      }}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const fromIdx = parseInt(e.dataTransfer.getData('chapterIdx'));
                        const toIdx = idx;
                        if (fromIdx !== toIdx) {
                          const newChapters = [...editingStory.chapters];
                          const [moved] = newChapters.splice(fromIdx, 1);
                          newChapters.splice(toIdx, 0, moved);
                          setEditingStory(prev => ({ ...prev, chapters: newChapters }));
                        }
                      }}
                      className="flex items-center gap-2 bg-gray-700 rounded p-2 cursor-grab active:cursor-grabbing hover:bg-gray-600 transition-colors"
                    >
                      {/* Drag Handle */}
                      <span className="text-gray-500 cursor-grab" title="Drag to reorder">⠿</span>
                      
                      {/* Chapter Number */}
                      <span className="text-purple-400 font-bold text-sm w-6">{idx + 1}.</span>
                      
                      {/* Chapter Name */}
                      <span className="text-white text-sm flex-1 truncate">{flowName}</span>
                      
                      {/* Edit Button - Change Flow */}
                      <select
                        value={flowId}
                        onChange={(e) => {
                          const newFlow = flows.find(f => f.id === e.target.value);
                          if (newFlow) {
                            const newChapters = [...editingStory.chapters];
                            // Convert to object format
                            newChapters[idx] = { flow_id: newFlow.id, flow_name: newFlow.name };
                            setEditingStory(prev => ({ ...prev, chapters: newChapters }));
                          }
                        }}
                        className="bg-gray-600 text-white text-xs rounded px-2 py-1 max-w-[120px]"
                        title="Change conversation flow"
                      >
                        {flows.map(flow => (
                          <option key={flow.id} value={flow.id}>{flow.name}</option>
                        ))}
                      </select>
                      
                      {/* Move Up */}
                      <button
                        onClick={() => {
                          if (idx > 0) {
                            const newChapters = [...editingStory.chapters];
                            [newChapters[idx - 1], newChapters[idx]] = [newChapters[idx], newChapters[idx - 1]];
                            setEditingStory(prev => ({ ...prev, chapters: newChapters }));
                          }
                        }}
                        disabled={idx === 0}
                        className={`text-xs px-1 ${idx === 0 ? 'text-gray-600' : 'text-blue-400 hover:text-blue-300'}`}
                        title="Move up"
                      >
                        ↑
                      </button>
                      
                      {/* Move Down */}
                      <button
                        onClick={() => {
                          if (idx < editingStory.chapters.length - 1) {
                            const newChapters = [...editingStory.chapters];
                            [newChapters[idx], newChapters[idx + 1]] = [newChapters[idx + 1], newChapters[idx]];
                            setEditingStory(prev => ({ ...prev, chapters: newChapters }));
                          }
                        }}
                        disabled={idx === editingStory.chapters.length - 1}
                        className={`text-xs px-1 ${idx === editingStory.chapters.length - 1 ? 'text-gray-600' : 'text-blue-400 hover:text-blue-300'}`}
                        title="Move down"
                      >
                        ↓
                      </button>
                      
                      {/* Delete Button */}
                      <button
                        onClick={() => {
                          const newChapters = editingStory.chapters.filter((_, i) => i !== idx);
                          setEditingStory(prev => ({ ...prev, chapters: newChapters }));
                        }}
                        className="text-red-400 hover:text-red-300 text-xs px-1"
                        title="Remove chapter"
                      >
                        ✕
                      </button>
                    </div>
                  );
                  })}
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) {
                      const flow = flows.find(f => f.id === e.target.value);
                      if (flow) {
                        const newChapter = { flow_id: flow.id, flow_name: flow.name };
                        setEditingStory(prev => ({ ...prev, chapters: [...(prev.chapters || []), newChapter] }));
                      }
                      e.target.value = '';
                    }
                  }}
                  className="w-full bg-gray-700 text-white rounded p-2 mt-2"
                >
                  <option value="">+ Add Chapter from Flows...</option>
                  {flows.map(flow => (
                    <option key={flow.id} value={flow.id}>{flow.name}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex gap-2 pt-4">
                <button
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API_URL}/api/conversation-challenge/stories/${editingStory.id}`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(editingStory)
                      });
                      if (response.ok) {
                        setStories(prev => prev.map(s => s.id === editingStory.id ? editingStory : s));
                        showNotification('Story updated!', 'success');
                        setEditingStory(null);
                      } else {
                        showNotification('Failed to update story', 'error');
                      }
                    } catch (error) {
                      showNotification('Error updating story', 'error');
                    }
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded"
                >
                  💾 Save Changes
                </button>
                <button
                  onClick={() => setEditingStory(null)}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {stories.map(story => (
          <div
            key={story.id}
            className="p-4 rounded-lg bg-gradient-to-br from-purple-800 to-indigo-800 relative group"
          >
            {/* Action buttons */}
            <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              <button
                onClick={(e) => { e.stopPropagation(); setEditingStory(story); }}
                className="w-7 h-7 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs flex items-center justify-center"
                title="Edit Story"
              >
                ✏️
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); deleteStory(story.id); }}
                className="w-7 h-7 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs flex items-center justify-center"
                title="Delete Story"
              >
                🗑️
              </button>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-3xl">📖</span>
              <div>
                <h4 className="font-bold text-white">{story.name}</h4>
                <span className="text-xs text-gray-300">{story.chapters?.length || 0} chapters • {story.total_duration || 0} min</span>
              </div>
            </div>
            <p className="text-sm text-gray-200">{story.description}</p>
            <div className="flex gap-1 mt-2">
              <span className="px-2 py-0.5 bg-gray-700 rounded text-xs text-gray-300">{story.difficulty}</span>
              {story.is_free && <span className="px-2 py-0.5 bg-green-600 rounded text-xs text-white">Free</span>}
              {!story.is_free && <span className="px-2 py-0.5 bg-yellow-600 rounded text-xs text-white">PRO</span>}
            </div>
          </div>
        ))}
      </div>
      
      {stories.length === 0 && (
        <div className="text-center py-8">
          <p className="text-gray-400">No stories yet. Create flows first, then combine them into stories!</p>
        </div>
      )}
    </div>
  );
  
  // Color options for bots
  const BOT_COLORS = [
    { id: 'pink', name: 'Pink', gradient: 'from-pink-900 to-pink-800' },
    { id: 'blue', name: 'Blue', gradient: 'from-blue-900 to-blue-800' },
    { id: 'green', name: 'Green', gradient: 'from-green-900 to-green-800' },
    { id: 'purple', name: 'Purple', gradient: 'from-purple-900 to-purple-800' },
    { id: 'orange', name: 'Orange', gradient: 'from-orange-900 to-orange-800' },
    { id: 'teal', name: 'Teal', gradient: 'from-teal-900 to-teal-800' },
    { id: 'red', name: 'Red', gradient: 'from-red-900 to-red-800' },
    { id: 'indigo', name: 'Indigo', gradient: 'from-indigo-900 to-indigo-800' }
  ];

  // Get bot by ID helper
  const getBotById = (botId) => {
    if (Array.isArray(bots)) {
      return bots.find(b => b.id === botId);
    }
    // Fallback for old format
    return bots[botId] || null;
  };

  // Get bot by gender helper (for backward compatibility)
  const getBotByGender = (gender) => {
    if (Array.isArray(bots)) {
      return bots.find(b => b.gender === gender || b.id === gender) || { name: gender === 'female' ? 'Fatima' : 'Ahmed', icon: gender === 'female' ? '👩' : '👨' };
    }
    return bots[gender] || { name: gender === 'female' ? 'Fatima' : 'Ahmed', icon: gender === 'female' ? '👩' : '👨' };
  };

  // Generate unique bot ID (wrapped in useCallback for purity)
  const generateBotId = useCallback(() => {
    return `bot_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Save bot (create or update)
  const saveBot = async (botData) => {
    try {
      const isNew = !botData.id || !bots.find(b => b.id === botData.id);
      const bot = isNew ? { ...botData, id: generateBotId() } : botData;
      
      const response = await fetch(`${API_URL}/api/conversation-challenge/bots/${bot.id}`, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bot)
      });
      
      if (response.ok) {
        if (isNew) {
          setBots(prev => Array.isArray(prev) ? [...prev, bot] : [bot]);
        } else {
          setBots(prev => Array.isArray(prev) ? prev.map(b => b.id === bot.id ? bot : b) : [bot]);
        }
        showNotification(isNew ? 'Bot created!' : 'Bot updated!', 'success');
        setShowBotForm(false);
        setEditingBot(null);
        setNewBotData({ id: '', name: '', icon: '👤', gender: 'female', color: 'blue', personality: 'friendly', greeting: '', background: '', is_smart_bot: false });
        return true;
      } else {
        showNotification('Failed to save bot', 'error');
        return false;
      }
    } catch (error) {
      console.error('Error saving bot:', error);
      showNotification('Error saving bot', 'error');
      return false;
    }
  };

  // Delete bot
  const deleteBot = async (botId) => {
    if (!confirm('Are you sure you want to delete this bot?')) return;
    try {
      const response = await fetch(`${API_URL}/api/conversation-challenge/bots/${botId}`, {
        method: 'DELETE'
      });
      
      if (response.ok) {
        setBots(prev => Array.isArray(prev) ? prev.filter(b => b.id !== botId) : []);
        showNotification('Bot deleted!', 'success');
      } else {
        showNotification('Failed to delete bot', 'error');
      }
    } catch (error) {
      showNotification('Error deleting bot', 'error');
    }
  };

  // Render bots management with full CRUD
  const renderBotsManager = () => {
    const botsList = Array.isArray(bots) ? bots : Object.entries(bots).map(([key, value]) => ({ ...value, id: key }));
    
    return (
      <div className="flex-1 overflow-y-auto p-4">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-lg font-bold text-white">🤖 Bot Characters</h3>
            <p className="text-gray-400 text-sm">Create and manage conversation partners</p>
          </div>
          <button 
            onClick={() => {
              setNewBotData({ id: '', name: '', icon: '👤', gender: 'female', color: 'blue', personality: 'friendly', greeting: '', background: '', is_smart_bot: false });
              setEditingBot(null);
              setShowBotForm(true);
            }}
            className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium"
          >
            ➕ Add New Bot
          </button>
        </div>
        
        {/* Bot Form Modal */}
        {(showBotForm || editingBot) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
              <h3 className="text-xl font-bold text-white mb-4">
                {editingBot ? '✏️ Edit Bot' : '➕ Create New Bot'}
              </h3>
              <div className="space-y-4">
                {/* Avatar Preview */}
                <div className="flex justify-center mb-2">
                  <div className="w-20 h-20 rounded-full bg-gray-700 flex items-center justify-center overflow-hidden border-2 border-gray-600">
                    {(editingBot?.avatarUrl || newBotData.avatarUrl) ? (
                      <img 
                        src={editingBot?.avatarUrl || newBotData.avatarUrl} 
                        alt="Bot avatar" 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      />
                    ) : null}
                    <span 
                      className="text-4xl" 
                      style={{ display: (editingBot?.avatarUrl || newBotData.avatarUrl) ? 'none' : 'flex' }}
                    >
                      {editingBot?.icon || newBotData.icon || '👤'}
                    </span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Name *</label>
                    <input
                      type="text"
                      value={editingBot?.name || newBotData.name}
                      onChange={(e) => editingBot 
                        ? setEditingBot(prev => ({ ...prev, name: e.target.value }))
                        : setNewBotData(prev => ({ ...prev, name: e.target.value }))
                      }
                      className="w-full bg-gray-700 text-white rounded p-2"
                      placeholder="e.g., Fatima"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Icon (emoji fallback)</label>
                    <input
                      type="text"
                      value={editingBot?.icon || newBotData.icon}
                      onChange={(e) => editingBot 
                        ? setEditingBot(prev => ({ ...prev, icon: e.target.value }))
                        : setNewBotData(prev => ({ ...prev, icon: e.target.value }))
                      }
                      className="w-full bg-gray-700 text-white rounded p-2 text-2xl text-center"
                      placeholder="👤"
                    />
                  </div>
                </div>
                
                {/* Avatar URL - Priority over icon */}
                <div>
                  <label className="text-sm text-gray-400 block mb-1">🖼️ Avatar URL (Dropbox - priority over icon)</label>
                  <input
                    type="text"
                    value={editingBot?.avatarUrl || newBotData.avatarUrl}
                    onChange={(e) => editingBot 
                      ? setEditingBot(prev => ({ ...prev, avatarUrl: e.target.value }))
                      : setNewBotData(prev => ({ ...prev, avatarUrl: e.target.value }))
                    }
                    className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    placeholder="https://dl.dropboxusercontent.com/..."
                  />
                  <p className="text-xs text-gray-500 mt-1">Paste Dropbox direct link (use ?dl=1 for raw image)</p>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Gender</label>
                    <select
                      value={editingBot?.gender || newBotData.gender}
                      onChange={(e) => editingBot 
                        ? setEditingBot(prev => ({ ...prev, gender: e.target.value }))
                        : setNewBotData(prev => ({ ...prev, gender: e.target.value }))
                      }
                      className="w-full bg-gray-700 text-white rounded p-2"
                    >
                      <option value="female">Female</option>
                      <option value="male">Male</option>
                      <option value="neutral">Neutral</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Color Theme</label>
                    <select
                      value={editingBot?.color || newBotData.color}
                      onChange={(e) => editingBot 
                        ? setEditingBot(prev => ({ ...prev, color: e.target.value }))
                        : setNewBotData(prev => ({ ...prev, color: e.target.value }))
                      }
                      className="w-full bg-gray-700 text-white rounded p-2"
                    >
                      {BOT_COLORS.map(c => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Personality</label>
                  <select
                    value={editingBot?.personality || newBotData.personality}
                    onChange={(e) => editingBot 
                      ? setEditingBot(prev => ({ ...prev, personality: e.target.value }))
                      : setNewBotData(prev => ({ ...prev, personality: e.target.value }))
                    }
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="friendly">😊 Friendly - Casual, warm with emojis</option>
                    <option value="teacher">📚 Teacher - Structured, educational</option>
                    <option value="playful">🎮 Playful - Fun, jokes</option>
                    <option value="calm">🧘 Calm - Gentle, supportive</option>
                    <option value="professional">💼 Professional - Formal, business-like</option>
                    <option value="energetic">⚡ Energetic - Excited, motivating</option>
                  </select>
                </div>
                
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Custom Greeting</label>
                  <input
                    type="text"
                    value={editingBot?.greeting || newBotData.greeting}
                    onChange={(e) => editingBot 
                      ? setEditingBot(prev => ({ ...prev, greeting: e.target.value }))
                      : setNewBotData(prev => ({ ...prev, greeting: e.target.value }))
                    }
                    className="w-full bg-gray-700 text-white rounded p-2"
                    placeholder="e.g., Salam! Ana Fatima 😊"
                  />
                </div>
                
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Background/Bio</label>
                  <textarea
                    value={editingBot?.background || newBotData.background}
                    onChange={(e) => editingBot 
                      ? setEditingBot(prev => ({ ...prev, background: e.target.value }))
                      : setNewBotData(prev => ({ ...prev, background: e.target.value }))
                    }
                    className="w-full bg-gray-700 text-white rounded p-2"
                    rows={2}
                    placeholder="e.g., A friendly café owner from Marrakech..."
                  />
                </div>

                {/* Smart Bot Toggle */}
                <div className="bg-cyan-900/30 border border-cyan-700 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <label className="text-cyan-300 font-medium">🧠 Smart Bot</label>
                      <p className="text-cyan-200/60 text-xs">Uses intent matching - understands "hello" = "salam" = "hi"</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editingBot?.is_smart_bot || newBotData.is_smart_bot || false}
                        onChange={(e) => editingBot
                          ? setEditingBot(prev => ({ ...prev, is_smart_bot: e.target.checked }))
                          : setNewBotData(prev => ({ ...prev, is_smart_bot: e.target.checked }))
                        }
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-cyan-600"></div>
                    </label>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    onClick={() => saveBot(editingBot || newBotData)}
                    disabled={!(editingBot?.name || newBotData.name)}
                    className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded font-medium"
                  >
                    💾 {editingBot ? 'Save Changes' : 'Create Bot'}
                  </button>
                  <button
                    onClick={() => {
                      setShowBotForm(false);
                      setEditingBot(null);
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Bots Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {botsList.map(bot => {
            const colorTheme = BOT_COLORS.find(c => c.id === bot.color) || BOT_COLORS[0];
            // Count flows for this bot - ONLY by bot_id for accurate count
            const botFlowCount = flows.filter(f => f.bot_id === bot.id).length;
            return (
              <div
                key={bot.id}
                className={`p-4 rounded-lg bg-gradient-to-r ${colorTheme.gradient} relative group ${!bot.is_active ? 'opacity-60' : ''}`}
              >
                {/* Active indicator */}
                {bot.is_active && (
                  <div className="absolute top-2 left-2 w-3 h-3 bg-green-400 rounded-full animate-pulse" title="Active - Visible to users" />
                )}

                {/* Smart Bot indicator */}
                {bot.is_smart_bot && (
                  <div className="absolute top-2 left-7 px-2 py-0.5 bg-cyan-500 text-white text-xs rounded-full font-medium" title="Smart Bot - Uses intent matching">
                    🧠 Smart
                  </div>
                )}
                
                {/* Action buttons */}
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={async () => {
                      try {
                        const response = await fetch(`${API_URL}/api/conversation-challenge/bots/${bot.id}/toggle-active?is_active=${!bot.is_active}`, { method: 'POST' });
                        if (response.ok) {
                          setBots(prev => prev.map(b => b.id === bot.id ? { ...b, is_active: !b.is_active } : b));
                          showNotification(bot.is_active ? `${bot.name} hidden from users` : `${bot.name} now visible to users`, 'success');
                        }
                      } catch (error) {
                        showNotification('Failed to toggle bot visibility', 'error');
                      }
                    }}
                    className={`w-7 h-7 ${bot.is_active ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-600 hover:bg-gray-500'} text-white rounded-full text-xs flex items-center justify-center`}
                    title={bot.is_active ? "Active - Click to hide" : "Hidden - Click to show"}
                  >
                    {bot.is_active ? '👁️' : '👁️‍🗨️'}
                  </button>
                  <button
                    onClick={() => setEditingBot(bot)}
                    className="w-7 h-7 bg-blue-600 hover:bg-blue-700 text-white rounded-full text-xs flex items-center justify-center"
                    title="Edit Bot"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => deleteBot(bot.id)}
                    className="w-7 h-7 bg-red-600 hover:bg-red-700 text-white rounded-full text-xs flex items-center justify-center"
                    title="Delete Bot"
                  >
                    🗑️
                  </button>
                </div>
                
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center text-3xl overflow-hidden">
                    {bot.avatarUrl ? (
                      <img src={bot.avatarUrl} alt={bot.name} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }} />
                    ) : null}
                    <span style={{ display: bot.avatarUrl ? 'none' : 'block' }}>{bot.icon || '👤'}</span>
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-white font-bold text-lg">{bot.name}</h4>
                      {!bot.is_active && <span className="px-1.5 py-0.5 bg-gray-800/50 rounded text-xs text-gray-300">Hidden</span>}
                    </div>
                    <span className="text-xs text-white/70 capitalize">{bot.gender} • {bot.personality}</span>
                  </div>
                </div>
                
                {bot.greeting && (
                  <p className="text-sm text-white/80 italic mb-2">&ldquo;{bot.greeting}&rdquo;</p>
                )}
                
                {bot.background && (
                  <p className="text-xs text-white/60 line-clamp-2 mb-2">{bot.background}</p>
                )}
                
                {/* Flow count badge */}
                <div className="flex items-center gap-2 mt-2 pt-2 border-t border-white/10">
                  <span className="px-2 py-1 bg-white/10 rounded text-xs text-white/80">
                    💬 {botFlowCount} {botFlowCount === 1 ? 'conversation' : 'conversations'}
                  </span>
                  {bot.is_active && <span className="px-2 py-1 bg-green-600/30 rounded text-xs text-green-300">✓ Active</span>}
                </div>
              </div>
            );
          })}
        </div>
        
        {botsList.length === 0 && (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🤖</div>
            <p className="text-gray-400 mb-4">No bots created yet. Add your first conversation partner!</p>
            <div className="flex justify-center gap-3">
              <button 
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_URL}/api/conversation-challenge/seed-bots`, { method: 'POST' });
                    if (response.ok) {
                      const data = await response.json();
                      loadBots();
                      showNotification(`Loaded ${data.bots?.length || 0} default bots!`, 'success');
                    }
                  } catch (error) {
                    showNotification('Failed to seed bots', 'error');
                  }
                }}
                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg"
              >
                🎭 Load Default Bots
              </button>
              <button 
                onClick={() => {
                  setNewBotData({ id: '', name: '', icon: '👤', gender: 'female', color: 'blue', personality: 'friendly', greeting: '', background: '', is_active: true, is_smart_bot: false });
                  setShowBotForm(true);
                }}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
              >
                ➕ Create Custom Bot
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // ============================================
  // SMART BOT INTENTS MANAGER
  // ============================================

  // Save intent (create or update)
  const saveIntent = async (intentData) => {
    try {
      const isEditing = editingIntent !== null;
      const url = isEditing
        ? `${API_URL}/api/conversation-challenge/intents/${editingIntent.id}`
        : `${API_URL}/api/conversation-challenge/intents`;

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(intentData)
      });

      if (response.ok) {
        showNotification(isEditing ? 'Intent updated!' : 'Intent created!', 'success');
        loadIntents();
        setShowIntentForm(false);
        setEditingIntent(null);
        setNewIntentData({ name: '', display_name: '', description: '', phrases: [], is_active: true });
      } else {
        showNotification('Error saving intent', 'error');
      }
    } catch (error) {
      showNotification('Error saving intent', 'error');
    }
  };

  // Delete intent
  const deleteIntent = (intentId) => {
    setConfirmAction({
      message: 'Are you sure you want to delete this intent?',
      onConfirm: async () => {
        setConfirmAction(null);
        try {
          const response = await fetch(`${API_URL}/api/conversation-challenge/intents/${intentId}`, {
            method: 'DELETE'
          });
          if (response.ok) {
            showNotification('Intent deleted!', 'success');
            loadIntents();
          } else {
            showNotification('Error deleting intent', 'error');
          }
        } catch (error) {
          showNotification('Error deleting intent', 'error');
        }
      }
    });
  };

  // Add phrase to intent
  const addPhraseToIntent = (phrase) => {
    if (!phrase.trim()) return;
    const normalizedPhrase = phrase.toLowerCase().trim();
    if (!newIntentData.phrases.includes(normalizedPhrase)) {
      setNewIntentData(prev => ({
        ...prev,
        phrases: [...prev.phrases, normalizedPhrase]
      }));
    }
    setNewPhrase('');
  };

  // Remove phrase from intent
  const removePhraseFromIntent = (phraseToRemove) => {
    setNewIntentData(prev => ({
      ...prev,
      phrases: prev.phrases.filter(p => p !== phraseToRemove)
    }));
  };

  // Render Intents Manager
  const renderIntentsManager = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-bold text-white">🧠 Smart Bot Intents</h3>
          <p className="text-gray-400 text-sm">Manage what the bot understands (greetings, goodbyes, thanks, etc.)</p>
        </div>
        <button
          onClick={() => {
            setEditingIntent(null);
            setNewIntentData({ name: '', display_name: '', description: '', phrases: [], is_active: true });
            setShowIntentForm(true);
          }}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
        >
          + New Intent
        </button>
      </div>

      {/* How it works info */}
      <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 mb-6">
        <h4 className="text-blue-300 font-medium mb-2">How Intents Work:</h4>
        <ul className="text-blue-200/80 text-sm space-y-1">
          <li>• Each intent has a <strong>name</strong> (e.g., "greeting") and <strong>phrases</strong> (e.g., "salam", "hello", "marhba")</li>
          <li>• When a user types a phrase, the bot detects the intent and accepts it as correct</li>
          <li>• Example: User types "salaam" → Bot detects "greeting" intent → Accepts answer</li>
          <li>• Add common variations of each phrase (with/without spaces, different spellings)</li>
        </ul>
      </div>

      {/* Intent Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {intents.map(intent => (
          <div
            key={intent.id}
            className={`bg-gray-800 rounded-lg p-4 border ${intent.is_active ? 'border-green-600' : 'border-gray-600'}`}
          >
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-white font-bold text-lg">{intent.display_name || intent.name}</h4>
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    setEditingIntent(intent);
                    setNewIntentData({
                      name: intent.name,
                      display_name: intent.display_name,
                      description: intent.description,
                      phrases: intent.phrases || [],
                      is_active: intent.is_active
                    });
                    setShowIntentForm(true);
                  }}
                  className="text-blue-400 hover:text-blue-300"
                  title="Edit"
                >
                  ✏️
                </button>
                <button
                  onClick={() => deleteIntent(intent.id)}
                  className="text-red-400 hover:text-red-300"
                  title="Delete"
                >
                  🗑️
                </button>
              </div>
            </div>

            <p className="text-gray-400 text-sm mb-3">{intent.description || 'No description'}</p>

            <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto">
              {(intent.phrases || []).slice(0, 20).map((phrase, idx) => (
                <span
                  key={idx}
                  className="px-2 py-1 bg-gray-700 text-gray-300 rounded text-xs"
                >
                  {phrase}
                </span>
              ))}
              {(intent.phrases || []).length > 20 && (
                <span className="px-2 py-1 bg-gray-600 text-gray-400 rounded text-xs">
                  +{intent.phrases.length - 20} more
                </span>
              )}
            </div>

            <div className="mt-3 flex items-center justify-between text-xs text-gray-500">
              <span>{intent.phrases?.length || 0} phrases</span>
              <span className={intent.is_active ? 'text-green-400' : 'text-gray-500'}>
                {intent.is_active ? '✓ Active' : '○ Inactive'}
              </span>
            </div>
          </div>
        ))}
      </div>

      {intents.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          <p className="text-4xl mb-4">🧠</p>
          <p>No intents yet. Create your first intent!</p>
        </div>
      )}

      {/* Intent Form Modal */}
      {showIntentForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-white mb-4">
              {editingIntent ? `Edit Intent: ${editingIntent.display_name}` : 'Create New Intent'}
            </h3>

            <div className="space-y-4">
              {/* Name (ID) */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  Intent Name (ID) <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newIntentData.name}
                  onChange={(e) => setNewIntentData(prev => ({ ...prev, name: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
                  placeholder="e.g., greeting, goodbye, thanks"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  disabled={editingIntent !== null}
                />
                <p className="text-gray-500 text-xs mt-1">Lowercase, no spaces (used as identifier)</p>
              </div>

              {/* Display Name */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  Display Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={newIntentData.display_name}
                  onChange={(e) => setNewIntentData(prev => ({ ...prev, display_name: e.target.value }))}
                  placeholder="e.g., Greeting, Goodbye, Thanks"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">Description</label>
                <input
                  type="text"
                  value={newIntentData.description}
                  onChange={(e) => setNewIntentData(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="e.g., Ways to say hello in Darija"
                  className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                />
              </div>

              {/* Active Toggle */}
              <div className="flex items-center gap-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={newIntentData.is_active}
                    onChange={(e) => setNewIntentData(prev => ({ ...prev, is_active: e.target.checked }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
                <span className="text-gray-300">Active</span>
              </div>

              {/* Phrases */}
              <div>
                <label className="block text-gray-300 text-sm font-medium mb-1">
                  Phrases ({newIntentData.phrases.length})
                </label>
                <p className="text-gray-500 text-xs mb-2">Add all variations the bot should recognize (lowercase)</p>

                <div className="flex gap-2 mb-3">
                  <input
                    type="text"
                    value={newPhrase}
                    onChange={(e) => setNewPhrase(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPhraseToIntent(newPhrase)}
                    placeholder="Type a phrase and press Enter"
                    className="flex-1 bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none"
                  />
                  <button
                    onClick={() => addPhraseToIntent(newPhrase)}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
                  >
                    Add
                  </button>
                </div>

                <div className="flex flex-wrap gap-2 max-h-48 overflow-y-auto bg-gray-900 rounded-lg p-3">
                  {newIntentData.phrases.length === 0 ? (
                    <p className="text-gray-500 text-sm">No phrases yet. Add some above.</p>
                  ) : (
                    newIntentData.phrases.map((phrase, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-700 text-white rounded-lg text-sm flex items-center gap-2"
                      >
                        {phrase}
                        <button
                          onClick={() => removePhraseFromIntent(phrase)}
                          className="text-red-400 hover:text-red-300"
                        >
                          ×
                        </button>
                      </span>
                    ))
                  )}
                </div>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowIntentForm(false);
                  setEditingIntent(null);
                }}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() => saveIntent(newIntentData)}
                disabled={!newIntentData.name || !newIntentData.display_name}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {editingIntent ? 'Update Intent' : 'Create Intent'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Render User Avatars Manager
  const renderUserAvatarsManager = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-white">👤 User Avatar Management</h3>
        <button
          onClick={saveUserAvatars}
          className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium"
        >
          💾 Save All Avatars
        </button>
      </div>
      
      <p className="text-gray-400 text-sm mb-6">
        Add Dropbox image URLs for user avatars. Users will select these and set their own name in the player settings.
      </p>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Female Avatars */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-pink-900/50 to-rose-900/50 border border-pink-700/50">
          <h4 className="text-white font-bold mb-4 flex items-center gap-2">
            <span className="text-2xl">👩</span>
            Female Avatars (3)
          </h4>
          <div className="space-y-4">
            {(userAvatars.female || []).map((avatar, idx) => (
              <div key={idx} className="p-3 bg-gray-800/80 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
                    {avatar.url ? (
                      <img src={avatar.url} alt={`Female ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="text-3xl">👩</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-pink-400 text-xs font-bold block mb-2">Avatar {idx + 1}</span>
                    <input
                      type="text"
                      value={avatar.url || ''}
                      onChange={(e) => {
                        const newAvatars = { ...userAvatars };
                        newAvatars.female[idx] = { ...newAvatars.female[idx], url: e.target.value };
                        setUserAvatars(newAvatars);
                      }}
                      placeholder="Paste Dropbox URL here..."
                      className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {/* Male Avatars */}
        <div className="p-4 rounded-xl bg-gradient-to-br from-blue-900/50 to-indigo-900/50 border border-blue-700/50">
          <h4 className="text-white font-bold mb-4 flex items-center gap-2">
            <span className="text-2xl">👨</span>
            Male Avatars (3)
          </h4>
          <div className="space-y-4">
            {(userAvatars.male || []).map((avatar, idx) => (
              <div key={idx} className="p-3 bg-gray-800/80 rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-16 h-16 rounded-full overflow-hidden bg-gray-700 flex items-center justify-center flex-shrink-0">
                    {avatar.url ? (
                      <img src={avatar.url} alt={`Male ${idx + 1}`} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                    ) : (
                      <span className="text-3xl">👨</span>
                    )}
                  </div>
                  <div className="flex-1">
                    <span className="text-blue-400 text-xs font-bold block mb-2">Avatar {idx + 1}</span>
                    <input
                      type="text"
                      value={avatar.url || ''}
                      onChange={(e) => {
                        const newAvatars = { ...userAvatars };
                        newAvatars.male[idx] = { ...newAvatars.male[idx], url: e.target.value };
                        setUserAvatars(newAvatars);
                      }}
                      placeholder="Paste Dropbox URL here..."
                      className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Instructions */}
      <div className="mt-6 p-4 bg-gray-800/50 rounded-lg border border-gray-700">
        <h4 className="text-white font-bold mb-2">📝 How to get Dropbox URL</h4>
        <ol className="text-gray-400 text-sm space-y-1 list-decimal list-inside">
          <li>Upload image to Dropbox</li>
          <li>Right-click the file → Copy link</li>
          <li>Change <code className="bg-gray-700 px-1 rounded">?dl=0</code> to <code className="bg-gray-700 px-1 rounded">?dl=1</code> or use <code className="bg-gray-700 px-1 rounded">?raw=1</code></li>
          <li>Paste the URL above</li>
        </ol>
      </div>
    </div>
  );
  
  // Render Fuzzy Match Manager
  const renderFuzzyMatchManager = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            🔤 Fuzzy Match Settings
          </h3>
        </div>
        <p className="text-gray-400 text-sm mb-6">
          Accept Darija spelling variants automatically. When a user types "chokran" and the accepted answer is "shukran", fuzzy match detects they mean the same word.
        </p>

        {/* How it works */}
        <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 mb-6">
          <h4 className="text-blue-300 font-medium mb-2">How Fuzzy Match Works:</h4>
          <ul className="text-blue-200/80 text-sm space-y-1">
            <li>• When a user's answer doesn't exact-match, fuzzy match checks spelling similarity</li>
            <li>• Letter equivalences normalize Darija: <strong>ch=sh</strong>, <strong>3=aa</strong>, <strong>7=h</strong>, <strong>9=q</strong></li>
            <li>• After normalization, Levenshtein distance measures how close the words are</li>
            <li>• If similarity exceeds the threshold → answer accepted with a spelling tip</li>
            <li>• Works in both <strong>Response</strong> and <strong>Challenge</strong> nodes</li>
          </ul>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-emerald-900 to-green-900 border border-emerald-700 rounded-lg mb-6">
          <div>
            <span className="text-white font-medium">🔤 Fuzzy Matching</span>
            <p className="text-xs text-gray-300">Accept similar Darija spellings as correct answers</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => saveFuzzySettings({ enabled: true })}
              className={`px-4 py-2 rounded font-medium transition-all ${fuzzySettings.enabled ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
            >
              ON
            </button>
            <button
              onClick={() => saveFuzzySettings({ enabled: false })}
              className={`px-4 py-2 rounded font-medium transition-all ${!fuzzySettings.enabled ? 'bg-red-600 text-white' : 'bg-gray-600 text-gray-300'}`}
            >
              OFF
            </button>
          </div>
        </div>

        {fuzzySettings.enabled && (
          <div className="space-y-6">
            {/* Threshold Slider */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <label className="text-white font-medium block mb-2">
                Match Threshold: <span className="text-yellow-400">{fuzzySettings.threshold}%</span>
              </label>
              <input
                type="range"
                min="30"
                max="100"
                value={fuzzySettings.threshold}
                onChange={(e) => setFuzzySettings(prev => ({ ...prev, threshold: parseInt(e.target.value) }))}
                onMouseUp={(e) => saveFuzzySettings({ threshold: parseInt(e.target.value) })}
                onTouchEnd={(e) => saveFuzzySettings({ threshold: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-yellow-400"
              />
              <div className="flex justify-between text-xs text-gray-500 mt-1">
                <span>30% (very loose)</span>
                <span>75% (recommended)</span>
                <span>100% (exact only)</span>
              </div>
            </div>

            {/* Show Tip Toggle + Template */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="flex items-center gap-3 mb-3">
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={fuzzySettings.show_tip}
                    onChange={(e) => saveFuzzySettings({ show_tip: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
                </label>
                <span className="text-white font-medium">Show spelling tip to user</span>
              </div>
              {fuzzySettings.show_tip && (
                <div>
                  <label className="text-gray-400 text-sm block mb-1">Tip message template (<code className="text-yellow-400">{'{answer}'}</code> = correct spelling)</label>
                  <input
                    type="text"
                    value={fuzzySettings.tip_template}
                    onChange={(e) => setFuzzySettings(prev => ({ ...prev, tip_template: e.target.value }))}
                    onBlur={(e) => saveFuzzySettings({ tip_template: e.target.value })}
                    className="w-full bg-gray-700 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-yellow-500 focus:outline-none"
                  />
                </div>
              )}
            </div>

            {/* Equivalences Table */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-medium mb-3">Letter Equivalences</h4>
              <p className="text-gray-400 text-sm mb-4">
                Letters that should be treated as the same in Darija. Both directions are applied automatically.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mb-4">
                {fuzzySettings.equivalences.map((eq, idx) => (
                  <div key={idx} className="flex items-center gap-2 bg-gray-700 rounded-lg px-3 py-2">
                    {fuzzyEditingIdx === idx ? (
                      <>
                        <input type="text" value={fuzzyEditFrom} onChange={(e) => setFuzzyEditFrom(e.target.value)}
                          className="w-16 bg-gray-600 text-yellow-300 px-2 py-1 rounded font-mono text-sm border border-yellow-500 focus:outline-none" maxLength={5} />
                        <span className="text-gray-500">=</span>
                        <input type="text" value={fuzzyEditTo} onChange={(e) => setFuzzyEditTo(e.target.value)}
                          className="w-16 bg-gray-600 text-green-300 px-2 py-1 rounded font-mono text-sm border border-green-500 focus:outline-none" maxLength={5}
                          onKeyDown={(e) => e.key === 'Enter' && saveEditEquivalence()} />
                        <button onClick={saveEditEquivalence} className="text-green-400 hover:text-green-300 text-sm" title="Save">&#10003;</button>
                        <button onClick={() => setFuzzyEditingIdx(null)} className="text-gray-400 hover:text-gray-300 text-sm" title="Cancel">&#10007;</button>
                      </>
                    ) : (
                      <>
                        <span className="text-yellow-300 font-mono font-bold">{eq.from}</span>
                        <span className="text-gray-500">=</span>
                        <span className="text-green-300 font-mono font-bold">{eq.to}</span>
                        <button onClick={() => startEditEquivalence(idx)} className="ml-auto text-blue-400 hover:text-blue-300 text-sm" title="Edit">&#9998;</button>
                        <button onClick={() => removeEquivalence(idx)} className="text-red-400 hover:text-red-300 text-sm" title="Remove">&#10005;</button>
                      </>
                    )}
                  </div>
                ))}
              </div>

              {/* Add new equivalence */}
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={fuzzyNewFrom}
                  onChange={(e) => setFuzzyNewFrom(e.target.value)}
                  placeholder="From (e.g. ch)"
                  className="w-24 bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-yellow-500 focus:outline-none font-mono"
                  maxLength={5}
                />
                <span className="text-gray-500">=</span>
                <input
                  type="text"
                  value={fuzzyNewTo}
                  onChange={(e) => setFuzzyNewTo(e.target.value)}
                  placeholder="To (e.g. sh)"
                  className="w-24 bg-gray-700 text-white px-3 py-2 rounded border border-gray-600 focus:border-green-500 focus:outline-none font-mono"
                  maxLength={5}
                  onKeyDown={(e) => e.key === 'Enter' && addEquivalence()}
                />
                <button
                  onClick={addEquivalence}
                  disabled={!fuzzyNewFrom.trim() || !fuzzyNewTo.trim()}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white rounded font-medium text-sm"
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Live Test Panel */}
            <div className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 rounded-lg p-4 border border-purple-700">
              <h4 className="text-purple-300 font-medium mb-3">🧪 Live Test</h4>
              <p className="text-gray-400 text-sm mb-4">Test how fuzzy matching works. Enter multiple user spellings (comma separated) and the accepted answers to see which ones match.</p>
              <div className="space-y-3">
                <div>
                  <label className="text-gray-300 text-sm block mb-1">
                    User types: <span className="text-gray-500">(comma separated — test multiple spellings at once)</span>
                  </label>
                  <input
                    type="text"
                    value={fuzzyTestInput}
                    onChange={(e) => setFuzzyTestInput(e.target.value)}
                    placeholder="e.g. chokran, shokan, shukran, chkran, choukrane"
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && testFuzzyMatch()}
                  />
                </div>
                <div>
                  <label className="text-gray-300 text-sm block mb-1">
                    Accepted answers: <span className="text-gray-500">(comma separated — the correct spellings you put in the flow)</span>
                  </label>
                  <input
                    type="text"
                    value={fuzzyTestAnswers}
                    onChange={(e) => setFuzzyTestAnswers(e.target.value)}
                    placeholder="e.g. shukran"
                    className="w-full bg-gray-800 text-white px-4 py-2 rounded-lg border border-gray-600 focus:border-purple-500 focus:outline-none"
                    onKeyDown={(e) => e.key === 'Enter' && testFuzzyMatch()}
                  />
                </div>
                <button
                  onClick={testFuzzyMatch}
                  disabled={fuzzyTesting || !fuzzyTestInput.trim() || !fuzzyTestAnswers.trim()}
                  className="px-6 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white rounded-lg font-medium"
                >
                  {fuzzyTesting ? 'Testing...' : 'Test Match'}
                </button>
              </div>

              {/* Test Results */}
              {fuzzyTestResult?.multi && (
                <div className="mt-4 space-y-3">
                  {/* Summary bar */}
                  <div className="flex items-center gap-3 p-3 bg-gray-800 rounded-lg">
                    <span className="text-white font-medium">Results:</span>
                    <span className="text-green-400 font-bold">
                      {fuzzyTestResult.tests.filter(t => t.overall_match).length} matched
                    </span>
                    <span className="text-gray-500">/</span>
                    <span className="text-red-400 font-bold">
                      {fuzzyTestResult.tests.filter(t => !t.overall_match).length} failed
                    </span>
                    <span className="text-gray-500">of {fuzzyTestResult.tests.length} tested</span>
                  </div>

                  {/* Each user input result */}
                  {fuzzyTestResult.tests.map((test, tIdx) => (
                    <div key={tIdx} className={`p-3 rounded-lg border ${test.overall_match ? 'bg-green-900/30 border-green-700' : 'bg-red-900/30 border-red-700'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{test.overall_match ? '✅' : '❌'}</span>
                          <span className="text-white font-mono font-bold">"{test.user_input}"</span>
                        </div>
                        {test.best_match && (
                          <span className={`font-bold text-sm ${test.best_match.would_match ? 'text-green-400' : 'text-red-400'}`}>
                            {test.best_match.score}%
                          </span>
                        )}
                      </div>
                      {test.best_match && (
                        <div className="text-xs text-gray-400">
                          Best match: "{test.best_match.answer}" — Normalized: <span className="text-yellow-300 font-mono">"{test.best_match.normalized_user}"</span> vs <span className="text-green-300 font-mono">"{test.best_match.normalized_answer}"</span>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Save as Group button */}
                  <button
                    onClick={() => {
                      const mainWord = fuzzyTestAnswers.split(',')[0]?.trim();
                      if (!mainWord) return;
                      const variants = fuzzyTestResult.tests.map(t => ({
                        text: t.user_input,
                        score: t.best_match?.score || 0
                      }));
                      saveAsSpellingGroup(mainWord, variants);
                    }}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium"
                  >
                    Save as Spelling Group
                  </button>
                </div>
              )}
            </div>

            {/* Saved Spelling Groups */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h4 className="text-white font-medium mb-3">Saved Spelling Groups ({spellingGroups.length})</h4>
              <p className="text-gray-400 text-sm mb-4">
                Words you've tested and saved. Each group shows all accepted spelling variants for a word.
              </p>

              {spellingGroups.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <p className="text-3xl mb-2">📝</p>
                  <p>No spelling groups yet. Test some words above and click "Save as Spelling Group".</p>
                </div>
              )}

              <div className="space-y-3">
                {spellingGroups.map(group => (
                  <div key={group.main_word} className="bg-gray-700 rounded-lg p-3">
                    {editingGroup === group.main_word ? (
                      /* Edit mode */
                      <div className="space-y-2">
                        <div>
                          <label className="text-gray-400 text-xs">Main word:</label>
                          <input
                            type="text"
                            value={editGroupMainWord}
                            onChange={(e) => setEditGroupMainWord(e.target.value)}
                            className="w-full bg-gray-600 text-white px-3 py-1 rounded border border-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                          />
                        </div>
                        <div>
                          <label className="text-gray-400 text-xs">Variants (comma separated):</label>
                          <input
                            type="text"
                            value={editGroupVariants}
                            onChange={(e) => setEditGroupVariants(e.target.value)}
                            className="w-full bg-gray-600 text-white px-3 py-1 rounded border border-gray-500 focus:border-blue-500 focus:outline-none text-sm"
                          />
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => {
                              const variants = editGroupVariants.split(',').map(v => v.trim()).filter(Boolean).map(t => ({ text: t, score: 0 }));
                              const updates = { variants };
                              if (editGroupMainWord !== group.main_word) updates.new_main_word = editGroupMainWord;
                              updateSpellingGroup(group.main_word, updates);
                            }}
                            className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-sm"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingGroup(null)}
                            className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm"
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Display mode */
                      <>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-yellow-300 font-bold text-lg">{group.main_word}</span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => {
                                setEditingGroup(group.main_word);
                                setEditGroupMainWord(group.main_word);
                                setEditGroupVariants((group.variants || []).map(v => v.text).join(', '));
                              }}
                              className="text-blue-400 hover:text-blue-300 text-sm"
                              title="Edit"
                            >
                              &#9998;
                            </button>
                            <button
                              onClick={() => deleteSpellingGroup(group.main_word)}
                              className="text-red-400 hover:text-red-300 text-sm"
                              title="Delete"
                            >
                              &#10005;
                            </button>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {(group.variants || []).map((v, vi) => (
                            <span
                              key={vi}
                              className={`px-2 py-1 rounded text-xs font-mono ${v.score >= 100 ? 'bg-green-800 text-green-200' : v.score >= 75 ? 'bg-yellow-800 text-yellow-200' : 'bg-red-800 text-red-200'}`}
                            >
                              {v.text} <span className="opacity-60">{v.score}%</span>
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-gray-500 mt-2">
                          {group.variants?.length || 0} variants
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Dictionary Manager - Separate Admin Tab for AI Dictionary
  const renderDictionaryManager = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <div className="max-w-4xl mx-auto">
        <h3 className="text-xl font-bold text-white mb-2 flex items-center gap-2">
          📚 AI Dictionary Manager
          <span className="text-xs px-2 py-0.5 bg-cyan-600 rounded-full">Admin Only</span>
        </h3>
        <p className="text-gray-400 text-sm mb-6">
          Manage your vocabulary database. Search words, review AI-generated entries, and build your curated dictionary.
        </p>

        {/* Enable/Disable Groq Toggle */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-900 to-cyan-900 border border-cyan-700 rounded-lg mb-4">
          <div>
            <span className="text-white font-medium">🔌 Groq AI Engine</span>
            <p className="text-xs text-gray-300">Generate grammar for unknown words using AI</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => toggleGroqEnabled(true)}
              className={`px-4 py-2 rounded font-medium transition-all ${groqEnabled ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
            >
              🟢 ON
            </button>
            <button
              onClick={() => toggleGroqEnabled(false)}
              className={`px-4 py-2 rounded font-medium transition-all ${!groqEnabled ? 'bg-red-600 text-white' : 'bg-gray-600 text-gray-300'}`}
            >
              ⚪ OFF
            </button>
          </div>
        </div>

        {/* Groq API Key Settings */}
        <div className="p-4 bg-gradient-to-r from-orange-900/50 to-yellow-900/50 border border-orange-700/50 rounded-lg mb-6">
          <div className="flex items-center justify-between mb-3">
            <div>
              <span className="text-white font-medium">🔑 Groq API Key</span>
              <p className="text-xs text-gray-300">Required for AI dictionary features</p>
            </div>
            <div className="flex items-center gap-2">
              {groqApiKeySet ? (
                <span className="text-green-400 text-sm flex items-center gap-1">
                  ✓ Key set {groqApiKeyMasked && `(${groqApiKeyMasked})`}
                </span>
              ) : (
                <span className="text-yellow-400 text-sm">No API key configured</span>
              )}
            </div>
          </div>

          {showApiKeyInput ? (
            <div className="flex gap-2">
              <input
                type="password"
                value={groqApiKeyInput}
                onChange={(e) => setGroqApiKeyInput(e.target.value)}
                placeholder="Enter your Groq API key (gsk_...)"
                className="flex-1 bg-gray-800 text-white rounded px-3 py-2 text-sm border border-gray-600 focus:border-orange-500 focus:outline-none"
              />
              <button
                onClick={saveGroqApiKey}
                className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium text-sm"
              >
                Save
              </button>
              <button
                onClick={() => { setShowApiKeyInput(false); setGroqApiKeyInput(''); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded font-medium text-sm"
              >
                Cancel
              </button>
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={() => setShowApiKeyInput(true)}
                className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white rounded font-medium text-sm"
              >
                {groqApiKeySet ? 'Change API Key' : 'Add API Key'}
              </button>
              {groqApiKeySet && (
                <button
                  onClick={clearGroqApiKey}
                  className="px-4 py-2 bg-red-600/50 hover:bg-red-600 text-white rounded font-medium text-sm"
                >
                  Remove Key
                </button>
              )}
            </div>
          )}

          <p className="text-xs text-gray-400 mt-3">
            Get your free API key from <a href="https://console.groq.com/keys" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">console.groq.com/keys</a>
          </p>
        </div>

        {groqEnabled && (
          <div className="space-y-6">
            {/* PyDODa Stats */}
            {pydodaStats && (
              <div className="p-4 bg-purple-900/50 rounded-lg border border-purple-700">
                <h4 className="text-purple-300 font-medium mb-2">📚 PyDODa Library Stats</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div className="bg-purple-800/50 rounded p-2 text-center">
                    <div className="text-2xl font-bold text-purple-200">{pydodaStats.total_darija_words?.toLocaleString()}</div>
                    <div className="text-xs text-purple-400">Darija Words</div>
                  </div>
                  <div className="bg-purple-800/50 rounded p-2 text-center">
                    <div className="text-2xl font-bold text-purple-200">{pydodaStats.categories?.length}</div>
                    <div className="text-xs text-purple-400">Categories</div>
                  </div>
                  <div className="bg-green-800/50 rounded p-2 text-center">
                    <div className="text-2xl font-bold text-green-200">{savedVocabulary.length}</div>
                    <div className="text-xs text-green-400">Your Vocabulary</div>
                  </div>
                  <div className="bg-cyan-800/50 rounded p-2 text-center">
                    <div className="text-2xl font-bold text-cyan-200">{groqCachedWords.length}</div>
                    <div className="text-xs text-cyan-400">AI Cache</div>
                  </div>
                </div>
              </div>
            )}
            
            {/* Search Box */}
            <div className="bg-gray-800 rounded-lg p-4">
              <label className="text-white font-medium block mb-2">🔍 Search & Generate Word</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={groqSearchWord}
                  onChange={(e) => setGroqSearchWord(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && searchGroqWord()}
                  placeholder="Enter a word (e.g., salam, klb, makla)..."
                  className="flex-1 bg-gray-700 text-white rounded-lg p-3 border border-gray-600 focus:border-cyan-500 focus:outline-none"
                />
                <button
                  onClick={searchGroqWord}
                  disabled={groqSearching || !groqSearchWord.trim()}
                  className="px-6 py-2 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-600 text-white rounded-lg font-medium"
                >
                  {groqSearching ? '⏳ Searching...' : '🔍 Search'}
                </button>
              </div>
            </div>
            
            {/* Search Result */}
            {groqSearchResult && (
              <div className="bg-gradient-to-r from-gray-800 to-gray-700 rounded-lg p-4 border border-gray-600">
                <div className="flex justify-between items-start mb-3">
                  <h4 className="text-white font-bold text-lg">
                    {groqSearchResult.pydoda_found ? '📚' : '🤖'} Result: "{groqSearchResult.word}"
                  </h4>
                  <div className="flex gap-2">
                    <button
                      onClick={() => groqSearchResult.data && startEditingAiEntry(groqSearchResult.word, groqSearchResult.data)}
                      className="px-3 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white rounded text-sm font-medium"
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => saveGroqToVocabulary(groqSearchResult.word)}
                      className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
                    >
                      💾 Save to Vocabulary
                    </button>
                  </div>
                </div>
                {groqSearchResult.pydoda_found && (
                  <div className="mb-2 text-xs text-purple-400 bg-purple-900/30 px-2 py-1 rounded inline-block">
                    ✨ Found in PyDODa library - AI enhanced
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-400">Darija:</span>
                    <span className="text-white ml-2">{groqSearchResult.data?.darija}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">English:</span>
                    <span className="text-white ml-2">{groqSearchResult.data?.english}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Arabic:</span>
                    <span className="text-white ml-2">{groqSearchResult.data?.arabic}</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Category:</span>
                    <span className="text-white ml-2">{groqSearchResult.data?.category}</span>
                  </div>
                </div>
              </div>
            )}
            
            {/* AI Cache Section */}
            <div className="bg-gray-800 rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium">📦 AI Cache ({groqCachedWords.length} words)</h4>
                <div className="flex gap-2">
                  <button 
                    onClick={() => loadGroqCache()}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    🔄 Refresh
                  </button>
                  {groqCachedWords.length > 0 && (
                    <button 
                      onClick={() => setConfirmDeleteAll('cache')}
                      className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                    >
                      🗑️ Delete All Cache
                    </button>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-400 mb-3">
                Words generated by AI. Review, edit, then save to your vocabulary or delete.
              </p>
              <div className="max-h-48 overflow-y-auto bg-gray-900 rounded-lg p-3">
                {groqCachedWords.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-4">No cached words. Search above to generate AI definitions.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {groqCachedWords.map((item, i) => (
                      <div key={i} className={`px-3 py-1.5 rounded text-sm flex items-center gap-2 ${item.approved ? 'bg-green-700' : 'bg-gray-700'} ${item.pydoda_found ? 'border border-purple-500' : ''}`}>
                        <span 
                          className="text-white cursor-pointer hover:text-cyan-300"
                          onClick={() => item.data && startEditingAiEntry(item.word, item.data)}
                        >
                          {item.word}
                        </span>
                        {item.pydoda_found && <span className="text-purple-400" title="From PyDODa">📚</span>}
                        {item.approved && <span className="text-green-300" title="Saved">✓</span>}
                        {!item.approved && (
                          <>
                            <button onClick={() => item.data && startEditingAiEntry(item.word, item.data)} className="text-yellow-400 hover:text-yellow-300" title="Edit">✏️</button>
                            <button onClick={() => saveGroqToVocabulary(item.word)} className="text-green-400 hover:text-green-300" title="Save">💾</button>
                            <button onClick={() => deleteFromGroqCache(item.word)} className="text-red-400 hover:text-red-300" title="Delete">✕</button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Your Vocabulary Section */}
            <div className="bg-gradient-to-r from-green-900 to-emerald-900 border border-green-700 rounded-lg p-4">
              {/* Protection Notice */}
              <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-2 mb-3 flex items-center gap-2">
                <span className="text-yellow-400 text-lg">🔒</span>
                <p className="text-yellow-200 text-xs">
                  <strong>PROTECTED DATA</strong> - This vocabulary is manually curated. Do not delete or modify programmatically.
                </p>
              </div>
              
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium text-lg">✅ Your Vocabulary ({savedVocabulary.length} {savedVocabulary.length === 1 ? 'word' : 'words'})</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddWordModal(true)}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
                  >
                    ➕ Add Word
                  </button>
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                  >
                    📤 Bulk Upload
                  </button>
                  <button 
                    onClick={() => loadSavedVocabulary()}
                    className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>
              
              {/* Selection Actions */}
              {selectedVocabWords.length > 0 && (
                <div className="mb-3 p-2 bg-yellow-900/50 rounded flex items-center justify-between">
                  <span className="text-yellow-200 text-sm">{selectedVocabWords.length} words selected</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedVocabWords([])}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={() => setConfirmDeleteAll('selected')}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                    >
                      🗑️ Delete Selected
                    </button>
                  </div>
                </div>
              )}
              
              <p className="text-xs text-green-300 mb-3">
                Your curated vocabulary. These words are saved permanently in the database.
              </p>
              
              <div className="max-h-64 overflow-y-auto bg-gray-900 rounded-lg">
                {savedVocabulary.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">No saved vocabulary yet. Search and save words above.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800 sticky top-0">
                      <tr>
                        <th className="p-2 text-left text-gray-400 w-8">
                          <input
                            type="checkbox"
                            checked={selectedVocabWords.length === savedVocabulary.length && savedVocabulary.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVocabWords(savedVocabulary.map(w => w.darija));
                              } else {
                                setSelectedVocabWords([]);
                              }
                            }}
                            className="rounded"
                          />
                        </th>
                        <th className="p-2 text-left text-gray-400">Darija</th>
                        <th className="p-2 text-left text-gray-400">English</th>
                        <th className="p-2 text-left text-gray-400">Category</th>
                        <th className="p-2 text-right text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedVocabulary.map((word, i) => (
                        <tr key={i} className="border-t border-gray-700 hover:bg-gray-800">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedVocabWords.includes(word.darija)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedVocabWords([...selectedVocabWords, word.darija]);
                                } else {
                                  setSelectedVocabWords(selectedVocabWords.filter(w => w !== word.darija));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="p-2 text-white font-medium">{word.darija}</td>
                          <td className="p-2 text-gray-300">{word.english}</td>
                          <td className="p-2 text-gray-400 text-xs">{word.category}</td>
                          <td className="p-2 text-right">
                            <button
                              onClick={() => setEditingVocabWord(word)}
                              className="text-yellow-400 hover:text-yellow-300 mr-2"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => setDeleteConfirmWord(word)}
                              className="text-red-400 hover:text-red-300"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Vocabulary Section - ALWAYS VISIBLE regardless of Groq status */}
        {!groqEnabled && (
          <div className="space-y-6">
            {/* Your Vocabulary Section - Always Show */}
            <div className="bg-gradient-to-r from-green-900 to-emerald-900 border border-green-700 rounded-lg p-4">
              {/* Protection Notice */}
              <div className="bg-yellow-900/50 border border-yellow-600 rounded-lg p-2 mb-3 flex items-center gap-2">
                <span className="text-yellow-400 text-lg">🔒</span>
                <p className="text-yellow-200 text-xs">
                  <strong>PROTECTED DATA</strong> - This vocabulary is manually curated. Do not delete or modify programmatically.
                </p>
              </div>

              <div className="flex items-center justify-between mb-3">
                <h4 className="text-white font-medium text-lg">✅ Your Vocabulary ({savedVocabulary.length} {savedVocabulary.length === 1 ? 'word' : 'words'})</h4>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowAddWordModal(true)}
                    className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-medium"
                  >
                    ➕ Add Word
                  </button>
                  <button
                    onClick={() => setShowBulkUploadModal(true)}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-medium"
                  >
                    📤 Bulk Upload
                  </button>
                  <button
                    onClick={() => loadSavedVocabulary()}
                    className="px-3 py-1.5 bg-gray-600 hover:bg-gray-700 text-white rounded text-sm"
                  >
                    🔄 Refresh
                  </button>
                </div>
              </div>

              {/* Selection Actions */}
              {selectedVocabWords.length > 0 && (
                <div className="mb-3 p-2 bg-yellow-900/50 rounded flex items-center justify-between">
                  <span className="text-yellow-200 text-sm">{selectedVocabWords.length} words selected</span>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedVocabWords([])}
                      className="px-2 py-1 bg-gray-600 hover:bg-gray-700 text-white rounded text-xs"
                    >
                      Clear Selection
                    </button>
                    <button
                      onClick={() => setConfirmDeleteAll('selected')}
                      className="px-2 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-xs"
                    >
                      🗑️ Delete Selected
                    </button>
                  </div>
                </div>
              )}

              <p className="text-xs text-green-300 mb-3">
                Your curated vocabulary. These words are saved permanently in the database.
              </p>

              <div className="max-h-64 overflow-y-auto bg-gray-900 rounded-lg">
                {savedVocabulary.length === 0 ? (
                  <p className="text-gray-500 text-sm text-center py-8">No saved vocabulary yet. Click "Add Word" or "Bulk Upload" to add words.</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead className="bg-gray-800 sticky top-0">
                      <tr>
                        <th className="p-2 text-left text-gray-400 w-8">
                          <input
                            type="checkbox"
                            checked={selectedVocabWords.length === savedVocabulary.length && savedVocabulary.length > 0}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedVocabWords(savedVocabulary.map(w => w.darija));
                              } else {
                                setSelectedVocabWords([]);
                              }
                            }}
                            className="rounded"
                          />
                        </th>
                        <th className="p-2 text-left text-gray-400">Darija</th>
                        <th className="p-2 text-left text-gray-400">English</th>
                        <th className="p-2 text-left text-gray-400">Category</th>
                        <th className="p-2 text-right text-gray-400">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {savedVocabulary.map((word, i) => (
                        <tr key={i} className="border-t border-gray-700 hover:bg-gray-800">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={selectedVocabWords.includes(word.darija)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedVocabWords([...selectedVocabWords, word.darija]);
                                } else {
                                  setSelectedVocabWords(selectedVocabWords.filter(w => w !== word.darija));
                                }
                              }}
                              className="rounded"
                            />
                          </td>
                          <td className="p-2 text-white font-medium">{word.darija}</td>
                          <td className="p-2 text-gray-300">{word.english}</td>
                          <td className="p-2 text-gray-400 text-xs">{word.category}</td>
                          <td className="p-2 text-right">
                            <button
                              onClick={() => setEditingVocabWord(word)}
                              className="text-yellow-400 hover:text-yellow-300 mr-2"
                              title="Edit"
                            >
                              ✏️
                            </button>
                            <button
                              onClick={() => setDeleteConfirmWord(word)}
                              className="text-red-400 hover:text-red-300"
                              title="Delete"
                            >
                              🗑️
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            </div>

            {/* AI Info Box */}
            <div className="bg-blue-900/30 border border-blue-700 rounded-lg p-4 text-center">
              <p className="text-blue-300 text-sm">
                💡 <strong>Tip:</strong> Enable Groq AI above to search and auto-generate word definitions using AI.
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Confirmation Modal for Delete All */}
      {confirmDeleteAll && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-red-600">
            <h3 className="text-xl font-bold text-red-400 mb-4">⚠️ Confirm Delete</h3>
            <p className="text-gray-300 mb-4">
              {confirmDeleteAll === 'vocabulary' && `Are you sure you want to delete ALL ${savedVocabulary.length} words from your vocabulary? This cannot be undone!`}
              {confirmDeleteAll === 'cache' && `Are you sure you want to delete ALL ${groqCachedWords.length} words from AI cache?`}
              {confirmDeleteAll === 'selected' && `Are you sure you want to delete ${selectedVocabWords.length} selected words?`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteAll(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (confirmDeleteAll === 'vocabulary') {
                    await deleteAllVocabulary();
                  } else if (confirmDeleteAll === 'cache') {
                    await deleteAllCache();
                  } else if (confirmDeleteAll === 'selected') {
                    await deleteSelectedVocabulary();
                  }
                  setConfirmDeleteAll(null);
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              >
                🗑️ Yes, Delete
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* General Confirmation Modal (replaces window.confirm) */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-red-600">
            <h3 className="text-xl font-bold text-red-400 mb-4">⚠️ Confirm</h3>
            <p className="text-gray-300 mb-4">{confirmAction.message}</p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmAction.onConfirm}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              >
                Yes, Continue
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Single Word Confirmation Modal */}
      {deleteConfirmWord && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4 border border-red-600">
            <h3 className="text-xl font-bold text-red-400 mb-4">🗑️ Delete Word</h3>
            <div className="mb-4">
              <p className="text-gray-300 mb-3">Are you sure you want to delete this word?</p>
              <div className="bg-gray-900 p-3 rounded-lg border border-gray-700">
                <div className="text-green-400 font-medium text-lg">{deleteConfirmWord.darija}</div>
                <div className="text-white">{deleteConfirmWord.english}</div>
                {deleteConfirmWord.arabic && <div className="text-blue-300 text-right" dir="rtl">{deleteConfirmWord.arabic}</div>}
              </div>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirmWord(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => deleteFromVocabulary(deleteConfirmWord.darija)}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded font-medium"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit AI Cache Entry Modal */}
      {editingAiEntry && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-cyan-600 my-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-cyan-400">✏️ Edit AI Entry: "{editingAiEntry.word}"</h3>
              <button onClick={() => setEditingAiEntry(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>

            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* Darija (Latin) */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Darija (Latin) *</label>
                <input
                  type="text"
                  value={editingAiEntry.data.darija || ''}
                  onChange={(e) => updateEditingField('darija', e.target.value)}
                  placeholder="e.g., salam"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>

              {/* Arabic Script */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Arabic Script</label>
                <input
                  type="text"
                  value={editingAiEntry.data.arabic || ''}
                  onChange={(e) => updateEditingField('arabic', e.target.value)}
                  placeholder="سلام"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600 text-right"
                  dir="rtl"
                />
              </div>

              {/* Pronunciation */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Pronunciation</label>
                <input
                  type="text"
                  value={editingAiEntry.data.pronunciation || ''}
                  onChange={(e) => updateEditingField('pronunciation', e.target.value)}
                  placeholder="e.g., sa-LAM"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>

              {/* Word Type */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Word Type</label>
                <select
                  value={editingAiEntry.data.type || ''}
                  onChange={(e) => updateEditingField('type', e.target.value)}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                >
                  <option value="">Select type...</option>
                  <option value="noun">Noun</option>
                  <option value="verb">Verb</option>
                  <option value="adjective">Adjective</option>
                  <option value="adverb">Adverb</option>
                  <option value="pronoun">Pronoun</option>
                  <option value="preposition">Preposition</option>
                  <option value="conjunction">Conjunction</option>
                  <option value="interjection">Interjection</option>
                  <option value="phrase">Phrase</option>
                  <option value="expression">Expression</option>
                </select>
              </div>

              {/* Meanings */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Meanings (comma separated)</label>
                <input
                  type="text"
                  value={Array.isArray(editingAiEntry.data.meanings) ? editingAiEntry.data.meanings.join(', ') : ''}
                  onChange={(e) => updateEditingField('meanings', e.target.value.split(',').map(m => m.trim()).filter(m => m))}
                  placeholder="e.g., hello, peace, greeting"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>

              {/* Grammar Note */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Grammar Note</label>
                <textarea
                  value={editingAiEntry.data.grammar_note || ''}
                  onChange={(e) => updateEditingField('grammar_note', e.target.value)}
                  placeholder="Grammar rules, usage notes..."
                  rows={2}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>

              {/* Gender Note */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Gender Note</label>
                <input
                  type="text"
                  value={editingAiEntry.data.gender_note || ''}
                  onChange={(e) => updateEditingField('gender_note', e.target.value)}
                  placeholder="e.g., masculine form"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>

              {/* Conjugation */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Conjugation</label>
                <input
                  type="text"
                  value={editingAiEntry.data.conjugation || ''}
                  onChange={(e) => updateEditingField('conjugation', e.target.value)}
                  placeholder="e.g., msalem (past)"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>

              {/* Cultural Note */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Cultural Note</label>
                <textarea
                  value={editingAiEntry.data.cultural_note || ''}
                  onChange={(e) => updateEditingField('cultural_note', e.target.value)}
                  placeholder="Cultural context, when to use this word..."
                  rows={2}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => setEditingAiEntry(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={() => saveGroqToVocabulary(editingAiEntry.word, editingAiEntry.data)}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium"
              >
                💾 Save to Vocabulary
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Vocabulary Word Modal */}
      {editingVocabWord && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-yellow-600 my-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-yellow-400">✏️ Edit Vocabulary Word</h3>
              <button onClick={() => setEditingVocabWord(null)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* English */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">English *</label>
                <input
                  type="text"
                  value={editingVocabWord.english || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, english: e.target.value})}
                  placeholder="e.g., hello"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Darija (Latin) */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Darija (Latin) *</label>
                <input
                  type="text"
                  value={editingVocabWord.darija || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, darija: e.target.value, originalDarija: editingVocabWord.originalDarija || editingVocabWord.darija})}
                  placeholder="e.g., salam"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Arabic Script */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Arabic Script</label>
                <input
                  type="text"
                  value={editingVocabWord.arabic || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, arabic: e.target.value})}
                  placeholder="سلام"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600 text-right"
                  dir="rtl"
                />
              </div>
              
              {/* Pronunciation */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Pronunciation</label>
                <input
                  type="text"
                  value={editingVocabWord.pronunciation || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, pronunciation: e.target.value})}
                  placeholder="e.g., sa-LAM"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Meaning/Definition */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Meaning/Definition</label>
                <input
                  type="text"
                  value={editingVocabWord.meaning || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, meaning: e.target.value})}
                  placeholder="e.g., greeting used to say hello"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Word Type */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Word Type</label>
                <select
                  value={editingVocabWord.type || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, type: e.target.value})}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                >
                  <option value="">Select type...</option>
                  <option value="noun">Noun</option>
                  <option value="verb">Verb</option>
                  <option value="adjective">Adjective</option>
                  <option value="adverb">Adverb</option>
                  <option value="pronoun">Pronoun</option>
                  <option value="preposition">Preposition</option>
                  <option value="conjunction">Conjunction</option>
                  <option value="interjection">Interjection</option>
                  <option value="phrase">Phrase</option>
                  <option value="expression">Expression</option>
                </select>
              </div>
              
              {/* Category */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Category</label>
                <select
                  value={editingVocabWord.category || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, category: e.target.value})}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                >
                  <option value="">Select Category...</option>
                  <option value="Greetings">Greetings</option>
                  <option value="Food">Food</option>
                  <option value="Family">Family</option>
                  <option value="Numbers">Numbers</option>
                  <option value="Colors">Colors</option>
                  <option value="Animals">Animals</option>
                  <option value="Body">Body</option>
                  <option value="Feelings">Feelings</option>
                  <option value="Travel">Travel</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Time">Time</option>
                  <option value="Weather">Weather</option>
                  <option value="Common">Common</option>
                  <option value="Slang">Slang</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Gender Note */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Gender Note</label>
                <input
                  type="text"
                  value={editingVocabWord.genderNote || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, genderNote: e.target.value})}
                  placeholder="e.g., masculine form"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Audio URL */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Audio URL (Dropbox link)</label>
                <input
                  type="text"
                  value={editingVocabWord.audioUrl || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, audioUrl: e.target.value})}
                  placeholder="https://dl.dropboxusercontent.com/..."
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600 text-xs"
                />
              </div>
              
              {/* Grammar Note */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Grammar Note</label>
                <textarea
                  value={editingVocabWord.grammarNote || ''}
                  onChange={(e) => setEditingVocabWord({...editingVocabWord, grammarNote: e.target.value})}
                  placeholder="Grammar rules, usage notes, examples..."
                  rows={2}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => setEditingVocabWord(null)}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={updateVocabWord}
                className="px-6 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded font-medium"
              >
                💾 Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Add Word Modal - Full Featured */}
      {showAddWordModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 overflow-y-auto py-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full mx-4 border border-green-600 my-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-green-400">➕ Add New Word</h3>
              <button onClick={() => setShowAddWordModal(false)} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            
            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              {/* English */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">English *</label>
                <input
                  type="text"
                  value={newWord.english || ''}
                  onChange={(e) => setNewWord({...newWord, english: e.target.value})}
                  placeholder="e.g., hello"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Darija (Latin) */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Darija (Latin) *</label>
                <input
                  type="text"
                  value={newWord.darija || ''}
                  onChange={(e) => setNewWord({...newWord, darija: e.target.value})}
                  placeholder="e.g., salam"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Arabic Script */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Arabic Script</label>
                <input
                  type="text"
                  value={newWord.arabic || ''}
                  onChange={(e) => setNewWord({...newWord, arabic: e.target.value})}
                  placeholder="سلام"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600 text-right"
                  dir="rtl"
                />
              </div>
              
              {/* Pronunciation */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Pronunciation</label>
                <input
                  type="text"
                  value={newWord.pronunciation || ''}
                  onChange={(e) => setNewWord({...newWord, pronunciation: e.target.value})}
                  placeholder="e.g., sa-LAM"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Meaning/Definition */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Meaning/Definition</label>
                <input
                  type="text"
                  value={newWord.meaning || ''}
                  onChange={(e) => setNewWord({...newWord, meaning: e.target.value})}
                  placeholder="e.g., greeting used to say hello"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Word Type */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Word Type</label>
                <select
                  value={newWord.type || ''}
                  onChange={(e) => setNewWord({...newWord, type: e.target.value})}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                >
                  <option value="">Select type...</option>
                  <option value="noun">Noun</option>
                  <option value="verb">Verb</option>
                  <option value="adjective">Adjective</option>
                  <option value="adverb">Adverb</option>
                  <option value="pronoun">Pronoun</option>
                  <option value="preposition">Preposition</option>
                  <option value="conjunction">Conjunction</option>
                  <option value="interjection">Interjection</option>
                  <option value="phrase">Phrase</option>
                  <option value="expression">Expression</option>
                </select>
              </div>
              
              {/* Category */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Category</label>
                <select
                  value={newWord.category || ''}
                  onChange={(e) => setNewWord({...newWord, category: e.target.value})}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                >
                  <option value="">Select Category...</option>
                  <option value="Greetings">Greetings</option>
                  <option value="Food">Food</option>
                  <option value="Family">Family</option>
                  <option value="Numbers">Numbers</option>
                  <option value="Colors">Colors</option>
                  <option value="Animals">Animals</option>
                  <option value="Body">Body</option>
                  <option value="Feelings">Feelings</option>
                  <option value="Travel">Travel</option>
                  <option value="Shopping">Shopping</option>
                  <option value="Time">Time</option>
                  <option value="Weather">Weather</option>
                  <option value="Common">Common</option>
                  <option value="Slang">Slang</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              {/* Gender Note */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Gender Note</label>
                <input
                  type="text"
                  value={newWord.genderNote || ''}
                  onChange={(e) => setNewWord({...newWord, genderNote: e.target.value})}
                  placeholder="e.g., masculine form"
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
              
              {/* Audio URL */}
              <div>
                <label className="block text-sm text-gray-300 mb-1">Audio URL (Dropbox link)</label>
                <input
                  type="text"
                  value={newWord.audioUrl || ''}
                  onChange={(e) => setNewWord({...newWord, audioUrl: e.target.value})}
                  placeholder="https://dl.dropboxusercontent.com/..."
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600 text-xs"
                />
              </div>
              
              {/* Grammar Note */}
              <div className="col-span-2">
                <label className="block text-sm text-gray-300 mb-1">Grammar Note</label>
                <textarea
                  value={newWord.grammarNote || ''}
                  onChange={(e) => setNewWord({...newWord, grammarNote: e.target.value})}
                  placeholder="Grammar rules, usage notes, examples..."
                  rows={2}
                  className="w-full bg-gray-700 text-white rounded-lg p-2.5 border border-gray-600"
                />
              </div>
            </div>
            
            <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-700">
              <button
                onClick={() => {
                  setShowAddWordModal(false);
                  setNewWord({});
                }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  if (!newWord.darija || !newWord.english) {
                    showNotification('English and Darija are required', 'error');
                    return;
                  }
                  try {
                    const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary`, {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        darija: newWord.darija,
                        english: newWord.english,
                        arabic: newWord.arabic || '',
                        meaning: newWord.meaning || '',
                        pronunciation: newWord.pronunciation || '',
                        type: newWord.type || '',
                        category: newWord.category || 'Other',
                        genderNote: newWord.genderNote || '',
                        grammarNote: newWord.grammarNote || '',
                        audioUrl: newWord.audioUrl || ''
                      })
                    });
                    if (response.ok) {
                      showNotification(`Added "${newWord.darija}" to vocabulary`, 'success');
                      setShowAddWordModal(false);
                      setNewWord({});
                      loadSavedVocabulary();
                    }
                  } catch (error) {
                    showNotification('Failed to add word', 'error');
                  }
                }}
                className="px-6 py-2 bg-green-600 hover:bg-green-700 text-white rounded font-medium"
              >
                💾 Save Word
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Bulk Upload Modal - Full Featured */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-5xl w-full border border-blue-600 flex flex-col" style={{maxHeight: '90vh'}}>
            <div className="flex justify-between items-center mb-4 flex-shrink-0">
              <h3 className="text-xl font-bold text-blue-400">📤 Bulk Upload Vocabulary</h3>
              <button onClick={() => { setShowBulkUploadModal(false); setBulkUploadText(''); setBulkUploadPreview([]); }} className="text-gray-400 hover:text-white text-2xl">×</button>
            </div>
            
            {/* Format Instructions */}
            <div className="bg-blue-900/50 border border-blue-700 rounded-lg p-4 mb-4 text-sm flex-shrink-0">
              <p className="text-blue-300 font-medium mb-2">Format: Separate each word with a blank line</p>
              <pre className="text-blue-200 text-xs bg-blue-950 p-2 rounded overflow-x-auto">
{`English: airport
Darija: l-mṭar
Arabic: المطار
Meaning: place where planes take off and land
Pronunciation: L-MTAR
Type: Noun
Category: Travel
Gender Note: masculine
Grammar Note: Used with prepositions like "f" (in)
Examples: Ana ghadi l-mṭar. → I am going to the airport.

English: hello
Darija: salam
Arabic: سلام
Meaning: greeting
Pronunciation: sa-LAM
Type: Noun
Category: Greetings`}
              </pre>
              <button
                onClick={() => {
                  const template = `English: airport
Darija: l-mṭar
Arabic: المطار
Meaning: place where planes take off and land
Pronunciation: L-MTAR
Type: Noun
Category: Travel
Gender Note: masculine
Grammar Note: Used with prepositions like "f" (in) or "mn" (from).
Examples: Ana ghadi l-mṭar. → I am going to the airport.

English: hello
Darija: salam
Arabic: سلام
Meaning: greeting
Pronunciation: sa-LAM
Type: Noun
Category: Greetings
Gender Note: 
Grammar Note: very common greeting
Examples: Salam, kif dayr? → Hello, how are you?

English: thank you
Darija: shukran
Arabic: شكرا
Meaning: expression of gratitude
Pronunciation: SHUK-ran
Type: Expression
Category: Common
Gender Note: 
Grammar Note: formal and informal
Examples: Shukran bzaaf! → Thank you very much!`;
                  const blob = new Blob([template], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'vocabulary_template.txt';
                  a.click();
                }}
                className="mt-2 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded text-xs flex items-center gap-1"
              >
                ⬇️ Download Template
              </button>
            </div>
            
            {/* Main Content - Two Columns */}
            <div className="flex gap-4 flex-1 overflow-hidden">
              {/* Left - Input */}
              <div className="flex-1 flex flex-col" style={{minWidth: '55%'}}>
                <label className="block text-sm text-gray-300 mb-2">Paste your TXT content here:</label>
                <textarea
                  className="flex-1 bg-gray-900 text-white rounded-lg p-4 border border-gray-600 font-mono text-sm resize-none overflow-auto"
                  style={{minHeight: '250px'}}
                  value={bulkUploadText}
                  placeholder={`English: airport
Darija: l-mṭar
Arabic: المطار
Meaning: place where planes take off and land
Pronunciation: L-MTAR
Type: Noun
Category: Travel
Gender Note: masculine
Grammar Note: Used with prepositions
Examples: Ana ghadi l-mṭar.

English: hello
Darija: salam
...`}
                  onChange={(e) => {
                    // Parse and update preview - format with blank lines between fields
                    const text = e.target.value;
                    setBulkUploadText(text);
                    
                    // Split entire text into lines, removing empty lines for parsing
                    const allLines = text.split('\n');
                    const words = [];
                    let currentWord = {};
                    let currentKey = '';
                    
                    allLines.forEach((line, idx) => {
                      const trimmedLine = line.trim();
                      
                      // Check if this line starts a new field (has colon within first 20 chars)
                      const colonIndex = trimmedLine.indexOf(':');
                      if (colonIndex > 0 && colonIndex < 20) {
                        const key = trimmedLine.substring(0, colonIndex).trim();
                        const value = trimmedLine.substring(colonIndex + 1).trim();
                        const normalizedKey = key.toLowerCase()
                          .replace(/[()]/g, '')
                          .replace('darija latin', 'darija')
                          .replace('arabic script', 'arabic')
                          .replace('word type', 'type')
                          .replace('gender note', 'gendernote')
                          .replace('grammar note', 'grammarnote');
                        
                        // If we hit a new "english" and already have data, save previous word
                        if (normalizedKey === 'english' && (currentWord.english || currentWord.darija)) {
                          words.push(currentWord);
                          currentWord = {};
                        }
                        
                        currentWord[normalizedKey] = value;
                        currentKey = normalizedKey;
                      } else if (trimmedLine && currentKey) {
                        // Continuation of previous field (multi-line content like examples or grammar notes)
                        currentWord[currentKey] = (currentWord[currentKey] || '') + ' ' + trimmedLine;
                      }
                    });
                    
                    // Don't forget the last word
                    if (currentWord.english || currentWord.darija) {
                      words.push(currentWord);
                    }
                    
                    setBulkUploadPreview(words);
                  }}
                />
              </div>
              
              {/* Right - Preview */}
              <div className="flex-1 flex flex-col">
                <label className="block text-sm text-gray-300 mb-2">Preview ({bulkUploadPreview.length} words found):</label>
                <div className="flex-1 bg-gray-900 rounded-lg p-3 border border-gray-600 overflow-y-auto text-xs">
                  {bulkUploadPreview.length === 0 ? (
                    <p className="text-gray-400">Paste content on the left to see preview</p>
                  ) : (
                    <div className="space-y-2">
                      {bulkUploadPreview.map((word, idx) => (
                        <div key={idx} className="bg-gray-800 p-2 rounded border border-gray-700">
                          <div className="flex justify-between items-start">
                            <span className="text-green-400 font-medium">{word.darija || '?'}</span>
                            <span className="text-gray-500 text-xs">#{idx + 1}</span>
                          </div>
                          <div className="text-white">{word.english || '?'}</div>
                          {word.arabic && <div className="text-blue-300 text-right" dir="rtl">{word.arabic}</div>}
                          {word.meaning && <div className="text-gray-400 text-xs mt-1">{word.meaning}</div>}
                          <div className="flex gap-1 mt-1 flex-wrap">
                            {word.category && <span className="text-xs bg-purple-900 px-1 rounded text-purple-300">{word.category}</span>}
                            {word.type && <span className="text-xs bg-blue-900 px-1 rounded text-blue-300">{word.type}</span>}
                          </div>
                          {word.examples && <div className="text-yellow-400 text-xs mt-1 italic">📝 {word.examples}</div>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Footer Buttons - Always visible */}
            <div className="flex gap-3 justify-end mt-4 pt-4 border-t border-gray-700 flex-shrink-0 bg-gray-800">
              <button
                onClick={() => { setShowBulkUploadModal(false); setBulkUploadText(''); setBulkUploadPreview([]); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded"
              >
                Cancel
              </button>
              <button
                disabled={bulkUploadPreview.length === 0}
                onClick={async () => {
                  if (bulkUploadPreview.length === 0) {
                    showNotification('No words to upload', 'error');
                    return;
                  }
                  
                  let successCount = 0;
                  for (const word of bulkUploadPreview) {
                    try {
                      const response = await fetch(`${API_URL}/api/conversation-challenge/vocabulary`, {
                        method: 'POST',
                        headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify({
                          darija: word.darija || '',
                          english: word.english || '',
                          arabic: word.arabic || '',
                          meaning: word.meaning || '',
                          pronunciation: word.pronunciation || '',
                          type: word.type || '',
                          category: word.category || 'Other',
                          genderNote: word.gendernote || word['gender note'] || '',
                          grammarNote: word.grammarnote || word['grammar note'] || '',
                          examples: word.examples || '',
                          culturalNote: word['cultural note'] || ''
                        })
                      });
                      if (response.ok) successCount++;
                    } catch (e) {}
                  }
                  
                  showNotification(`Uploaded ${successCount} of ${bulkUploadPreview.length} words`, 'success');
                  setShowBulkUploadModal(false);
                  setBulkUploadText('');
                  setBulkUploadPreview([]);
                  loadSavedVocabulary();
                }}
                className={`px-6 py-2 text-white rounded font-medium text-lg ${bulkUploadPreview.length > 0 ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-500 cursor-not-allowed'}`}
              >
                Import {bulkUploadPreview.length} Words
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  
  // Render settings with Game Visibility Controls
  // ============================================
  // TRAIN BOT — Teach by chatting
  // ============================================
  const renderTrainBot = () => (
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col">
      {!trainingMode ? (
        // LIST VIEW — show existing trained conversations
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-white">🎓 Train Bot</h3>
            <div className="flex gap-2">
              <button
                onClick={() => { /* go back to main editor tabs */ setActiveTab && setActiveTab('flows'); }}
                className="px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded-lg text-sm font-medium"
              >
                ← Back
              </button>
              <button
                onClick={startTraining}
                className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium"
              >
                + New Training
              </button>
            </div>
          </div>
          <p className="text-gray-400 text-sm mb-4">Teach the bot conversations by chatting with it. No complex flow editor needed.</p>

          {trainedConversations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <div className="text-4xl mb-2">🎓</div>
              <p>No trained conversations yet.</p>
              <p className="text-sm mt-1">Click "+ New Training" to teach the bot a conversation.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {trainedConversations.map(conv => (
                <div key={conv._id} className="bg-gray-800 border border-gray-700 rounded-lg p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="text-white font-medium">{conv.title}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-gray-400 text-xs">{conv.steps?.length || 0} steps</span>
                        {conv.bot_id && conv.bot_id !== 'default' && (
                          <span className="text-xs bg-purple-600/30 text-purple-300 px-1.5 py-0.5 rounded">
                            {(Array.isArray(bots) ? bots : []).find(b => b.id === conv.bot_id)?.icon || '🤖'}{' '}
                            {(Array.isArray(bots) ? bots : []).find(b => b.id === conv.bot_id)?.name || conv.bot_id}
                          </span>
                        )}
                      </div>
                      {conv.description && <p className="text-gray-500 text-xs mt-1">{conv.description}</p>}
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={() => editTrainedConversation(conv)}
                        className="px-2 py-1 rounded text-xs bg-blue-600 hover:bg-blue-500 text-white"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => toggleTrainedActive(conv)}
                        className={`px-2 py-1 rounded text-xs ${conv.is_active ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                      >
                        {conv.is_active ? 'Active' : 'Hidden'}
                      </button>
                      <button onClick={() => deleteTrainedConversation(conv._id)} className="text-red-400 hover:text-red-300 text-sm">
                        🗑️
                      </button>
                    </div>
                  </div>
                  {/* Show steps preview */}
                  {conv.steps?.length > 0 && (
                    <div className="mt-3 space-y-1">
                      {conv.steps.slice(0, 3).map((step, i) => (
                        <div key={i} className="text-xs text-gray-500 truncate">
                          {i + 1}. Bot: "{step.bot_message}" → expects [{step.expected_intent || 'keywords'}]
                        </div>
                      ))}
                      {conv.steps.length > 3 && <div className="text-xs text-gray-600">+{conv.steps.length - 3} more steps</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        // TRAINING VIEW — three-column full page: toolkit left, chat center, node panel right
        <div className="flex gap-0 flex-1 min-h-0">

          {/* ════════ LEFT PANEL: TOOLKIT ════════ */}
          <div className="w-56 bg-gray-800 border-r border-gray-700 p-3 overflow-y-auto flex-shrink-0">

            {/* NODES section */}
            <div className="mb-4">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Nodes</h4>
              <div className="space-y-1">
                {[
                  { type: 'message', icon: '💬', label: 'Message + Media', color: 'bg-blue-600' },
                  { type: 'response', icon: '❓', label: 'Response', color: 'bg-amber-600' },
                  { type: 'branch_exchange', icon: '🔀', label: 'Branch', color: 'bg-cyan-600' },
                  { type: 'achievement', icon: '🏆', label: 'Achievement', color: 'bg-pink-600' },
                  { type: 'audio', icon: '🔊', label: 'Audio', color: 'bg-teal-600' },
                  { type: 'vocabulary', icon: '📚', label: 'Vocabulary', color: 'bg-indigo-600' },
                  { type: 'tip', icon: '💡', label: 'Tip / Info', color: 'bg-purple-600' },
                  { type: 'quiz', icon: '🎯', label: 'Challenge', color: 'bg-red-600' },
                  { type: 'delay', icon: '⏱️', label: 'Delay', color: 'bg-gray-600' },
                  { type: 'end', icon: '🔚', label: 'End', color: 'bg-rose-600' }
                ].map(node => {
                  const canClick = trainingPhase !== 'topic';
                  return (
                    <button
                      key={node.type}
                      onClick={() => handleNodeClick(node.type)}
                      disabled={!canClick}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-xs text-left transition-all ${
                        canClick
                          ? `${node.color} bg-opacity-20 hover:bg-opacity-40 text-white cursor-pointer border border-transparent hover:border-white/20`
                          : 'bg-gray-700/30 text-gray-500 cursor-not-allowed'
                      }${nodeEditForm?.type === node.type ? ' ring-2 ring-white' : ''}`}
                    >
                      <span>{node.icon}</span>
                      <span>{node.label}</span>
                    </button>
                  );
                })}
              </div>
              {trainingPhase !== 'topic' && (
                <p className="text-[10px] text-green-400/70 mt-1">Click to add a node</p>
              )}
            </div>

            {/* INTENTS section */}
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">
                Intents
                {trainingPhase !== 'topic' && (
                  <span className="text-yellow-400 ml-1">← click</span>
                )}
              </h4>
              <div className="space-y-1">
                {intents.filter(i => i.is_active !== false).map(intent => {
                  const canClick = trainingPhase !== 'topic';
                  return (
                    <div key={intent.id || intent.name} className="group">
                      <button
                        onClick={() => handleIntentClick(intent)}
                        disabled={!canClick}
                        className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-xs text-left transition-all ${
                          canClick
                            ? 'bg-yellow-600/20 hover:bg-yellow-600/40 text-white cursor-pointer border border-yellow-600/30'
                            : 'bg-gray-700/30 text-gray-400 cursor-default'
                        }`}
                      >
                        <span className="truncate">{intent.display_name || intent.name}</span>
                        <span className="text-[10px] text-gray-500 ml-1">{intent.phrases?.length || 0}</span>
                      </button>
                      <div className="hidden group-hover:block px-2 py-1 text-[10px] text-gray-500">
                        {(intent.phrases || []).slice(0, 3).join(', ')}
                        {(intent.phrases?.length || 0) > 3 && '...'}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* HOW IT WORKS guide */}
            <div className="mt-4 border-t border-gray-700 pt-3">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">How It Works</h4>
              <div className="space-y-2 text-[10px] text-gray-500 leading-relaxed">
                <div className="flex gap-1.5">
                  <span className="text-green-400 font-bold shrink-0">1.</span>
                  <span>Set a <span className="text-white">topic</span> (e.g. "Ordering at a café")</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-green-400 font-bold shrink-0">2.</span>
                  <span>Type what the <span className="text-green-300">bot says</span> in Darija</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-green-400 font-bold shrink-0">3.</span>
                  <span>Type what the <span className="text-blue-300">user should say</span> + pick an intent from the left</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-green-400 font-bold shrink-0">4.</span>
                  <span>Type the <span className="text-green-300">bot's reply</span> when user answers correctly</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-green-400 font-bold shrink-0">5.</span>
                  <span>Repeat steps 2-4 or add <span className="text-purple-300">nodes</span> from above</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="text-green-400 font-bold shrink-0">6.</span>
                  <span>Click <span className="text-yellow-300">Save & Finish</span> when done</span>
                </div>
              </div>
              <div className="mt-2 p-2 bg-gray-700/50 rounded text-[10px] text-gray-400">
                <span className="text-yellow-400">Tip:</span> Click nodes on the left to add Message, Audio, Vocabulary, etc. between exchanges.
              </div>
            </div>
          </div>

          {/* ════════ CENTER: TRAINING CHAT ════════ */}
          <div className="flex-1 flex flex-col p-4 min-w-0">
            {/* Header bar */}
            <div className="flex items-center justify-between mb-3 flex-shrink-0 bg-gray-800 rounded-lg px-4 py-2">
              <div className="flex items-center gap-3">
                <h3 className="text-lg font-bold text-green-400">{editingTrainedConv ? '✏️ Editing' : '🎓 Training'}</h3>
                {currentTrainingData.title && (
                  editingTitle ? (
                    <input
                      type="text"
                      value={currentTrainingData.title}
                      onChange={e => setCurrentTrainingData(prev => ({ ...prev, title: e.target.value }))}
                      onBlur={() => setEditingTitle(false)}
                      onKeyDown={e => { if (e.key === 'Enter') setEditingTitle(false); if (e.key === 'Escape') setEditingTitle(false); }}
                      className="text-sm text-white bg-gray-600 border border-blue-500 px-2 py-0.5 rounded outline-none w-48"
                      autoFocus
                    />
                  ) : (
                    <span
                      className="text-sm text-white bg-gray-700 px-2 py-0.5 rounded cursor-pointer hover:bg-gray-600 transition-colors"
                      onClick={() => setEditingTitle(true)}
                      title="Click to edit title"
                    >
                      {currentTrainingData.title} ✏️
                    </span>
                  )
                )}
                <span className={`text-xs px-2 py-0.5 rounded ${trainingSteps.length > 0 ? 'text-gray-300 bg-gray-700' : 'text-gray-500 bg-gray-800'}`}>
                  {trainingSteps.length > 0
                    ? `${trainingSteps.length} step${trainingSteps.length !== 1 ? 's' : ''}`
                    : 'No steps yet'}
                </span>
                <select
                  value={trainingBotId}
                  onChange={e => setTrainingBotId(e.target.value)}
                  className="bg-gray-700 text-white text-xs rounded px-2 py-1 border border-gray-600"
                >
                  <option value="">-- Assign Bot --</option>
                  {(Array.isArray(bots) ? bots : []).map(bot => (
                    <option key={bot.id} value={bot.id}>{bot.icon || '🤖'} {bot.name}</option>
                  ))}
                </select>
                <label className="flex items-center gap-1.5 text-xs text-gray-300 cursor-pointer hover:text-white transition-colors" title="When checked, player chooses ♀/♂ before starting. When unchecked (or auto), gender picker is shown only if male versions exist.">
                  <input
                    type="checkbox"
                    checked={trainingGenderMode === 'ask'}
                    onChange={e => setTrainingGenderMode(e.target.checked ? 'ask' : 'auto')}
                    className="w-3.5 h-3.5 rounded border-gray-500 bg-gray-700 text-blue-500 focus:ring-blue-500 cursor-pointer"
                  />
                  ♀♂ Ask gender
                </label>
              </div>
              <div className="flex gap-2 items-center">
                {trainingSteps.length > 0 && (
                  <button onClick={() => setShowStepsList(prev => !prev)} className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs">
                    {showStepsList ? 'Hide Steps' : `View Steps (${trainingSteps.length})`}
                  </button>
                )}
                <button
                  onClick={resetAllActions}
                  disabled={trainingPhaseHistory.length === 0 && trainingSteps.length === 0}
                  className={`px-2 py-1 rounded text-xs ${
                    (trainingPhaseHistory.length > 0 || trainingSteps.length > 0)
                      ? 'bg-red-600 hover:bg-red-500 text-white'
                      : 'bg-gray-700 text-gray-500 cursor-not-allowed'
                  }`}
                  title="Reset all actions back to start"
                >
                  ↩️ Reset All
                </button>
                <button
                  onClick={() => { setTrainingMode(false); setTrainingChat([]); setTrainingSteps([]); setNodeEditForm(null); setEditingTrainedConv(null); setEditingStepIndex(null); }}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm"
                  title="Back to training list"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { setTrainingMode(false); setTrainingChat([]); setTrainingSteps([]); setNodeEditForm(null); setEditingTrainedConv(null); setEditingStepIndex(null); setActiveTab('flows'); }}
                  className="px-3 py-1 bg-red-600 hover:bg-red-500 text-white rounded text-sm"
                  title="Exit training and go back to dashboard"
                >
                  Exit
                </button>
                <button
                  onClick={finishTraining}
                  disabled={trainingSteps.length === 0 && !nodeEditForm && !currentTrainingData.bot_message}
                  className={`px-3 py-1 rounded text-sm font-medium ${
                    (trainingSteps.length > 0 || nodeEditForm || currentTrainingData.bot_message) ? 'bg-green-600 hover:bg-green-500 text-white' : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                  }`}
                  title={trainingSteps.length === 0 && !nodeEditForm && !currentTrainingData.bot_message
                    ? 'Complete at least one exchange: bot message → user reply → bot response'
                    : (currentTrainingData.bot_message && trainingSteps.length === 0 ? 'Will save current partial exchange' : 'Save conversation to database')}
                >
                  {editingTrainedConv ? 'Update & Finish' : 'Save & Finish'}
                </button>
              </div>
            </div>

            {/* Steps list (collapsible) */}
            {showStepsList && trainingSteps.length > 0 && (
              <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 mb-3 max-h-48 overflow-y-auto flex-shrink-0">
                <h4 className="text-xs font-bold text-gray-400 uppercase mb-2">Saved Steps ({trainingSteps.length})</h4>
                <div className="space-y-1">
                  {trainingSteps.map((step, idx) => (
                    <div key={idx} className={`flex items-center justify-between px-2 py-1.5 rounded text-xs ${
                      editingStepIndex === idx ? 'bg-blue-900 border border-blue-600' : 'bg-gray-700/50 hover:bg-gray-700'
                    }`}>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-gray-500 w-4">{idx + 1}.</span>
                        <span className="text-gray-400">{step.type || 'exchange'}{step.branches ? ` 🔀${step.branches.length}` : ''}</span>
                        <span className="text-white truncate">
                          {step.bot_message || step.text || step.name || step.question || step.darija || step.message || '...'}
                        </span>
                      </div>
                      <div className="flex gap-1 ml-2 flex-shrink-0">
                        <button onClick={() => startEditStep(idx)} className="text-blue-400 hover:text-blue-300 px-1" title="Edit">✏️</button>
                        <button onClick={() => deleteStep(idx)} className="text-red-400 hover:text-red-300 px-1" title="Delete">✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Chat messages */}
            <div className="flex-1 bg-gray-900/50 rounded-xl border border-gray-700 p-4 mb-3 overflow-y-auto space-y-3">
              {trainingChat.map((msg, i) => (
                <div key={i} className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-sm px-3 py-2 rounded-xl text-sm ${
                    msg.sender === 'admin'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : msg.text?.startsWith('✅') ? 'bg-green-800 text-green-200 rounded-bl-sm'
                      : 'bg-gray-700 text-gray-100 rounded-bl-sm'
                  }`}>
                    {msg.text}
                  </div>
                </div>
              ))}

              {/* "Keep Going" / "End & Save" buttons */}
              {trainingPhase === 'choose_next' && !nodeEditForm && (
                <div className="flex gap-2 justify-center py-2">
                  <button
                    onClick={() => { setTrainingPhase('bot_message'); addTrainingChat('bot', '🤖 BOT\'s turn — type what the bot says next, click a node, or click an intent.'); }}
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium"
                  >
                    ▶️ Keep Going
                  </button>
                  <button onClick={finishTraining} className="px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded-lg text-sm font-medium">
                    🏁 End & Save
                  </button>
                </div>
              )}

              {/* "Single / Multiple answers" buttons after bot message */}
              {trainingPhase === 'choose_response_mode' && !nodeEditForm && (
                <div className="flex flex-col items-center gap-2 py-2">
                  <p className="text-xs text-gray-400">How should the user respond?</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        pushPhaseHistory();
                        addTrainingChat('bot', '👤 Type what the USER should say (single answer):');
                        setTrainingPhase('user_input');
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-sm font-medium"
                    >
                      💬 Single Answer
                    </button>
                    <button
                      onClick={() => {
                        pushPhaseHistory();
                        addTrainingChat('bot', '🔀 Opening branch editor — add multiple user response paths in the right panel.');
                        setNodeEditForm({
                          type: 'branch_exchange',
                          bot_message: currentTrainingData.bot_message || '',
                          bot_message_male: currentTrainingData.bot_message_male || null,
                          branches: [
                            { id: generateId(), keywords: '', intent: '', response: '', response_male: '', hint: '', points: 10 },
                            { id: generateId(), keywords: '', intent: '', response: '', response_male: '', hint: '', points: 10 }
                          ]
                        });
                        setTrainingPhase('bot_message'); // allow continuing after saving branch
                      }}
                      className="px-4 py-2 bg-cyan-600 hover:bg-cyan-500 text-white rounded-lg text-sm font-medium"
                    >
                      🔀 Multiple Answers (Branch)
                    </button>
                  </div>
                  <p className="text-[10px] text-gray-500">Or just type the user's answer below</p>
                </div>
              )}
            </div>

            {/* Input */}
            {trainingPhase !== 'choose_next' && (
              <div className="flex gap-2 flex-shrink-0">
                <input
                  type="text"
                  value={trainingInput}
                  onChange={(e) => setTrainingInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      if (trainingInput.trim().toLowerCase() === 'done' && trainingSteps.length > 0) finishTraining();
                      else handleTrainingInput();
                    }
                  }}
                  placeholder={
                    trainingPhase === 'topic' ? 'Enter conversation topic...' :
                    trainingPhase === 'bot_message' ? '🤖 Type what the BOT says... (or click node/intent)' :
                    trainingPhase === 'choose_response_mode' ? '👤 Type user answer (or click Single/Branch above)...' :
                    trainingPhase === 'user_input' ? '👤 Type what the USER says... (or click intent)' :
                    trainingPhase === 'bot_response' ? '🤖 Type the BOT reply when user is correct...' :
                    'Type here...'
                  }
                  className="flex-1 px-4 py-3 bg-gray-700 border border-gray-600 rounded-xl text-white text-sm placeholder-gray-400"
                  autoFocus
                />
                <button
                  onClick={() => {
                    if (trainingInput.trim().toLowerCase() === 'done' && trainingSteps.length > 0) finishTraining();
                    else handleTrainingInput();
                  }}
                  className="px-4 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl text-sm"
                >
                  Send
                </button>
              </div>
            )}

            {/* Phase indicator */}
            <div className={`mt-2 text-xs text-center flex-shrink-0 py-1 px-3 rounded-full inline-block mx-auto ${
              trainingPhase === 'topic' ? 'bg-gray-700 text-gray-300' :
              trainingPhase === 'bot_message' || trainingPhase === 'bot_response' ? 'bg-green-900 text-green-300' :
              trainingPhase === 'user_input' ? 'bg-blue-900 text-blue-300' :
              trainingPhase === 'choose_response_mode' ? 'bg-cyan-900 text-cyan-300' :
              trainingPhase === 'choose_next' ? 'bg-yellow-900 text-yellow-300' :
              'bg-gray-700 text-gray-300'
            }`}>
              {trainingPhase === 'topic' ? '📝 Set topic' :
               trainingPhase === 'bot_message' ? '🤖 BOT says...' :
               trainingPhase === 'choose_response_mode' ? '🔀 Choose: single or branching response' :
               trainingPhase === 'user_input' ? '👤 USER says...' :
               trainingPhase === 'bot_response' ? '🤖 BOT replies when correct...' :
               trainingPhase === 'choose_next' ? '✅ Step saved — keep going or end?' :
               trainingPhase}
            </div>
          </div>

          {/* ════════ RIGHT PANEL: NODE PROPERTIES (exact copy of flow editor) ════════ */}
          {nodeEditForm && (
            <div className="w-96 border-l bg-gray-800 border-gray-700 p-4 overflow-y-auto flex-shrink-0">
              <div className="flex items-center gap-2 mb-4 pb-2 border-b border-gray-600">
                <span className="text-2xl">
                  {nodeEditForm.type === 'exchange' && '🔄'}
                  {nodeEditForm.type === 'branch_exchange' && '🔀'}
                  {nodeEditForm.type === 'message' && '💬'}
                  {nodeEditForm.type === 'response' && '❓'}
                  {nodeEditForm.type === 'achievement' && '🏆'}
                  {nodeEditForm.type === 'audio' && '🔊'}
                  {nodeEditForm.type === 'vocabulary' && '📚'}
                  {nodeEditForm.type === 'tip' && '💡'}
                  {nodeEditForm.type === 'quiz' && '🎯'}
                  {nodeEditForm.type === 'delay' && '⏱️'}
                  {nodeEditForm.type === 'end' && '🔚'}
                </span>
                <h3 className="text-lg font-bold text-white flex-1">
                  {editingStepIndex !== null ? '✏️ Edit ' : ''}
                  {nodeEditForm.type === 'exchange' && 'Exchange'}
                  {nodeEditForm.type === 'branch_exchange' && 'Branching Exchange'}
                  {nodeEditForm.type === 'message' && 'Message'}
                  {nodeEditForm.type === 'response' && 'Response'}
                  {nodeEditForm.type === 'achievement' && 'Achievement'}
                  {nodeEditForm.type === 'audio' && 'Audio'}
                  {nodeEditForm.type === 'vocabulary' && 'Vocabulary'}
                  {nodeEditForm.type === 'tip' && 'Tip'}
                  {nodeEditForm.type === 'quiz' && 'Challenge'}
                  {nodeEditForm.type === 'delay' && 'Delay'}
                  {nodeEditForm.type === 'end' && 'End'}
                </h3>
                <button onClick={() => { setNodeEditForm(null); setEditingStepIndex(null); }} className="text-gray-400 hover:text-white text-lg">✕</button>
              </div>

              <div className="space-y-4">

                {/* ══════ EXCHANGE (edit chat exchange steps) ══════ */}
                {nodeEditForm.type === 'exchange' && (<>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-pink-400 block mb-2">🤖 Bot Message (Latin)</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.bot_message || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, bot_message: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          placeholder="What the bot says to a female user..." autoFocus
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.bot_message_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, bot_message_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          placeholder="What the bot says to a male user..."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-pink-400 block mb-2">🤖 Bot Message (Arabic Script)</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.bot_message_arabic || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, bot_message_arabic: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30 text-right" rows={2} dir="rtl"
                          placeholder="النسخة العربية..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.bot_message_arabic_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, bot_message_arabic_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30 text-right" rows={2} dir="rtl"
                          placeholder="النسخة العربية للذكور..."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-600 pt-3 mt-1">
                    <label className="text-sm text-blue-400 block mb-1">Expected Intent</label>
                    <input type="text"
                      value={nodeEditForm.expected_intent || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, expected_intent: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="e.g. greeting, order_food, ask_price"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-blue-400 block mb-1">Expected Keywords <span className="text-gray-500">(comma-separated)</span></label>
                    <input type="text"
                      value={(nodeEditForm.expected_keywords || []).join(', ')}
                      onChange={e => setNodeEditForm(p => ({ ...p, expected_keywords: e.target.value.split(',').map(k => k.trim()).filter(Boolean) }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="salam, labas, ahlan"
                    />
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600 mt-1">
                    <label className="text-sm font-semibold text-green-400 block mb-2">✅ Correct Response — Latin</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.correct_response || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, correct_response: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          placeholder="Bot's reply to female user when correct..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.correct_response_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, correct_response_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          placeholder="Bot's reply to male user when correct..."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-green-400 block mb-2">✅ Correct Response — Arabic Script</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.correct_response_arabic || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, correct_response_arabic: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30 text-right" rows={2} dir="rtl"
                          placeholder="الرد بالعربية..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.correct_response_arabic_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, correct_response_arabic_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30 text-right" rows={2} dir="rtl"
                          placeholder="الرد بالعربية للذكور..."
                        />
                      </div>
                    </div>
                  </div>
                  <div className="border-t border-gray-600 pt-3 mt-1">
                    <label className="text-sm text-yellow-400 block mb-1">Hint</label>
                    <input type="text"
                      value={nodeEditForm.hint || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, hint: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="Hint shown when user is stuck..."
                    />
                  </div>
                  <div className="border-t border-gray-600 pt-3 mt-1">
                    <label className="text-sm text-emerald-400 block mb-1">Success Reaction (optional)</label>
                    <p className="text-[10px] text-gray-400 mb-1">Shown when user answers correctly. Leave empty for no system message — just the bot reply.</p>
                    <input type="text"
                      value={nodeEditForm.success_message || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, success_message: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-emerald-600/30"
                      placeholder="e.g., Bravo! 🎉 or Zwiin, 3raft! or leave empty"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-yellow-400 block mb-1">Points</label>
                    <input type="number"
                      value={nodeEditForm.points || 10}
                      onChange={e => setNodeEditForm(p => ({ ...p, points: parseInt(e.target.value) || 0 }))}
                      className="w-24 bg-gray-700 text-white rounded p-2 text-sm"
                    />
                  </div>
                  {/* ── Media & Audio (collapsible) ── */}
                  <div className="border-t border-gray-600 pt-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setNodeEditForm(p => ({ ...p, _showMedia: !p._showMedia }))}
                      className="flex items-center gap-2 text-sm font-semibold text-teal-400 hover:text-teal-300 transition-colors w-full"
                    >
                      <span>{nodeEditForm._showMedia ? '▼' : '▶'}</span>
                      Media & Audio (optional)
                    </button>
                    {nodeEditForm._showMedia && (
                      <div className="mt-3 space-y-3">
                        {/* Bot Message Audio ♀ */}
                        <div>
                          <label className="text-xs text-pink-300 block mb-1">🔊 Bot Message Audio (♀ Female)</label>
                          <div className="flex gap-2">
                            <input type="text" value={nodeEditForm.audioUrl || ''}
                              onChange={e => setNodeEditForm(p => ({ ...p, audioUrl: e.target.value }))}
                              className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="https://dropbox.com/..." />
                            {nodeEditForm.audioUrl && (
                              <button onClick={() => { const a = new Audio(nodeEditForm.audioUrl); a.play().catch(() => {}); }}
                                className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                            )}
                          </div>
                        </div>
                        {/* Bot Message Audio ♂ */}
                        <div>
                          <label className="text-xs text-blue-300 block mb-1">🔊 Bot Message Audio (♂ Male)</label>
                          <div className="flex gap-2">
                            <input type="text" value={nodeEditForm.audioUrl_male || ''}
                              onChange={e => setNodeEditForm(p => ({ ...p, audioUrl_male: e.target.value }))}
                              className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Male audio URL (leave empty if same)" />
                            {nodeEditForm.audioUrl_male && (
                              <button onClick={() => { const a = new Audio(nodeEditForm.audioUrl_male); a.play().catch(() => {}); }}
                                className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                            )}
                          </div>
                        </div>
                        {/* Image URL */}
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Image URL</label>
                          <input type="text" value={nodeEditForm.imageUrl || ''}
                            onChange={e => setNodeEditForm(p => ({ ...p, imageUrl: e.target.value }))}
                            className="w-full bg-gray-700 text-white rounded p-2 text-sm" placeholder="https://dl.dropboxusercontent.com/..." />
                          {nodeEditForm.imageUrl && isValidMediaUrl(nodeEditForm.imageUrl) && (
                            <div className="mt-2 rounded overflow-hidden border border-gray-600">
                              <img src={nodeEditForm.imageUrl} alt="Preview" className="w-full max-h-32 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                        </div>
                        {/* GIF URL */}
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">GIF URL</label>
                          <input type="text" value={nodeEditForm.gifUrl || ''}
                            onChange={e => setNodeEditForm(p => ({ ...p, gifUrl: e.target.value }))}
                            className="w-full bg-gray-700 text-white rounded p-2 text-sm" placeholder="https://media.giphy.com/..." />
                        </div>
                        {/* Correct Response Audio ♀ */}
                        <div>
                          <label className="text-xs text-pink-300 block mb-1">🔊 Correct Response Audio (♀ Female)</label>
                          <div className="flex gap-2">
                            <input type="text" value={nodeEditForm.correctResponseAudioUrl || ''}
                              onChange={e => setNodeEditForm(p => ({ ...p, correctResponseAudioUrl: e.target.value }))}
                              className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Audio played with correct response" />
                            {nodeEditForm.correctResponseAudioUrl && (
                              <button onClick={() => { const a = new Audio(nodeEditForm.correctResponseAudioUrl); a.play().catch(() => {}); }}
                                className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                            )}
                          </div>
                        </div>
                        {/* Correct Response Audio ♂ */}
                        <div>
                          <label className="text-xs text-blue-300 block mb-1">🔊 Correct Response Audio (♂ Male)</label>
                          <div className="flex gap-2">
                            <input type="text" value={nodeEditForm.correctResponseAudioUrl_male || ''}
                              onChange={e => setNodeEditForm(p => ({ ...p, correctResponseAudioUrl_male: e.target.value }))}
                              className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Male correct response audio" />
                            {nodeEditForm.correctResponseAudioUrl_male && (
                              <button onClick={() => { const a = new Audio(nodeEditForm.correctResponseAudioUrl_male); a.play().catch(() => {}); }}
                                className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                            )}
                          </div>
                        </div>
                        {/* Listen Prompt Toggle */}
                        <div className="border-t border-gray-600 pt-3">
                          <label className="flex items-center gap-2 text-sm text-teal-400 cursor-pointer">
                            <input type="checkbox" checked={nodeEditForm.listenPrompt || false}
                              onChange={e => setNodeEditForm(p => ({ ...p, listenPrompt: e.target.checked }))}
                              className="rounded" />
                            Enable "Want to hear?" prompt after correct answer
                          </label>
                          {nodeEditForm.listenPrompt && (
                            <div className="mt-3 space-y-3 pl-2 border-l-2 border-teal-600/40">
                              <div>
                                <label className="text-xs text-gray-400 block mb-1">Prompt Text (Latin)</label>
                                <input type="text" value={nodeEditForm.listenPromptText || ''}
                                  onChange={e => setNodeEditForm(p => ({ ...p, listenPromptText: e.target.value }))}
                                  className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                                  placeholder="Bghiti tsm3 kif kat-tgulha?" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400 block mb-1">Prompt Text (Arabic)</label>
                                <input type="text" value={nodeEditForm.listenPromptText_arabic || ''}
                                  onChange={e => setNodeEditForm(p => ({ ...p, listenPromptText_arabic: e.target.value }))}
                                  className="w-full bg-gray-700 text-white rounded p-2 text-sm text-right" dir="rtl"
                                  placeholder="بغيتي تسمع كيف كتقولها؟" />
                              </div>
                              <div>
                                <label className="text-xs text-pink-300 block mb-1">🔊 Listen Audio (♀ Female)</label>
                                <div className="flex gap-2">
                                  <input type="text" value={nodeEditForm.listenAudioUrl || ''}
                                    onChange={e => setNodeEditForm(p => ({ ...p, listenAudioUrl: e.target.value }))}
                                    className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Audio to play when user clicks Yes" />
                                  {nodeEditForm.listenAudioUrl && (
                                    <button onClick={() => { const a = new Audio(nodeEditForm.listenAudioUrl); a.play().catch(() => {}); }}
                                      className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                                  )}
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-blue-300 block mb-1">🔊 Listen Audio (♂ Male)</label>
                                <div className="flex gap-2">
                                  <input type="text" value={nodeEditForm.listenAudioUrl_male || ''}
                                    onChange={e => setNodeEditForm(p => ({ ...p, listenAudioUrl_male: e.target.value }))}
                                    className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Male listen audio" />
                                  {nodeEditForm.listenAudioUrl_male && (
                                    <button onClick={() => { const a = new Audio(nodeEditForm.listenAudioUrl_male); a.play().catch(() => {}); }}
                                      className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>)}

                {/* ══════ BRANCH EXCHANGE NODE ══════ */}
                {nodeEditForm.type === 'branch_exchange' && (<>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-cyan-600/30">
                    <label className="text-sm font-semibold text-cyan-400 block mb-2">🤖 Bot Message</label>
                    <textarea
                      value={nodeEditForm.bot_message || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, bot_message: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-cyan-600/30" rows={2}
                      placeholder="What the bot says..."
                    />
                    <textarea
                      value={nodeEditForm.bot_message_male || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, bot_message_male: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30 mt-1" rows={1}
                      placeholder="♂ Male version (optional)"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-emerald-400 block mb-1">Success Reaction (optional)</label>
                    <p className="text-[10px] text-gray-400 mb-1">Shown when any branch matches. Leave empty for no system message.</p>
                    <input type="text"
                      value={nodeEditForm.success_message || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, success_message: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-emerald-600/30"
                      placeholder="e.g., Bravo! 🎉 or Zwiin! or leave empty"
                    />
                  </div>
                  <div className="border-t border-gray-600 pt-3">
                    <label className="text-sm font-semibold text-cyan-400 block mb-2">🔀 User Response Branches</label>
                    <p className="text-[10px] text-gray-400 mb-2">Each branch is a different way the user might respond. The bot gives a different reply for each.</p>
                    <div className="space-y-3 max-h-80 overflow-y-auto">
                      {(nodeEditForm.branches || []).map((branch, idx) => (
                        <div key={branch.id || idx} className="bg-gray-700/50 rounded-lg p-3 border border-gray-600 relative">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold text-cyan-300">Branch {idx + 1}</span>
                            {(nodeEditForm.branches || []).length > 1 && (
                              <button
                                onClick={() => {
                                  const b = (nodeEditForm.branches || []).filter((_, i) => i !== idx);
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="text-red-400 hover:text-red-300 text-xs"
                              >✕ Remove</button>
                            )}
                          </div>
                          <div className="space-y-1.5">
                            <div>
                              <label className="text-[10px] text-blue-300">👤 User keywords (comma-separated)</label>
                              <input type="text"
                                value={branch.keywords || ''}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], keywords: e.target.value };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs"
                                placeholder="labas, bikhir, hamdulah"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] text-blue-300">Intent (optional)</label>
                              <select
                                value={branch.intent || ''}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], intent: e.target.value };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs"
                              >
                                <option value="">-- No intent --</option>
                                {intents.filter(i => i.is_active !== false).map(i => (
                                  <option key={i.id || i.name} value={i.id || i.name}>{i.display_name || i.name}</option>
                                ))}
                              </select>
                            </div>
                            <div>
                              <label className="text-[10px] text-pink-300">🤖 Bot reply ♀ for this branch</label>
                              <textarea
                                value={branch.response || ''}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], response: e.target.value };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs border border-pink-600/30" rows={2}
                                placeholder="Bot says when user picks this path..."
                              />
                            </div>
                            <div>
                              <label className="text-[10px] text-blue-300">🤖 Bot reply ♂ Male (optional)</label>
                              <textarea
                                value={branch.response_male || ''}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], response_male: e.target.value };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs border border-blue-600/30" rows={1}
                                placeholder="Male version of bot reply (leave empty if same)..."
                              />
                            </div>
                            <div>
                              <label className="text-[10px] text-yellow-300">Hint</label>
                              <input type="text"
                                value={branch.hint || ''}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], hint: e.target.value };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs"
                                placeholder="Hint for this branch..."
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-[10px] text-gray-400">Points:</label>
                              <input type="number"
                                value={branch.points || 10}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], points: parseInt(e.target.value) || 0 };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-16 bg-gray-600 text-white rounded px-2 py-1 text-xs"
                              />
                            </div>
                            <div>
                              <label className="text-[10px] text-purple-300">Jump to step (optional)</label>
                              <select
                                value={branch.next_step_id || ''}
                                onChange={e => {
                                  const b = [...(nodeEditForm.branches || [])];
                                  b[idx] = { ...b[idx], next_step_id: e.target.value || null };
                                  setNodeEditForm(p => ({ ...p, branches: b }));
                                }}
                                className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs"
                              >
                                <option value="">-- Next step (default) --</option>
                                {trainingSteps.map((s, sIdx) => (
                                  <option key={s.id || sIdx} value={s.id || ''}>
                                    Step {sIdx + 1}: {s.type} — {(s.bot_message || s.text || s.name || s.darija || s.message || '...').substring(0, 35)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        const b = [...(nodeEditForm.branches || []), { id: generateId(), keywords: '', intent: '', response: '', response_male: '', hint: '', points: 10, next_step_id: '' }];
                        setNodeEditForm(p => ({ ...p, branches: b }));
                      }}
                      className="mt-2 px-3 py-1.5 bg-cyan-600 hover:bg-cyan-500 text-white rounded text-xs w-full"
                    >
                      + Add Branch
                    </button>
                  </div>
                  {/* ── Fallback Response (when no branch matches) ── */}
                  <div className="border-t border-gray-600 pt-3 mt-2">
                    <label className="text-sm font-semibold text-orange-400 block mb-1">Fallback (when no branch matches)</label>
                    <p className="text-[10px] text-gray-400 mb-2">Bot says this naturally when user's answer doesn't match any branch. Leave empty for default hint.</p>
                    <div className="space-y-1">
                      <textarea
                        value={nodeEditForm.fallback_response || ''}
                        onChange={e => setNodeEditForm(p => ({ ...p, fallback_response: e.target.value }))}
                        className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-orange-600/30" rows={2}
                        placeholder="e.g., Ma fhemtsh, 3awd 3afak... goli iyeh wla la!"
                      />
                      <textarea
                        value={nodeEditForm.fallback_response_male || ''}
                        onChange={e => setNodeEditForm(p => ({ ...p, fallback_response_male: e.target.value }))}
                        className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={1}
                        placeholder="♂ Male fallback (optional)"
                      />
                    </div>
                  </div>
                  {/* ── Media & Audio (collapsible) ── */}
                  <div className="border-t border-gray-600 pt-3 mt-2">
                    <button type="button"
                      onClick={() => setNodeEditForm(p => ({ ...p, _showMedia: !p._showMedia }))}
                      className="flex items-center gap-2 text-sm font-semibold text-teal-400 hover:text-teal-300 transition-colors w-full">
                      <span>{nodeEditForm._showMedia ? '▼' : '▶'}</span>
                      Media & Audio (optional)
                    </button>
                    {nodeEditForm._showMedia && (
                      <div className="mt-3 space-y-3">
                        <div>
                          <label className="text-xs text-pink-300 block mb-1">🔊 Bot Message Audio (♀ Female)</label>
                          <div className="flex gap-2">
                            <input type="text" value={nodeEditForm.audioUrl || ''}
                              onChange={e => setNodeEditForm(p => ({ ...p, audioUrl: e.target.value }))}
                              className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="https://dropbox.com/..." />
                            {nodeEditForm.audioUrl && (
                              <button onClick={() => { const a = new Audio(nodeEditForm.audioUrl); a.play().catch(() => {}); }}
                                className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-blue-300 block mb-1">🔊 Bot Message Audio (♂ Male)</label>
                          <div className="flex gap-2">
                            <input type="text" value={nodeEditForm.audioUrl_male || ''}
                              onChange={e => setNodeEditForm(p => ({ ...p, audioUrl_male: e.target.value }))}
                              className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Male audio URL" />
                            {nodeEditForm.audioUrl_male && (
                              <button onClick={() => { const a = new Audio(nodeEditForm.audioUrl_male); a.play().catch(() => {}); }}
                                className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                            )}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">Image URL</label>
                          <input type="text" value={nodeEditForm.imageUrl || ''}
                            onChange={e => setNodeEditForm(p => ({ ...p, imageUrl: e.target.value }))}
                            className="w-full bg-gray-700 text-white rounded p-2 text-sm" placeholder="https://dl.dropboxusercontent.com/..." />
                          {nodeEditForm.imageUrl && isValidMediaUrl(nodeEditForm.imageUrl) && (
                            <div className="mt-2 rounded overflow-hidden border border-gray-600">
                              <img src={nodeEditForm.imageUrl} alt="Preview" className="w-full max-h-32 object-cover" onError={(e) => { e.target.style.display = 'none'; }} />
                            </div>
                          )}
                        </div>
                        <div>
                          <label className="text-xs text-gray-400 block mb-1">GIF URL</label>
                          <input type="text" value={nodeEditForm.gifUrl || ''}
                            onChange={e => setNodeEditForm(p => ({ ...p, gifUrl: e.target.value }))}
                            className="w-full bg-gray-700 text-white rounded p-2 text-sm" placeholder="https://media.giphy.com/..." />
                        </div>
                        <div className="border-t border-gray-600 pt-3">
                          <label className="flex items-center gap-2 text-sm text-teal-400 cursor-pointer">
                            <input type="checkbox" checked={nodeEditForm.listenPrompt || false}
                              onChange={e => setNodeEditForm(p => ({ ...p, listenPrompt: e.target.checked }))} className="rounded" />
                            Enable "Want to hear?" prompt
                          </label>
                          {nodeEditForm.listenPrompt && (
                            <div className="mt-3 space-y-3 pl-2 border-l-2 border-teal-600/40">
                              <div>
                                <label className="text-xs text-gray-400 block mb-1">Prompt Text (Latin)</label>
                                <input type="text" value={nodeEditForm.listenPromptText || ''}
                                  onChange={e => setNodeEditForm(p => ({ ...p, listenPromptText: e.target.value }))}
                                  className="w-full bg-gray-700 text-white rounded p-2 text-sm" placeholder="Bghiti tsm3 kif kat-tgulha?" />
                              </div>
                              <div>
                                <label className="text-xs text-gray-400 block mb-1">Prompt Text (Arabic)</label>
                                <input type="text" value={nodeEditForm.listenPromptText_arabic || ''}
                                  onChange={e => setNodeEditForm(p => ({ ...p, listenPromptText_arabic: e.target.value }))}
                                  className="w-full bg-gray-700 text-white rounded p-2 text-sm text-right" dir="rtl" placeholder="بغيتي تسمع كيف كتقولها؟" />
                              </div>
                              <div>
                                <label className="text-xs text-pink-300 block mb-1">🔊 Listen Audio (♀ Female)</label>
                                <div className="flex gap-2">
                                  <input type="text" value={nodeEditForm.listenAudioUrl || ''}
                                    onChange={e => setNodeEditForm(p => ({ ...p, listenAudioUrl: e.target.value }))}
                                    className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Audio when user clicks Yes" />
                                  {nodeEditForm.listenAudioUrl && (
                                    <button onClick={() => { const a = new Audio(nodeEditForm.listenAudioUrl); a.play().catch(() => {}); }}
                                      className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                                  )}
                                </div>
                              </div>
                              <div>
                                <label className="text-xs text-blue-300 block mb-1">🔊 Listen Audio (♂ Male)</label>
                                <div className="flex gap-2">
                                  <input type="text" value={nodeEditForm.listenAudioUrl_male || ''}
                                    onChange={e => setNodeEditForm(p => ({ ...p, listenAudioUrl_male: e.target.value }))}
                                    className="flex-1 bg-gray-700 text-white rounded p-2 text-sm" placeholder="Male listen audio" />
                                  {nodeEditForm.listenAudioUrl_male && (
                                    <button onClick={() => { const a = new Audio(nodeEditForm.listenAudioUrl_male); a.play().catch(() => {}); }}
                                      className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm" title="Preview">🔊</button>
                                  )}
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </>)}

                {/* ══════ RESPONSE NODE ══════ */}
                {nodeEditForm.type === 'response' && (<>
                  <div>
                    <label className="text-sm font-semibold text-amber-400 block mb-1">Prompt</label>
                    <input type="text"
                      value={nodeEditForm.prompt || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, prompt: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="What do you say?" autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Prompt (Arabic)</label>
                    <input type="text"
                      value={nodeEditForm.prompt_arabic || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, prompt_arabic: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm text-right" dir="rtl"
                      placeholder="شنو غادي تقول؟"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-amber-400 block mb-1">Input Type</label>
                    <select
                      value={nodeEditForm.inputType || 'choice'}
                      onChange={e => setNodeEditForm(p => ({ ...p, inputType: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    >
                      <option value="choice">Multiple Choice</option>
                      <option value="text">Text Input</option>
                    </select>
                  </div>
                  {nodeEditForm.inputType !== 'text' && (
                    <div className="border-t border-gray-600 pt-3">
                      <label className="text-sm font-semibold text-amber-400 block mb-2">Options</label>
                      <div className="space-y-2 max-h-60 overflow-y-auto">
                        {(nodeEditForm.options || []).map((opt, idx) => (
                          <div key={opt.id || idx} className="bg-gray-700/50 rounded p-2 border border-gray-600">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs text-gray-400">Option {idx + 1}</span>
                              <div className="flex items-center gap-2">
                                <label className="flex items-center gap-1 text-xs">
                                  <input
                                    type="checkbox"
                                    checked={opt.isCorrect || false}
                                    onChange={e => {
                                      const opts = [...(nodeEditForm.options || [])];
                                      opts[idx] = { ...opts[idx], isCorrect: e.target.checked };
                                      setNodeEditForm(p => ({ ...p, options: opts }));
                                    }}
                                    className="rounded"
                                  />
                                  <span className="text-green-400">Correct</span>
                                </label>
                                <button
                                  onClick={() => {
                                    const opts = (nodeEditForm.options || []).filter((_, i) => i !== idx);
                                    setNodeEditForm(p => ({ ...p, options: opts }));
                                  }}
                                  className="text-red-400 hover:text-red-300 text-xs"
                                >✕</button>
                              </div>
                            </div>
                            <input type="text"
                              value={opt.text || ''}
                              onChange={e => {
                                const opts = [...(nodeEditForm.options || [])];
                                opts[idx] = { ...opts[idx], text: e.target.value };
                                setNodeEditForm(p => ({ ...p, options: opts }));
                              }}
                              className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs mb-1"
                              placeholder="Option text (Latin/Darija)"
                            />
                            <input type="text"
                              value={opt.text_arabic || ''}
                              onChange={e => {
                                const opts = [...(nodeEditForm.options || [])];
                                opts[idx] = { ...opts[idx], text_arabic: e.target.value };
                                setNodeEditForm(p => ({ ...p, options: opts }));
                              }}
                              className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs text-right" dir="rtl"
                              placeholder="Arabic text"
                            />
                            <div className="flex items-center gap-2 mt-1">
                              <label className="text-xs text-gray-400">Points:</label>
                              <input type="number"
                                value={opt.points || 0}
                                onChange={e => {
                                  const opts = [...(nodeEditForm.options || [])];
                                  opts[idx] = { ...opts[idx], points: parseInt(e.target.value) || 0 };
                                  setNodeEditForm(p => ({ ...p, options: opts }));
                                }}
                                className="w-16 bg-gray-600 text-white rounded px-2 py-1 text-xs"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                      <button
                        onClick={() => {
                          const opts = [...(nodeEditForm.options || []), { id: generateId(), text: '', text_arabic: '', isCorrect: false, points: 0 }];
                          setNodeEditForm(p => ({ ...p, options: opts }));
                        }}
                        className="mt-2 px-3 py-1 bg-amber-600 hover:bg-amber-500 text-white rounded text-xs"
                      >
                        + Add Option
                      </button>
                    </div>
                  )}
                </>)}

                {/* ══════ MESSAGE NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'message' && (<>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-gray-300 block mb-2">💬 Darija Text (Latin)</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.text || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, text: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          placeholder="Salam! Labas?" autoFocus
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.text_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, text_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          placeholder="Male version (leave empty if same)"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-gray-300 block mb-2">Arabic Script</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.text_arabic || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, text_arabic: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          dir="rtl" placeholder="سلام! لاباس؟"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.text_arabic_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, text_arabic_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          dir="rtl" placeholder="Male Arabic version (leave empty if same)"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">English Translation</label>
                    <textarea
                      value={nodeEditForm.translation || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, translation: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm" rows={2}
                      placeholder="Hello! How are you?"
                    />
                  </div>

                  {/* Dictionary Words for this message */}
                  <div className="border-t border-gray-600 pt-3 mt-3">
                    <label className="text-sm text-purple-400 block mb-2">📖 Dictionary Words (clickable in chat)</label>
                    <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                      {(nodeEditForm.vocabulary || []).map((word, idx) => (
                        <div key={idx} className="bg-gray-600 rounded p-2 text-xs">
                          <div className="flex justify-between items-center mb-1">
                            <span className="text-purple-300 font-bold">{word.darija || 'Word ' + (idx + 1)}</span>
                            <button
                              onClick={() => {
                                const newVocab = (nodeEditForm.vocabulary || []).filter((_, i) => i !== idx);
                                setNodeEditForm(p => ({ ...p, vocabulary: newVocab }));
                              }}
                              className="text-red-400 hover:text-red-300"
                            >✕</button>
                          </div>
                          <input type="text" value={word.darija || ''}
                            onChange={e => {
                              const nv = [...(nodeEditForm.vocabulary || [])];
                              nv[idx] = { ...nv[idx], darija: e.target.value };
                              setNodeEditForm(p => ({ ...p, vocabulary: nv }));
                            }}
                            placeholder="Darija word (e.g., Salam)"
                            className="w-full bg-gray-700 text-white rounded px-2 py-1 mb-1"
                          />
                          <input type="text" value={word.meaning || ''}
                            onChange={e => {
                              const nv = [...(nodeEditForm.vocabulary || [])];
                              nv[idx] = { ...nv[idx], meaning: e.target.value };
                              setNodeEditForm(p => ({ ...p, vocabulary: nv }));
                            }}
                            placeholder="Meaning (e.g., Hello)"
                            className="w-full bg-gray-700 text-white rounded px-2 py-1 mb-1"
                          />
                          <input type="text" value={word.phonetic || ''}
                            onChange={e => {
                              const nv = [...(nodeEditForm.vocabulary || [])];
                              nv[idx] = { ...nv[idx], phonetic: e.target.value };
                              setNodeEditForm(p => ({ ...p, vocabulary: nv }));
                            }}
                            placeholder="Phonetic (e.g., sah-LAAM)"
                            className="w-full bg-gray-700 text-white rounded px-2 py-1"
                          />
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => {
                        const nv = [...(nodeEditForm.vocabulary || []), { darija: '', meaning: '', phonetic: '' }];
                        setNodeEditForm(p => ({ ...p, vocabulary: nv }));
                      }}
                      className="w-full bg-purple-600 hover:bg-purple-500 text-white rounded p-2 text-xs"
                    >
                      + Add Dictionary Word
                    </button>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Emotion</label>
                    <select
                      value={nodeEditForm.emotion || 'neutral'}
                      onChange={e => setNodeEditForm(p => ({ ...p, emotion: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    >
                      <option value="neutral">😐 Neutral</option>
                      <option value="happy">😊 Happy</option>
                      <option value="excited">🤩 Excited</option>
                      <option value="thinking">🤔 Thinking</option>
                      <option value="surprised">😮 Surprised</option>
                      <option value="sad">😢 Sad</option>
                    </select>
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Audio URL (Dropbox)</label>
                    <div className="flex gap-2">
                      <input type="text"
                        value={nodeEditForm.audioUrl || ''}
                        onChange={e => setNodeEditForm(p => ({ ...p, audioUrl: e.target.value }))}
                        className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                        placeholder="https://dropbox.com/..."
                      />
                      {nodeEditForm.audioUrl && (
                        <button
                          onClick={() => { const a = new Audio(nodeEditForm.audioUrl); a.play().catch(() => {}); }}
                          className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm"
                          title="Preview audio"
                        >🔊</button>
                      )}
                    </div>
                    {nodeEditForm.audioUrl && !isValidMediaUrl(nodeEditForm.audioUrl) && (
                      <p className="text-xs text-red-400 mt-1">URL must be from Dropbox, GIPHY, Tenor, or Imgur</p>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Image URL (Dropbox)</label>
                    <input type="text"
                      value={nodeEditForm.imageUrl || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, imageUrl: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="https://dl.dropboxusercontent.com/..."
                    />
                    {nodeEditForm.imageUrl && !isValidMediaUrl(nodeEditForm.imageUrl) && (
                      <p className="text-xs text-red-400 mt-1">URL must be from Dropbox, GIPHY, Tenor, or Imgur</p>
                    )}
                    {nodeEditForm.imageUrl && isValidMediaUrl(nodeEditForm.imageUrl) && (
                      <div className="mt-2 rounded overflow-hidden border border-gray-600">
                        <img src={nodeEditForm.imageUrl} alt="Preview" className="w-full max-h-32 object-cover"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="text-sm text-gray-400 block mb-1">GIF URLs (GIPHY / Tenor) — Bot picks one randomly</label>
                    {(nodeEditForm.gifUrls?.length > 0
                      ? nodeEditForm.gifUrls
                      : (nodeEditForm.gifUrl ? [nodeEditForm.gifUrl] : [])
                    ).map((url, idx) => (
                      <div key={idx} className="flex items-center gap-2 mb-2">
                        <input type="text" value={url}
                          onChange={e => {
                            const urls = [...(nodeEditForm.gifUrls?.length > 0 ? nodeEditForm.gifUrls : (nodeEditForm.gifUrl ? [nodeEditForm.gifUrl] : []))];
                            urls[idx] = e.target.value;
                            setNodeEditForm(p => ({ ...p, gifUrls: urls, gifUrl: urls[0] || '' }));
                          }}
                          className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                          placeholder="https://media.giphy.com/..."
                        />
                        <button
                          onClick={() => {
                            const urls = [...(nodeEditForm.gifUrls?.length > 0 ? nodeEditForm.gifUrls : (nodeEditForm.gifUrl ? [nodeEditForm.gifUrl] : []))];
                            urls.splice(idx, 1);
                            setNodeEditForm(p => ({ ...p, gifUrls: urls, gifUrl: urls[0] || '' }));
                          }}
                          className="text-red-400 hover:text-red-300 text-sm px-2 py-1" title="Remove"
                        >✕</button>
                      </div>
                    ))}
                    {(nodeEditForm.gifUrls?.length || 0) < 4 && (
                      <button
                        onClick={() => {
                          const urls = [...(nodeEditForm.gifUrls?.length > 0 ? nodeEditForm.gifUrls : (nodeEditForm.gifUrl ? [nodeEditForm.gifUrl] : []))];
                          urls.push('');
                          setNodeEditForm(p => ({ ...p, gifUrls: urls, gifUrl: urls[0] || '' }));
                        }}
                        className="text-sm text-blue-400 hover:text-blue-300 mt-1"
                      >+ Add GIF URL</button>
                    )}
                    <p className="text-xs text-gray-500 mt-1">Add 1-4 GIF URLs from giphy.com or tenor.com. Bot randomly picks one each time.</p>
                    {(nodeEditForm.gifUrls || []).some(u => u && !isValidMediaUrl(u)) && (
                      <p className="text-xs text-red-400 mt-1">Some URLs are not from allowed domains (GIPHY, Tenor, Dropbox, Imgur)</p>
                    )}
                    {(() => {
                      const firstValid = (nodeEditForm.gifUrls || []).find(u => u && isValidMediaUrl(u));
                      return firstValid ? (
                        <div className="mt-2 rounded overflow-hidden border border-gray-600">
                          <img src={firstValid} alt="GIF Preview" className="w-full max-h-32 object-cover"
                            onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                      ) : null;
                    })()}
                  </div>
                </>)}

                {/* ══════ TIP NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'tip' && (<>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-gray-300 block mb-2">💡 Tip Text (Latin)</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.text || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, text: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          placeholder="Helpful tip here..." autoFocus
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.text_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, text_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          placeholder="Male version (leave empty if same)"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-gray-300 block mb-2">Arabic Script</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.tipText_arabic || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, tipText_arabic: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30 text-right" dir="rtl" rows={2}
                          placeholder="Arabic script..."
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.tipText_arabic_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, tipText_arabic_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30 text-right" dir="rtl" rows={2}
                          placeholder="Male Arabic version (leave empty if same)"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Tip Type</label>
                    <select
                      value={nodeEditForm.tipType || 'info'}
                      onChange={e => setNodeEditForm(p => ({ ...p, tipType: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    >
                      <option value="info">ℹ️ Info</option>
                      <option value="warning">⚠️ Warning</option>
                      <option value="success">✅ Success</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Audio URL (optional)</label>
                    <div className="flex gap-2">
                      <input type="text"
                        value={nodeEditForm.audioUrl || ''}
                        onChange={e => setNodeEditForm(p => ({ ...p, audioUrl: e.target.value }))}
                        className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                        placeholder="https://dropbox.com/...tip-audio.mp3"
                      />
                      {nodeEditForm.audioUrl && (
                        <button onClick={() => { const a = new Audio(nodeEditForm.audioUrl); a.play().catch(() => {}); }}
                          className="px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded text-xs">
                          Preview
                        </button>
                      )}
                    </div>
                  </div>
                </>)}

                {/* ══════ ACHIEVEMENT NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'achievement' && (<>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Achievement Name</label>
                    <input type="text"
                      value={nodeEditForm.name || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, name: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="First Greeting!" autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Icon</label>
                    <input type="text"
                      value={nodeEditForm.icon || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, icon: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="🏆"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Points</label>
                    <input type="number"
                      value={nodeEditForm.points || 0}
                      onChange={e => setNodeEditForm(p => ({ ...p, points: parseInt(e.target.value) || 0 }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    />
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-gray-300 block mb-2">Message</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.message || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, message: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          placeholder="You earned an achievement!"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.message_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, message_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          placeholder="Male version (leave empty if same)"
                        />
                      </div>
                    </div>
                  </div>
                </>)}

                {/* ══════ VOCABULARY NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'vocabulary' && (<>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Word (Darija Latin)</label>
                    <input type="text"
                      value={nodeEditForm.darija || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, darija: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="salam" autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Word (Arabic)</label>
                    <input type="text"
                      value={nodeEditForm.arabic || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, arabic: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm" dir="rtl"
                      placeholder="سلام"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Translation</label>
                    <input type="text"
                      value={nodeEditForm.english || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, english: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="hello"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Pronunciation</label>
                    <input type="text"
                      value={nodeEditForm.phonetic || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, phonetic: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="sa-lam"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Example</label>
                    <input type="text"
                      value={nodeEditForm.example || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, example: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="Salam, kifash?"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Audio URL</label>
                    <div className="flex gap-2">
                      <input type="text"
                        value={nodeEditForm.audioUrl || ''}
                        onChange={e => setNodeEditForm(p => ({ ...p, audioUrl: e.target.value }))}
                        className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                        placeholder="https://dropbox.com/..."
                      />
                      {nodeEditForm.audioUrl && (
                        <button
                          onClick={() => { const a = new Audio(nodeEditForm.audioUrl); a.play().catch(() => {}); }}
                          className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm"
                          title="Preview audio"
                        >🔊</button>
                      )}
                    </div>
                  </div>
                </>)}

                {/* ══════ AUDIO NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'audio' && (<>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Audio URL (Dropbox)</label>
                    <div className="flex gap-2">
                      <input type="text"
                        value={nodeEditForm.audioUrl || ''}
                        onChange={e => setNodeEditForm(p => ({ ...p, audioUrl: e.target.value }))}
                        className="flex-1 bg-gray-700 text-white rounded p-2 text-sm"
                        placeholder="https://dropbox.com/..." autoFocus
                      />
                      {nodeEditForm.audioUrl && (
                        <button
                          onClick={() => { const a = new Audio(nodeEditForm.audioUrl); a.play().catch(() => {}); }}
                          className="px-3 bg-teal-600 hover:bg-teal-500 text-white rounded text-sm"
                          title="Preview audio"
                        >🔊</button>
                      )}
                    </div>
                    {nodeEditForm.audioUrl && !isValidMediaUrl(nodeEditForm.audioUrl) && (
                      <p className="text-xs text-red-400 mt-1">URL must be from Dropbox, GIPHY, Tenor, or Imgur</p>
                    )}
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Auto Play</label>
                    <select
                      value={nodeEditForm.autoPlay ? 'yes' : 'no'}
                      onChange={e => setNodeEditForm(p => ({ ...p, autoPlay: e.target.value === 'yes' }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    >
                      <option value="yes">Yes — plays automatically</option>
                      <option value="no">No — user clicks to play</option>
                    </select>
                  </div>
                </>)}

                {/* ══════ CHALLENGE NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'quiz' && (<>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Challenge Type</label>
                    <select
                      value={nodeEditForm.challengeType || 'choice'}
                      onChange={e => setNodeEditForm(p => ({ ...p, challengeType: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    >
                      <option value="choice">Multiple Choice</option>
                      <option value="unscramble">Unscramble</option>
                      <option value="fill_blank">Fill in Blank</option>
                    </select>
                  </div>
                  <div className="bg-gray-700/30 rounded-lg p-3 border border-gray-600">
                    <label className="text-sm font-semibold text-gray-300 block mb-2">Question (Latin)</label>
                    <div className="space-y-2">
                      <div>
                        <label className="text-xs text-pink-300 block mb-1">♀ Female version</label>
                        <textarea
                          value={nodeEditForm.question || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, question: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-pink-600/30" rows={2}
                          placeholder="How do you say hello in Darija?" autoFocus
                        />
                      </div>
                      <div>
                        <label className="text-xs text-blue-300 block mb-1">♂ Male version</label>
                        <textarea
                          value={nodeEditForm.question_male || ''}
                          onChange={e => setNodeEditForm(p => ({ ...p, question_male: e.target.value }))}
                          className="w-full bg-gray-700 text-white rounded p-2 text-sm border border-blue-600/30" rows={2}
                          placeholder="Male version (leave empty if same)"
                        />
                      </div>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Question (Arabic Script)</label>
                    <textarea
                      value={nodeEditForm.question_arabic || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, question_arabic: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm text-right" dir="rtl" rows={2}
                      placeholder="Arabic script..."
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Correct Answer</label>
                    <input type="text"
                      value={nodeEditForm.correctAnswer || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, correctAnswer: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                      placeholder="salam"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Points</label>
                    <input type="number"
                      value={nodeEditForm.points || 0}
                      onChange={e => setNodeEditForm(p => ({ ...p, points: parseInt(e.target.value) || 0 }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    />
                  </div>
                </>)}

                {/* ══════ DELAY NODE ══════ */}
                {nodeEditForm.type === 'delay' && (<>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Delay (milliseconds)</label>
                    <input type="number"
                      value={nodeEditForm.delayMs || 1500}
                      onChange={e => setNodeEditForm(p => ({ ...p, delayMs: parseInt(e.target.value) || 1500 }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    />
                    <p className="text-xs text-gray-500 mt-1">1500 = 1.5 seconds. Bot pauses before next step.</p>
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Show Typing Indicator</label>
                    <select
                      value={nodeEditForm.showTyping ? 'yes' : 'no'}
                      onChange={e => setNodeEditForm(p => ({ ...p, showTyping: e.target.value === 'yes' }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                    >
                      <option value="yes">Yes — show "typing..." during delay</option>
                      <option value="no">No — just pause silently</option>
                    </select>
                  </div>
                </>)}

                {/* ══════ END NODE (exact flow editor copy) ══════ */}
                {nodeEditForm.type === 'end' && (<>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">End Message</label>
                    <textarea
                      value={nodeEditForm.message || ''}
                      onChange={e => setNodeEditForm(p => ({ ...p, message: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm" rows={2}
                      placeholder="Great job! You completed this conversation!" autoFocus
                    />
                  </div>
                  <div>
                    <label className="text-sm text-gray-400 block mb-1">Summary (one per line)</label>
                    <textarea
                      value={(nodeEditForm.summary || []).join('\n')}
                      onChange={e => setNodeEditForm(p => ({ ...p, summary: e.target.value.split('\n').filter(s => s.trim()) }))}
                      className="w-full bg-gray-700 text-white rounded p-2 text-sm" rows={4}
                      placeholder={"Labas - How are you?\nShukran - Thank you"}
                    />
                    <p className="text-xs text-gray-500 mt-1">Words/phrases learned — shown at end of conversation.</p>
                  </div>
                </>)}

                {/* Add Step button */}
                <div className="border-t border-gray-700 pt-4 mt-4">
                  {trainingSteps.length === 0 && (
                    <div className="bg-yellow-900/50 border border-yellow-600/50 rounded-lg p-2 text-xs text-yellow-300 mb-2">
                      Click "Add Step" to save this node before finishing.
                    </div>
                  )}
                  <button onClick={saveNodeFromForm}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-500 text-white rounded text-sm font-medium">
                    {editingStepIndex !== null ? '✏️ Update Step' : '✅ Add Step'}
                  </button>
                  <button onClick={() => setNodeEditForm(null)}
                    className="w-full mt-2 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );


  const renderSettings = () => (
    <div className="flex-1 overflow-y-auto p-4">
      <h3 className="text-lg font-bold text-white mb-4">⚙️ Conversation Challenge Settings</h3>
      
      <div className="space-y-6 max-w-xl">
        {/* GAME VISIBILITY CONTROLS - Like Morocco Journey */}
        <div className="p-4 rounded-lg bg-gradient-to-r from-purple-900 to-indigo-900 border border-purple-700">
          <h4 className="text-white font-bold mb-4">🎮 Game Visibility & Access</h4>
          <div className="space-y-4">
            {/* Visible/Hidden Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
              <div>
                <span className="text-white font-medium">👁️ Visibility</span>
                <p className="text-xs text-gray-400">Show or hide from Games page</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleGameVisibility(true)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-all ${gameSettings.is_visible ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                >
                  Visible
                </button>
                <button
                  onClick={() => toggleGameVisibility(false)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-all ${!gameSettings.is_visible ? 'bg-red-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                >
                  Hidden
                </button>
              </div>
            </div>
            
            {/* Free/Pro Toggle */}
            <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
              <div>
                <span className="text-white font-medium">💎 Access Level</span>
                <p className="text-xs text-gray-400">Free for all or Pro members only</p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => toggleGamePro(false)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-all ${!gameSettings.is_pro ? 'bg-green-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                >
                  Free
                </button>
                <button
                  onClick={() => toggleGamePro(true)}
                  className={`px-3 py-1 rounded text-sm font-medium transition-all ${gameSettings.is_pro ? 'bg-yellow-600 text-white' : 'bg-gray-600 text-gray-300'}`}
                >
                  Pro 💎
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Game Card Text */}
        <div className="p-4 rounded-lg bg-gray-800 border border-gray-700">
          <h4 className="text-white font-bold mb-3">🏷️ Game Card Text</h4>
          <p className="text-xs text-gray-400 mb-3">Edit the name and description shown on the Games page card</p>
          <div className="space-y-3">
            <div>
              <label className="text-xs font-semibold text-gray-400">Game Name</label>
              <input
                value={gameSettings.name || ''}
                onChange={e => setGameSettings(prev => ({ ...prev, name: e.target.value }))}
                className="w-full px-3 py-1.5 rounded-lg text-sm bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-gray-400">Description</label>
              <input
                value={gameSettings.description || ''}
                onChange={e => setGameSettings(prev => ({ ...prev, description: e.target.value }))}
                className="w-full px-3 py-1.5 rounded-lg text-sm bg-gray-700 text-white border border-gray-600 focus:border-blue-500 focus:outline-none"
              />
            </div>
            <button
              disabled={savingGameCard}
              onClick={async () => {
                setSavingGameCard(true);
                try {
                  const res = await fetch(`${API_URL}/api/conversation-challenge/game-settings`, {
                    method: 'PUT',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ name: gameSettings.name, description: gameSettings.description })
                  });
                  if (res.ok) showNotification('Game card text saved!', 'success');
                  else showNotification('Failed to save', 'error');
                } catch (e) { showNotification('Error saving', 'error'); }
                finally { setSavingGameCard(false); }
              }}
              className="px-4 py-1.5 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold"
            >
              {savingGameCard ? 'Saving...' : 'Save Card Text'}
            </button>
          </div>
        </div>

        {/* Feature Toggles */}
        <div>
          <h4 className="text-white font-bold mb-3">🔧 Feature Toggles</h4>
          {[
            { key: 'audio_enabled', label: '🔊 Audio Enabled', desc: 'Enable audio playback for pronunciation' },
            { key: 'achievements_enabled', label: '🏆 Achievements Enabled', desc: 'Show achievement popups' },
            { key: 'story_mode_enabled', label: '📖 Story Mode Enabled', desc: 'Allow story mode with chapters' },
            { key: 'randomization_enabled', label: '🎲 Randomization', desc: 'Randomize conversation paths' },
            { key: 'hints_enabled', label: '💡 Hints Enabled', desc: 'Show hints for wrong answers' },
            { key: 'streak_tracking', label: '🔥 Streak Tracking', desc: 'Track correct answer streaks' },
            { key: 'time_challenges_enabled', label: '⏱️ Time Challenges', desc: 'Enable timed challenges for bonus points' }
          ].map(item => (
            <div key={item.key} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg mb-2">
              <div>
                <span className="text-white font-medium">{item.label}</span>
                <p className="text-xs text-gray-400">{item.desc}</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings[item.key]}
                  onChange={(e) => setSettings(prev => ({ ...prev, [item.key]: e.target.checked }))}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-500"></div>
              </label>
            </div>
          ))}
        </div>
        
        {/* Hint delay */}
        <div className="p-3 bg-gray-700 rounded-lg">
          <label className="text-white font-medium block mb-2">Default Hint Delay</label>
          <p className="text-xs text-gray-400 mb-2">Show hint after this many wrong attempts</p>
          <input
            type="number"
            value={settings.default_hint_delay}
            onChange={(e) => setSettings(prev => ({ ...prev, default_hint_delay: parseInt(e.target.value) }))}
            className="w-20 bg-gray-600 text-white rounded p-2"
            min={1}
            max={5}
          />
        </div>
        
        <button
          onClick={saveSettings}
          className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-lg font-bold"
        >
          💾 Save Settings
        </button>
      </div>
      
      {/* GROQ AI DICTIONARY - Moved to Dictionary Tab */}
      <div className="mt-8 p-4 rounded-lg bg-gradient-to-r from-blue-900 to-cyan-900 border border-cyan-700">
        <h4 className="text-white font-bold mb-4 flex items-center gap-2">
          🤖 AI Dictionary (Groq)
          <span className="text-xs px-2 py-0.5 bg-cyan-600 rounded-full">Admin Only</span>
        </h4>
        <p className="text-gray-300 mb-4">The AI Dictionary has been moved to its own dedicated tab for easier management.</p>
        <button
          onClick={() => setActiveTab('dictionary')}
          className="px-6 py-3 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg font-bold"
        >
          📚 Go to Dictionary Tab →
        </button>
      </div>
    </div>
  );
  
  // Main render
  return (
    <div className={`h-full flex flex-col ${isDarkMode ? 'bg-gray-900' : 'bg-gray-100'}`}>
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 px-4 py-2 rounded-lg shadow-lg z-50 ${
          notification.type === 'success' ? 'bg-green-600' : 
          notification.type === 'error' ? 'bg-red-600' : 'bg-blue-600'
        } text-white`}>
          {notification.message}
        </div>
      )}
      
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-800 to-indigo-800 p-4 shadow-lg">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          🗣️ Conversation Challenge Editor
        </h2>
        <p className="text-purple-200 text-sm">Create visual conversation flows for your bots ({Array.isArray(bots) ? bots.length : 2} characters)</p>
      </div>

      {/* Info Note about Latin/Arabic in same flow */}
      {(
        <div className="bg-blue-900/50 border-l-4 border-blue-400 px-4 py-3 mx-4 mt-3 rounded-r-lg">
          <div className="flex items-start gap-2">
            <span className="text-blue-400 text-lg">💡</span>
            <div>
              <p className="text-blue-200 text-sm font-medium">Latin & Arabic Script in Same Flow</p>
              <p className="text-blue-300/80 text-xs mt-1">
                Each conversation has <strong>both Latin and Arabic text</strong> in the same flow.
                Edit the <span className="text-yellow-300">Darija Text (Latin)</span> and <span className="text-yellow-300">Arabic Script</span> fields in each message node.
                Users switch between scripts in the frontend - same conversation, different display.
              </p>
            </div>
          </div>
        </div>
      )}
      
      {/* Tabs */}
      <div className="flex border-b border-gray-700 bg-gray-800">
        {[
          { id: 'flows', label: '💬 Flows', count: flows.length },
          { id: 'stories', label: '📖 Stories', count: stories.length },
          { id: 'bots', label: '🤖 Bots' },
          { id: 'user-avatars', label: '👤 User Avatars' },
          { id: 'intents', label: '🧠 Smart Intents', count: intents.length },
          { id: 'fuzzy', label: '🔤 Fuzzy Match' },
          { id: 'dictionary', label: '📚 Dictionary', count: savedVocabulary.length },
          { id: 'settings', label: '⚙️ Settings' },
          { id: 'train', label: '🎓 Train Bot', count: trainedConversations.length }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setActiveTab(tab.id); setSelectedFlow(null); setSelectedNode(null); }}
            className={`px-4 py-3 font-medium transition-all ${
              activeTab === tab.id 
                ? 'bg-gray-700 text-white border-b-2 border-yellow-400' 
                : 'text-gray-400 hover:text-white hover:bg-gray-700'
            }`}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="ml-2 px-2 py-0.5 bg-gray-600 rounded-full text-xs">{tab.count}</span>
            )}
          </button>
        ))}
      </div>
      
      {/* New Flow Form Modal */}
      {showNewFlowForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Create New Flow</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Name</label>
                <input
                  type="text"
                  value={newFlowData.name}
                  onChange={(e) => setNewFlowData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-700 text-white rounded p-2"
                  placeholder="Morning Greetings"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Description</label>
                <textarea
                  value={newFlowData.description}
                  onChange={(e) => setNewFlowData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-gray-700 text-white rounded p-2"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Assign to Bot</label>
                  <select
                    value={newFlowData.bot_id || newFlowData.bot_gender || ''}
                    onChange={(e) => {
                      const selectedBotId = e.target.value;
                      const selectedBotObj = (Array.isArray(bots) ? bots : []).find(b => b.id === selectedBotId);
                      setNewFlowData(prev => ({ 
                        ...prev, 
                        bot_id: selectedBotId,
                        bot_gender: selectedBotObj?.gender || selectedBotId 
                      }));
                    }}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="">Select a bot...</option>
                    {(Array.isArray(bots) ? bots : []).map(bot => (
                      <option key={bot.id} value={bot.id}>{bot.icon} {bot.name} ({bot.personality})</option>
                    ))}
                    {(!Array.isArray(bots) || bots.length === 0) && (
                      <>
                        <option value="female">👩 Fatima</option>
                        <option value="male">👨 Ahmed</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Script</label>
                  <select
                    value={newFlowData.script_type}
                    onChange={(e) => setNewFlowData(prev => ({ ...prev, script_type: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="latin">Latin/English</option>
                    <option value="arabic">Arabic Script</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Difficulty</label>
                  <select
                    value={newFlowData.difficulty}
                    onChange={(e) => setNewFlowData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Category</label>
                  <input
                    type="text"
                    value={newFlowData.category}
                    onChange={(e) => setNewFlowData(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                    placeholder="Greetings"
                  />
                </div>
              </div>
              <label className="flex items-center gap-2 text-white">
                <input
                  type="checkbox"
                  checked={newFlowData.is_free}
                  onChange={(e) => setNewFlowData(prev => ({ ...prev, is_free: e.target.checked }))}
                />
                Free (available to all users)
              </label>
              
              {/* Quiz Configuration */}
              <div className="border-t border-gray-700 pt-4">
                <label className="text-sm text-gray-400 block mb-2">🧠 Quiz Settings</label>
                <div className="flex items-center gap-4 mb-2">
                  <label className="flex items-center gap-1 text-xs text-gray-400">
                    <input
                      type="checkbox"
                      checked={newFlowData.quiz_enabled !== false}
                      onChange={(e) => setNewFlowData(prev => ({ ...prev, quiz_enabled: e.target.checked }))}
                      className="rounded bg-gray-700 border-gray-600"
                    />
                    Enable Quiz
                  </label>
                  <select
                    value={newFlowData.quiz_difficulty || 'auto'}
                    onChange={(e) => setNewFlowData(prev => ({ ...prev, quiz_difficulty: e.target.value }))}
                    className="bg-gray-700 text-white rounded px-2 py-1 text-xs"
                  >
                    <option value="auto">Auto Difficulty</option>
                    <option value="easy">Easy (2 options)</option>
                    <option value="medium">Medium (3 options)</option>
                    <option value="hard">Hard (4 options)</option>
                  </select>
                </div>
              </div>
              
              {/* Enhanced Vocabulary / Dictionary Words */}
              <div className="border-t border-gray-700 pt-4">
                <label className="text-sm text-gray-400 block mb-2">📖 Dictionary Words ({(newFlowData.vocabulary || []).length})</label>
                <div className="space-y-2 max-h-40 overflow-y-auto mb-2">
                  {(newFlowData.vocabulary || []).map((word, idx) => (
                    <div key={idx} className="bg-gray-700 rounded p-2">
                      <div className="flex justify-between items-start mb-1">
                        <span className="text-purple-400 text-xs font-bold">{word.darija || '...'}</span>
                        <button
                          onClick={() => {
                            const newVocab = (newFlowData.vocabulary || []).filter((_, i) => i !== idx);
                            setNewFlowData(prev => ({ ...prev, vocabulary: newVocab }));
                          }}
                          className="text-red-400 hover:text-red-300 text-xs"
                        >✕</button>
                      </div>
                      <div className="grid grid-cols-2 gap-1 text-xs">
                        <input
                          type="text"
                          value={word.darija || ''}
                          onChange={(e) => {
                            const newVocab = [...(newFlowData.vocabulary || [])];
                            newVocab[idx] = { ...newVocab[idx], darija: e.target.value };
                            setNewFlowData(prev => ({ ...prev, vocabulary: newVocab }));
                          }}
                          placeholder="Darija"
                          className="bg-gray-600 text-white rounded px-2 py-1"
                        />
                        <input
                          type="text"
                          value={word.meaning || ''}
                          onChange={(e) => {
                            const newVocab = [...(newFlowData.vocabulary || [])];
                            newVocab[idx] = { ...newVocab[idx], meaning: e.target.value };
                            setNewFlowData(prev => ({ ...prev, vocabulary: newVocab }));
                          }}
                          placeholder="Meaning"
                          className="bg-gray-600 text-white rounded px-2 py-1"
                        />
                        <input
                          type="text"
                          value={word.phonetic || ''}
                          onChange={(e) => {
                            const newVocab = [...(newFlowData.vocabulary || [])];
                            newVocab[idx] = { ...newVocab[idx], phonetic: e.target.value };
                            setNewFlowData(prev => ({ ...prev, vocabulary: newVocab }));
                          }}
                          placeholder="Phonetic"
                          className="bg-gray-600 text-white rounded px-2 py-1"
                        />
                        <select
                          value={word.category || 'Other'}
                          onChange={(e) => {
                            const newVocab = [...(newFlowData.vocabulary || [])];
                            newVocab[idx] = { ...newVocab[idx], category: e.target.value };
                            setNewFlowData(prev => ({ ...prev, vocabulary: newVocab }));
                          }}
                          className="bg-gray-600 text-white rounded px-2 py-1"
                        >
                          {vocabCategories.map(cat => (
                            <option key={cat} value={cat}>{cat}</option>
                          ))}
                        </select>
                      </div>
                      <input
                        type="text"
                        value={word.audioUrl || ''}
                        onChange={(e) => {
                          const newVocab = [...(newFlowData.vocabulary || [])];
                          newVocab[idx] = { ...newVocab[idx], audioUrl: e.target.value };
                          setNewFlowData(prev => ({ ...prev, vocabulary: newVocab }));
                        }}
                        placeholder="🔊 Audio URL (Dropbox link)"
                        className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs mt-1"
                      />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => {
                    const newWord = { darija: '', meaning: '', phonetic: '', category: 'Other', audioUrl: '' };
                    setNewFlowData(prev => ({ ...prev, vocabulary: [...(prev.vocabulary || []), newWord] }));
                  }}
                  className="w-full bg-gray-700 hover:bg-gray-600 text-purple-400 rounded p-2 text-sm"
                >
                  + Add Dictionary Word
                </button>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowNewFlowForm(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={createNewFlow}
                disabled={!newFlowData.name || !newFlowData.bot_id}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
              >
                Create Flow
              </button>
            </div>
            {!newFlowData.bot_id && (
              <p className="text-amber-400 text-xs mt-2 text-center">⚠️ Please select a bot to assign this conversation to</p>
            )}
          </div>
        </div>
      )}
      
      {/* New Story Form Modal */}
      {showNewStoryForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">Create New Story</h3>
            <div className="space-y-4">
              <div>
                <label className="text-sm text-gray-400 block mb-1">Story Name</label>
                <input
                  type="text"
                  value={newStoryData.name}
                  onChange={(e) => setNewStoryData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full bg-gray-700 text-white rounded p-2"
                  placeholder="A Day in Marrakech"
                />
              </div>
              <div>
                <label className="text-sm text-gray-400 block mb-1">Description</label>
                <textarea
                  value={newStoryData.description}
                  onChange={(e) => setNewStoryData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full bg-gray-700 text-white rounded p-2"
                  rows={2}
                  placeholder="Follow a complete day learning Darija..."
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Main Bot</label>
                  <select
                    value={newStoryData.bot_gender}
                    onChange={(e) => setNewStoryData(prev => ({ ...prev, bot_gender: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    {(Array.isArray(bots) ? bots : []).map(bot => (
                      <option key={bot.id} value={bot.gender || bot.id}>{bot.icon} {bot.name}</option>
                    ))}
                    {(!Array.isArray(bots) || bots.length === 0) && (
                      <>
                        <option value="female">👩 Fatima</option>
                        <option value="male">👨 Ahmed</option>
                      </>
                    )}
                  </select>
                </div>
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Script</label>
                  <select
                    value={newStoryData.script_type}
                    onChange={(e) => setNewStoryData(prev => ({ ...prev, script_type: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="latin">Latin/English</option>
                    <option value="arabic">Arabic Script</option>
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-gray-400 block mb-1">Difficulty</label>
                  <select
                    value={newStoryData.difficulty}
                    onChange={(e) => setNewStoryData(prev => ({ ...prev, difficulty: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded p-2"
                  >
                    <option value="Beginner">Beginner</option>
                    <option value="Intermediate">Intermediate</option>
                    <option value="Advanced">Advanced</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-2 text-white mt-6">
                    <input
                      type="checkbox"
                      checked={newStoryData.is_free}
                      onChange={(e) => setNewStoryData(prev => ({ ...prev, is_free: e.target.checked }))}
                    />
                    Free (available to all)
                  </label>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <button
                onClick={() => setShowNewStoryForm(false)}
                className="flex-1 px-4 py-2 bg-gray-600 hover:bg-gray-500 text-white rounded"
              >
                Cancel
              </button>
              <button
                onClick={createNewStory}
                disabled={!newStoryData.name}
                className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded disabled:opacity-50"
              >
                Create Story
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Main content area */}
      {loading ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
        </div>
      ) : (
        <div className="flex-1 flex overflow-hidden">
          {activeTab === 'flows' && !selectedFlow && renderFlowsList()}
          {activeTab === 'stories' && renderStoriesList()}
          {activeTab === 'bots' && renderBotsManager()}
          {activeTab === 'user-avatars' && renderUserAvatarsManager()}
          {activeTab === 'intents' && renderIntentsManager()}
          {activeTab === 'fuzzy' && renderFuzzyMatchManager()}
          {activeTab === 'dictionary' && renderDictionaryManager()}
          {activeTab === 'settings' && renderSettings()}
          {activeTab === 'train' && renderTrainBot()}

          {/* Flow Editor */}
          {activeTab === 'flows' && selectedFlow && (
            <div className={`flex ${isFullscreen ? 'fixed inset-0 z-50 bg-gray-900' : 'flex-1'}`}>
              {/* Flow toolbar */}
              <div className={`${isFullscreen ? 'w-56' : 'w-64'} bg-gray-800 p-4 border-r border-gray-700 overflow-y-auto`}>
                <div className="flex items-center justify-between mb-4">
                  <button
                    onClick={() => { 
                      if (isFullscreen) setIsFullscreen(false);
                      else { setSelectedFlow(null); setSelectedNode(null); }
                    }}
                    className="text-gray-400 hover:text-white text-sm flex items-center gap-1"
                  >
                    ← {isFullscreen ? 'Exit Fullscreen' : 'Back to Flows'}
                  </button>
                </div>
                
                <h3 className="text-white font-bold mb-2 truncate">{selectedFlow.name}</h3>
                
                {/* Flow Name Editing */}
                <div className="mb-3 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-0.5">Title (English)</label>
                    <input
                      type="text"
                      value={selectedFlow.name || ''}
                      onChange={(e) => setSelectedFlow(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded px-2 py-1 text-xs"
                      placeholder="Flow name"
                    />
                  </div>
                  <div>
                    <label className="text-[10px] text-gray-500 block mb-0.5">Title (Arabic)</label>
                    <input
                      type="text"
                      value={selectedFlow.name_arabic || ''}
                      onChange={(e) => setSelectedFlow(prev => ({ ...prev, name_arabic: e.target.value }))}
                      className="w-full bg-gray-700 text-white rounded px-2 py-1 text-xs text-right"
                      dir="rtl"
                      placeholder="العنوان بالعربية"
                    />
                  </div>
                </div>
                
                {/* Reassign Bot dropdown */}
                <div className="mb-4">
                  <label className="text-xs text-gray-400 block mb-1">Assigned to Bot:</label>
                  <select
                    value={selectedFlow.bot_id || selectedFlow.bot_gender || ''}
                    onChange={(e) => {
                      const newBotId = e.target.value;
                      const newBot = (Array.isArray(bots) ? bots : []).find(b => b.id === newBotId);
                      setSelectedFlow(prev => ({
                        ...prev,
                        bot_id: newBotId,
                        bot_gender: newBot?.gender || newBotId
                      }));
                      // Also update in flows list
                      setFlows(prevFlows => prevFlows.map(f => 
                        f.id === selectedFlow.id 
                          ? { ...f, bot_id: newBotId, bot_gender: newBot?.gender || newBotId }
                          : f
                      ));
                      showNotification(`Flow reassigned to ${newBot?.name || newBotId}`, 'success');
                    }}
                    className="w-full bg-gray-700 text-white rounded p-2 text-sm"
                  >
                    {(Array.isArray(bots) ? bots : []).map(bot => (
                      <option key={bot.id} value={bot.id}>
                        {bot.icon} {bot.name} ({bot.personality})
                      </option>
                    ))}
                  </select>
                </div>
                
                <p className="text-gray-400 text-xs mb-4">
                  {selectedFlow.script_type} • {selectedFlow.nodes?.length || 0} nodes
                </p>
                
                {/* Quiz Configuration */}
                <div className="mb-4 border-t border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-400 text-xs font-bold uppercase">🧠 Quiz Settings</p>
                    <button
                      onClick={() => setShowQuizEditor(!showQuizEditor)}
                      className="text-purple-400 hover:text-purple-300 text-xs"
                    >
                      {showQuizEditor ? '▼' : '▶'} {selectedFlow.custom_quiz_questions?.length || 0} custom
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-2">
                    <label className="flex items-center gap-1 text-xs text-gray-400">
                      <input
                        type="checkbox"
                        checked={selectedFlow.quiz_enabled !== false}
                        onChange={(e) => setSelectedFlow(prev => ({ ...prev, quiz_enabled: e.target.checked }))}
                        className="rounded bg-gray-700 border-gray-600"
                      />
                      Enable Quiz
                    </label>
                  </div>
                  
                  <select
                    value={selectedFlow.quiz_difficulty || 'auto'}
                    onChange={(e) => setSelectedFlow(prev => ({ ...prev, quiz_difficulty: e.target.value }))}
                    className="w-full bg-gray-700 text-white rounded px-2 py-1 text-xs mb-2"
                  >
                    <option value="auto">Auto (from vocabulary)</option>
                    <option value="easy">Easy (show 2 options)</option>
                    <option value="medium">Medium (show 3 options)</option>
                    <option value="hard">Hard (show 4 options)</option>
                  </select>
                  
                  {/* Custom Quiz Questions */}
                  {showQuizEditor && (
                    <div className="space-y-2 mt-2 p-2 bg-gray-800 rounded">
                      <p className="text-gray-500 text-xs">Custom Quiz Questions</p>
                      {(selectedFlow.custom_quiz_questions || []).map((q, idx) => (
                        <div key={idx} className="bg-gray-700 rounded p-2 text-xs">
                          <div className="flex justify-between items-start mb-1">
                            <span className="text-purple-400">{q.question || 'Question...'}</span>
                            <button
                              onClick={() => {
                                const newQs = (selectedFlow.custom_quiz_questions || []).filter((_, i) => i !== idx);
                                setSelectedFlow(prev => ({ ...prev, custom_quiz_questions: newQs }));
                              }}
                              className="text-red-400 hover:text-red-300"
                            >✕</button>
                          </div>
                          <p className="text-green-400 text-xs">✓ {q.correctAnswer}</p>
                        </div>
                      ))}
                      <button
                        onClick={() => {
                          const newQ = { 
                            question: '', 
                            correctAnswer: '', 
                            options: ['', '', ''],
                            word: '' 
                          };
                          setSelectedFlow(prev => ({ 
                            ...prev, 
                            custom_quiz_questions: [...(prev.custom_quiz_questions || []), newQ] 
                          }));
                          setEditingQuizQuestion((selectedFlow.custom_quiz_questions || []).length);
                        }}
                        className="w-full bg-purple-600 hover:bg-purple-500 text-white rounded p-1 text-xs"
                      >
                        + Add Question
                      </button>
                    </div>
                  )}
                </div>
                
                {/* Enhanced Vocabulary / Dictionary Editor */}
                <div className="mb-4 border-t border-gray-700 pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-gray-400 text-xs font-bold uppercase">📖 Dictionary ({(selectedFlow.vocabulary || []).length})</p>
                    <button
                      onClick={() => setShowVocabManager(!showVocabManager)}
                      className="text-blue-400 hover:text-blue-300 text-xs"
                    >
                      {showVocabManager ? '✕ Close' : '⚙️ Manage'}
                    </button>
                  </div>
                  
                  {/* Vocabulary Manager Panel */}
                  {showVocabManager && (
                    <div className="bg-gray-800 rounded p-2 mb-2 space-y-2">
                      {/* Search */}
                      <input
                        type="text"
                        value={vocabSearchQuery}
                        onChange={(e) => setVocabSearchQuery(e.target.value)}
                        placeholder="🔍 Search vocabulary..."
                        className="w-full bg-gray-700 text-white rounded px-2 py-1 text-xs"
                      />
                      
                      {/* Bulk Import */}
                      <div>
                        <p className="text-gray-500 text-xs mb-1">Bulk Import (format: darija|meaning|phonetic)</p>
                        <textarea
                          value={vocabBulkInput}
                          onChange={(e) => setVocabBulkInput(e.target.value)}
                          placeholder="sba7|morning|sbah&#10;shukran|thank you|shook-ran"
                          className="w-full bg-gray-700 text-white rounded px-2 py-1 text-xs h-16"
                        />
                        <button
                          onClick={() => {
                            if (!vocabBulkInput.trim()) return;
                            const lines = vocabBulkInput.split('\n').filter(l => l.trim());
                            const newWords = lines.map(line => {
                              const parts = line.split('|').map(p => p.trim());
                              return {
                                darija: parts[0] || '',
                                meaning: parts[1] || '',
                                phonetic: parts[2] || '',
                                category: 'Other',
                                audioUrl: ''
                              };
                            }).filter(w => w.darija && w.meaning);
                            
                            if (newWords.length > 0) {
                              setSelectedFlow(prev => ({ 
                                ...prev, 
                                vocabulary: [...(prev.vocabulary || []), ...newWords] 
                              }));
                              setVocabBulkInput('');
                              showNotification(`Added ${newWords.length} words`, 'success');
                            }
                          }}
                          className="w-full bg-green-600 hover:bg-green-500 text-white rounded p-1 text-xs mt-1"
                        >
                          Import Words
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {/* Vocabulary List with Search Filter */}
                  <div className="space-y-2 max-h-48 overflow-y-auto mb-2">
                    {(selectedFlow.vocabulary || [])
                      .filter(word => {
                        if (!vocabSearchQuery) return true;
                        const q = vocabSearchQuery.toLowerCase();
                        return (word.darija || '').toLowerCase().includes(q) ||
                               (word.meaning || '').toLowerCase().includes(q) ||
                               (word.category || '').toLowerCase().includes(q);
                      })
                      .map((word, idx) => {
                        const actualIdx = (selectedFlow.vocabulary || []).indexOf(word);
                        return (
                          <div key={actualIdx} className="bg-gray-700 rounded p-2">
                            <div className="flex justify-between items-start mb-1">
                              <div className="flex items-center gap-1">
                                <span className="text-purple-400 text-xs font-bold">{word.darija || '...'}</span>
                                {word.category && word.category !== 'Other' && (
                                  <span className="text-xs px-1 py-0.5 bg-gray-600 rounded text-gray-400">{word.category}</span>
                                )}
                              </div>
                              <button
                                onClick={() => {
                                  const newVocab = (selectedFlow.vocabulary || []).filter((_, i) => i !== actualIdx);
                                  setSelectedFlow(prev => ({ ...prev, vocabulary: newVocab }));
                                }}
                                className="text-red-400 hover:text-red-300 text-xs"
                              >✕</button>
                            </div>
                            <input
                              type="text"
                              value={word.darija || ''}
                              onChange={(e) => {
                                const newVocab = [...(selectedFlow.vocabulary || [])];
                                newVocab[actualIdx] = { ...newVocab[actualIdx], darija: e.target.value };
                                setSelectedFlow(prev => ({ ...prev, vocabulary: newVocab }));
                              }}
                              placeholder="Darija word"
                              className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs mb-1"
                            />
                            <input
                              type="text"
                              value={word.meaning || ''}
                              onChange={(e) => {
                                const newVocab = [...(selectedFlow.vocabulary || [])];
                                newVocab[actualIdx] = { ...newVocab[actualIdx], meaning: e.target.value };
                                setSelectedFlow(prev => ({ ...prev, vocabulary: newVocab }));
                              }}
                              placeholder="English meaning"
                              className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs mb-1"
                            />
                            <div className="flex gap-1">
                              <input
                                type="text"
                                value={word.phonetic || ''}
                                onChange={(e) => {
                                  const newVocab = [...(selectedFlow.vocabulary || [])];
                                  newVocab[actualIdx] = { ...newVocab[actualIdx], phonetic: e.target.value };
                                  setSelectedFlow(prev => ({ ...prev, vocabulary: newVocab }));
                                }}
                                placeholder="Phonetic"
                                className="flex-1 bg-gray-600 text-white rounded px-2 py-1 text-xs"
                              />
                              <select
                                value={word.category || 'Other'}
                                onChange={(e) => {
                                  const newVocab = [...(selectedFlow.vocabulary || [])];
                                  newVocab[actualIdx] = { ...newVocab[actualIdx], category: e.target.value };
                                  setSelectedFlow(prev => ({ ...prev, vocabulary: newVocab }));
                                }}
                                className="bg-gray-600 text-white rounded px-1 py-1 text-xs"
                              >
                                {vocabCategories.map(cat => (
                                  <option key={cat} value={cat}>{cat}</option>
                                ))}
                              </select>
                            </div>
                            {/* Audio URL Field */}
                            <input
                              type="text"
                              value={word.audioUrl || ''}
                              onChange={(e) => {
                                const newVocab = [...(selectedFlow.vocabulary || [])];
                                newVocab[actualIdx] = { ...newVocab[actualIdx], audioUrl: e.target.value };
                                setSelectedFlow(prev => ({ ...prev, vocabulary: newVocab }));
                              }}
                              placeholder="🔊 Audio URL (Dropbox link)"
                              className="w-full bg-gray-600 text-white rounded px-2 py-1 text-xs mt-1"
                            />
                          </div>
                        );
                      })}
                  </div>
                  <button
                    onClick={() => {
                      const newWord = { darija: '', meaning: '', phonetic: '', category: 'Other', audioUrl: '' };
                      setSelectedFlow(prev => ({ ...prev, vocabulary: [...(prev.vocabulary || []), newWord] }));
                    }}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-purple-400 rounded p-2 text-xs"
                  >
                    + Add Word
                  </button>
                </div>
                
                <div className="space-y-2 mb-4">
                  <p className="text-gray-400 text-xs font-bold uppercase">Add Node</p>
                  {Object.entries(NODE_COLORS).filter(([key]) => key !== NODE_TYPES.START).map(([type, config]) => (
                    <button
                      key={type}
                      onClick={() => addNode(type)}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded text-white text-sm hover:opacity-80 transition-opacity"
                      style={{ backgroundColor: config.bg }}
                    >
                      <span>{config.icon}</span>
                      <span>{config.label}</span>
                    </button>
                  ))}
                </div>
                
                <div className="border-t border-gray-700 pt-4 space-y-2">
                  <button
                    onClick={() => saveFlow(selectedFlow)}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded text-sm font-bold"
                  >
                    💾 Save Flow
                  </button>
                  <button
                    onClick={() => deleteFlow(selectedFlow.id)}
                    className="w-full px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded text-sm"
                  >
                    🗑️ Delete Flow
                  </button>
                  <button
                    onClick={() => setShowHelp(true)}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm"
                  >
                    📖 Flow Guide
                  </button>
                </div>
              </div>

              {/* Help Guide Modal */}
              {showHelp && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={() => setShowHelp(false)}>
                  <div className="bg-gray-800 rounded-xl w-[720px] max-h-[90vh] overflow-y-auto p-6 shadow-2xl border border-gray-700" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-between mb-5 sticky top-0 bg-gray-800 pb-3 border-b border-gray-700">
                      <h2 className="text-white text-lg font-bold">📖 Flow Editor — Complete Guide</h2>
                      <button onClick={() => setShowHelp(false)} className="text-gray-400 hover:text-white text-2xl leading-none">&times;</button>
                    </div>

                    <div className="space-y-5 text-sm">

                      {/* Getting Started */}
                      <div className="bg-gradient-to-r from-blue-900/40 to-cyan-900/40 rounded-lg p-4 border border-blue-700/40">
                        <h3 className="text-blue-400 font-bold mb-3">Getting Started — How to Create a Conversation</h3>
                        <ol className="text-gray-300 space-y-2 list-decimal list-inside leading-relaxed">
                          <li>Click <strong>"+ New Flow"</strong> button on the Flows tab</li>
                          <li>Give it a name (English + Arabic), pick difficulty, bot gender, and script type</li>
                          <li>You'll see a canvas with a <strong>START</strong> node (green) already placed</li>
                          <li>Click <strong>"Message"</strong> in the left sidebar under "Add Node" — a new blue node appears</li>
                          <li>Click the blue node to select it — the right panel shows its editable fields</li>
                          <li>Fill in: Darija text (Latin), Arabic text, English translation</li>
                          <li>Continue adding nodes: Message → Response → Message → End</li>
                          <li>Click <strong>"Save Flow"</strong> when done</li>
                        </ol>
                      </div>

                      {/* Canvas controls */}
                      <div className="bg-gray-700/40 rounded-lg p-4 border border-gray-600/40">
                        <h3 className="text-blue-400 font-bold mb-3">Canvas Controls</h3>
                        <div className="grid grid-cols-2 gap-3 text-gray-300">
                          <div className="bg-gray-800/60 rounded p-2"><strong className="text-white">Scroll wheel</strong><br/>Zoom in/out on the canvas</div>
                          <div className="bg-gray-800/60 rounded p-2"><strong className="text-white">Middle mouse + drag</strong><br/>Pan/move the canvas around</div>
                          <div className="bg-gray-800/60 rounded p-2"><strong className="text-white">Click a node</strong><br/>Select it — properties panel opens on the right</div>
                          <div className="bg-gray-800/60 rounded p-2"><strong className="text-white">Drag a node</strong><br/>Move it to a new position</div>
                          <div className="bg-gray-800/60 rounded p-2"><strong className="text-white">Click empty space</strong><br/>Deselect current node</div>
                          <div className="bg-gray-800/60 rounded p-2"><strong className="text-white">Fullscreen button</strong><br/>Expand editor to fill the entire screen</div>
                        </div>
                      </div>

                      {/* Node Types — Detailed */}
                      <div className="bg-gray-700/40 rounded-lg p-4 border border-gray-600/40">
                        <h3 className="text-blue-400 font-bold mb-4">All Node Types — Detailed Guide</h3>
                        <div className="space-y-4">

                          {/* START */}
                          <div className="bg-green-900/20 rounded-lg p-3 border border-green-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">▶️</span><strong className="text-green-400 text-base">START</strong></div>
                            <p className="text-gray-300 mb-2">The entry point. Every flow has exactly one. It's auto-created when you make a new flow.</p>
                            <p className="text-gray-400 text-xs"><strong>How to use:</strong> Don't edit this node. Just make sure a connection leads from START to your first MESSAGE node. The flow begins here automatically.</p>
                          </div>

                          {/* MESSAGE */}
                          <div className="bg-blue-900/20 rounded-lg p-3 border border-blue-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">💬</span><strong className="text-blue-400 text-base">MESSAGE</strong></div>
                            <p className="text-gray-300 mb-2">The bot sends a message to the user. This is the most-used node — it's the bot talking.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Click "Message" in the Add Node sidebar</li>
                                <li>Click the new node on the canvas to select it</li>
                                <li>In the right panel, fill in:
                                  <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                                    <li><strong>Darija (Latin):</strong> The main text in Latin script, e.g. "Salam, labas 3lik?"</li>
                                    <li><strong>Arabic:</strong> Same text in Arabic script, e.g. "سلام، لاباس عليك؟"</li>
                                    <li><strong>Translation:</strong> English meaning, e.g. "Hello, how are you?"</li>
                                    <li><strong>Emotion:</strong> Bot's facial expression (neutral, happy, excited, thinking, surprised, sad)</li>
                                    <li><strong>Audio URL:</strong> (Optional) Dropbox link to pronunciation audio</li>
                                    <li><strong>Image URL:</strong> (Optional) Dropbox link to an image shown in the chat bubble</li>
                                    <li><strong>GIF URL:</strong> (Optional) GIPHY/Tenor link to an animated GIF</li>
                                  </ul>
                                </li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> The message appears in the chat as a bot bubble, then instantly advances to the next connected node.</p>
                            </div>
                          </div>

                          {/* RESPONSE */}
                          <div className="bg-yellow-900/20 rounded-lg p-3 border border-yellow-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">❓</span><strong className="text-yellow-400 text-base">RESPONSE</strong></div>
                            <p className="text-gray-300 mb-2">Waits for the user to answer. This is how users interact with the conversation.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add a Response node after a Message node</li>
                                <li>Set the <strong>Prompt</strong> — the question text (e.g. "What do you say?")</li>
                                <li>Choose <strong>Input Type</strong>:
                                  <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                                    <li><strong>Choice:</strong> User picks from clickable buttons. Add 2-4 options. Mark one as "Correct"</li>
                                    <li><strong>Text:</strong> User types freely. The Smart Bot matches their answer against intents</li>
                                  </ul>
                                </li>
                                <li>For each option, set:
                                  <ul className="list-disc list-inside ml-4 mt-1 space-y-0.5">
                                    <li><strong>Text:</strong> What the button/option says</li>
                                    <li><strong>Arabic text:</strong> Arabic version</li>
                                    <li><strong>Is Correct:</strong> Toggle ON for the right answer</li>
                                    <li><strong>Points:</strong> Score earned for picking this</li>
                                    <li><strong>Next Node:</strong> (Optional) Route to a specific node instead of the default next</li>
                                    <li><strong>Intent:</strong> (For text mode) Semantic category like "greeting", "thanks", "yes"</li>
                                  </ul>
                                </li>
                                <li>Set <strong>Wrong Path Node</strong> to redirect wrong answers (e.g. to a hint message)</li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> The conversation PAUSES here until the user responds. After answering, it follows the connection to the next node (or the wrong path if incorrect).</p>
                            </div>
                          </div>

                          {/* TIP */}
                          <div className="bg-purple-900/20 rounded-lg p-3 border border-purple-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">💡</span><strong className="text-purple-400 text-base">TIP</strong></div>
                            <p className="text-gray-300 mb-2">Shows a quick popup hint, cultural note, or grammar tip.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add a Tip node (usually before a Response node to help the user)</li>
                                <li>Write the <strong>Tip Text</strong> — keep it short and helpful</li>
                                <li>(Optional) Add <strong>Arabic tip text</strong> for Arabic script users</li>
                                <li>Choose <strong>Tip Type:</strong> info (blue), warning (yellow), or success (green)</li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> A popup appears for 2 seconds, then auto-advances. Good for: grammar hints, cultural context, word explanations.</p>
                              <p><strong>Example:</strong> "In Darija, 'labas' means both 'fine' and 'how are you' depending on context."</p>
                            </div>
                          </div>

                          {/* ACHIEVEMENT */}
                          <div className="bg-pink-900/20 rounded-lg p-3 border border-pink-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">🏆</span><strong className="text-pink-400 text-base">ACHIEVEMENT</strong></div>
                            <p className="text-gray-300 mb-2">Awards the user a badge with points — a celebration moment.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add after a correct answer or at a milestone point</li>
                                <li>Set <strong>Achievement Name</strong> — e.g. "Greeting Master", "First Words"</li>
                                <li>Set <strong>Icon</strong> — emoji for the badge (default: 🏆)</li>
                                <li>Set <strong>Points</strong> — score to award (e.g. 10, 25, 50)</li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> A celebration popup shows for 3 seconds with the badge name, icon, and points. The user's score increases. Then auto-advances to the next node.</p>
                              <p><strong>Best placement:</strong> After a RESPONSE (correct answer), before the next MESSAGE.</p>
                            </div>
                          </div>

                          {/* AUDIO */}
                          <div className="bg-teal-900/20 rounded-lg p-3 border border-teal-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">🔊</span><strong className="text-teal-400 text-base">AUDIO</strong></div>
                            <p className="text-gray-300 mb-2">Plays a standalone audio clip — for pronunciation practice or ambient sounds.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add an Audio node where you want a sound to play</li>
                                <li>Paste the <strong>Audio URL</strong> (Dropbox direct link)</li>
                                <li>Set <strong>Auto Play:</strong> Yes (plays automatically) or No (user clicks play)</li>
                              </ol>
                              <p className="mt-2"><strong>Note:</strong> For audio attached to a message, use the Audio URL field inside a MESSAGE node instead. Use standalone Audio nodes when you want audio without a text bubble.</p>
                            </div>
                          </div>

                          {/* VOCABULARY */}
                          <div className="bg-indigo-900/20 rounded-lg p-3 border border-indigo-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">📚</span><strong className="text-indigo-400 text-base">VOCABULARY</strong></div>
                            <p className="text-gray-300 mb-2">Teaches a new Darija word — adds it to the dictionary panel on the right.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add a Vocabulary node when you want to highlight a word</li>
                                <li>Fill in: <strong>Darija word</strong>, <strong>Arabic version</strong>, <strong>English meaning</strong>, <strong>Pronunciation</strong></li>
                                <li>(Optional) Add an <strong>Audio URL</strong> for pronunciation</li>
                                <li>(Optional) Add an <strong>Example sentence</strong></li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> The word appears in the dictionary panel (right side of chat). Auto-advances instantly to the next node. The word stays visible in the panel so the user can reference it.</p>
                              <p><strong>Tip:</strong> Place Vocabulary nodes BEFORE a Response node so the user sees the new word before they need to use it.</p>
                            </div>
                          </div>

                          {/* DELAY */}
                          <div className="bg-gray-800/60 rounded-lg p-3 border border-gray-600/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">⏱️</span><strong className="text-gray-300 text-base">DELAY</strong></div>
                            <p className="text-gray-300 mb-2">A spacer/pause node for organizing your flow visually.</p>
                            <div className="text-gray-400 text-xs">
                              <p><strong>How to use:</strong> Add between nodes to space them out on the canvas. Useful for visual clarity when your flow gets complex. Currently acts as a pass-through (no actual pause in the chat).</p>
                            </div>
                          </div>

                          {/* CHALLENGE */}
                          <div className="bg-red-900/20 rounded-lg p-3 border border-red-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">🎯</span><strong className="text-red-400 text-base">CHALLENGE</strong></div>
                            <p className="text-gray-300 mb-2">A mini-quiz question with a special challenge card in the chat.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add a Challenge node</li>
                                <li>Choose <strong>Challenge Type:</strong> choice, unscramble, or fill-in-the-blank</li>
                                <li>Write the <strong>Question</strong> (+ Arabic version)</li>
                                <li>Add <strong>Options</strong> — the possible answers</li>
                                <li>Set the <strong>Correct Answer</strong></li>
                                <li>Set <strong>Points</strong> for correct answers</li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> Shows a special amber-colored challenge card in the chat. The conversation PAUSES until the user answers. Correct answers earn points.</p>
                              <p><strong>Difference from Response:</strong> Challenge has a distinct visual style (quiz card) and is meant for testing knowledge. Response is for conversation flow (dialogue choices).</p>
                            </div>
                          </div>

                          {/* BRANCH */}
                          <div className="bg-cyan-900/20 rounded-lg p-3 border border-cyan-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">🔀</span><strong className="text-cyan-400 text-base">BRANCH</strong></div>
                            <p className="text-gray-300 mb-2">Randomly picks a path — adds variety so the same flow plays differently each time.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add a Branch node where you want random paths</li>
                                <li>Add <strong>Branches</strong> — each with a weight (probability) and next node</li>
                                <li>Higher weight = more likely to be chosen</li>
                                <li>Toggle <strong>Randomize</strong> on/off</li>
                              </ol>
                              <p className="mt-2"><strong>Example:</strong> Branch with 2 paths (weight 50/50) — half the time the bot talks about food, half about travel. Makes the conversation feel fresh on replay.</p>
                            </div>
                          </div>

                          {/* END */}
                          <div className="bg-rose-900/20 rounded-lg p-3 border border-rose-700/30">
                            <div className="flex items-center gap-2 mb-2"><span className="text-lg">🔚</span><strong className="text-rose-400 text-base">END</strong></div>
                            <p className="text-gray-300 mb-2">Finishes the conversation and shows a completion screen.</p>
                            <div className="text-gray-400 text-xs space-y-1">
                              <p><strong>How to use:</strong></p>
                              <ol className="list-decimal list-inside space-y-1 ml-2">
                                <li>Add an End node at the end of every conversation path</li>
                                <li>Set a <strong>Completion Message</strong> (e.g. "Great job! You learned 5 new words!")</li>
                              </ol>
                              <p className="mt-2"><strong>Behavior:</strong> Shows "Conversation complete!" message. In Story Mode, automatically loads the next chapter.</p>
                              <p><strong>Important:</strong> Every path in your flow should end with an END node. If a path has no END, the conversation will stop without a completion message.</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Media — Detailed how-to */}
                      <div className="bg-gradient-to-r from-teal-900/40 to-blue-900/40 rounded-lg p-4 border border-teal-700/40">
                        <h3 className="text-teal-400 font-bold mb-3">Adding Media — Step by Step</h3>
                        <div className="space-y-4 text-gray-300">
                          <div>
                            <p className="font-bold text-white mb-1">Audio (Pronunciation / Sound Effects)</p>
                            <ol className="list-decimal list-inside space-y-1 text-xs">
                              <li>Record or download an audio file (.mp3, .wav, .ogg)</li>
                              <li>Upload it to your <strong>Dropbox</strong> account</li>
                              <li>Right-click the file in Dropbox → <strong>"Copy Link"</strong></li>
                              <li>The link looks like: <code className="bg-gray-700 px-1 rounded text-[10px]">https://www.dropbox.com/...?dl=0</code></li>
                              <li>Change <code className="bg-gray-700 px-1 rounded text-[10px]">?dl=0</code> to <code className="bg-gray-700 px-1 rounded text-[10px]">?raw=1</code> at the end</li>
                              <li>Paste the modified link into the <strong>Audio URL</strong> field</li>
                              <li>Click the 🔊 button next to the field to preview the audio</li>
                            </ol>
                          </div>
                          <div>
                            <p className="font-bold text-white mb-1">Images (Photos / Illustrations)</p>
                            <ol className="list-decimal list-inside space-y-1 text-xs">
                              <li>Upload your image to <strong>Dropbox</strong></li>
                              <li>Right-click → <strong>"Copy Link"</strong></li>
                              <li>Change <code className="bg-gray-700 px-1 rounded text-[10px]">?dl=0</code> to <code className="bg-gray-700 px-1 rounded text-[10px]">?raw=1</code></li>
                              <li>Paste into the <strong>Image URL</strong> field on a MESSAGE node</li>
                              <li>A preview thumbnail appears below the field</li>
                              <li>In the chat, the image shows inside the bot's message bubble</li>
                            </ol>
                            <p className="text-xs text-gray-500 mt-1">Supported: .jpg, .png, .gif, .webp</p>
                          </div>
                          <div>
                            <p className="font-bold text-white mb-1">GIFs (Animated Images)</p>
                            <ol className="list-decimal list-inside space-y-1 text-xs">
                              <li>Go to <strong>giphy.com</strong> (or <strong>tenor.com</strong>)</li>
                              <li>Search for a GIF (e.g. "hello", "thinking", "celebration")</li>
                              <li>Click on the GIF you want</li>
                              <li><strong>Right-click</strong> the GIF → <strong>"Copy Image Address"</strong> (NOT "Copy Link")</li>
                              <li>The URL should look like: <code className="bg-gray-700 px-1 rounded text-[10px]">https://media.giphy.com/media/.../giphy.gif</code></li>
                              <li>Paste into the <strong>GIF URL</strong> field on a MESSAGE node</li>
                              <li>An animated preview appears below the field</li>
                            </ol>
                            <p className="text-xs text-gray-500 mt-1">Free to use. GIPHY is used by Facebook, Slack, Discord.</p>
                          </div>
                          <div className="bg-yellow-900/20 rounded p-2 border border-yellow-700/30">
                            <p className="text-yellow-400 text-xs font-bold">Allowed Sources (Security)</p>
                            <p className="text-xs text-gray-400">URLs must be from: Dropbox, GIPHY, Tenor, or Imgur. Other domains are rejected for security. If your URL doesn't work, check the domain.</p>
                          </div>
                        </div>
                      </div>

                      {/* Story Mode */}
                      <div className="bg-gray-700/40 rounded-lg p-4 border border-gray-600/40">
                        <h3 className="text-blue-400 font-bold mb-3">Story Mode — Multi-Chapter Conversations</h3>
                        <div className="text-gray-300 text-xs space-y-2">
                          <p>Stories chain multiple flows together as chapters. The user plays Chapter 1, and when it ends, Chapter 2 automatically starts.</p>
                          <ol className="list-decimal list-inside space-y-1">
                            <li>Go to the <strong>Stories</strong> tab</li>
                            <li>Click <strong>"+ New Story"</strong></li>
                            <li>Give it a name, description, difficulty, and cover image</li>
                            <li>Add flows as chapters (drag to reorder)</li>
                            <li>Each flow's END node triggers the next chapter</li>
                          </ol>
                          <p><strong>Tip:</strong> Make each chapter 3-5 minutes. Keep a consistent bot and theme across chapters.</p>
                        </div>
                      </div>

                      {/* Smart Intent */}
                      <div className="bg-gradient-to-r from-cyan-900/30 to-blue-900/30 rounded-lg p-4 border border-cyan-700/40">
                        <h3 className="text-cyan-400 font-bold mb-3">🧠 Smart Intent — Understanding User Meaning</h3>
                        <div className="text-gray-300 text-xs space-y-3">
                          <p>Smart Intent lets the bot understand what the user <strong>means</strong>, not just match exact text. If the correct answer is "Sbah l'khir!" but the user types "hello" or "salam", the bot recognizes it as a greeting and accepts it.</p>

                          <div>
                            <p className="text-cyan-400 font-bold mb-1">How to Enable Smart Intent (Step by Step):</p>
                            <ol className="list-decimal list-inside space-y-1 ml-2">
                              <li>Select a <strong>RESPONSE</strong> node in your flow</li>
                              <li>Change <strong>Input Type</strong> from "Multiple Choice" to <strong>"Text Input"</strong> — this lets users type freely instead of clicking buttons</li>
                              <li>On the <strong>correct option</strong>, find the <strong>🧠 Intent</strong> dropdown</li>
                              <li>Select the matching intent (e.g., "Greeting" for greetings, "I'm Fine" for health responses)</li>
                              <li><strong>Save</strong> the flow — done!</li>
                            </ol>
                          </div>

                          <div>
                            <p className="text-cyan-400 font-bold mb-1">Available Intents:</p>
                            <div className="grid grid-cols-2 gap-1 ml-2">
                              <p><strong>Greeting:</strong> salam, hello, marhba, sbah l'khir, ahlan</p>
                              <p><strong>Goodbye:</strong> bslama, bye, tsbah 3la khir</p>
                              <p><strong>Thanks:</strong> shukran, merci, thank you, barak allahu fik</p>
                              <p><strong>I'm Fine:</strong> labas, hamdullah, bikhir, mezyan</p>
                              <p><strong>Yes:</strong> iyeh, wakha, naam, ah, ok, safi</p>
                              <p><strong>No:</strong> la, lla, no, mashi</p>
                              <p><strong>Introduction:</strong> smiti..., ana mn..., my name is...</p>
                              <p><strong>Please:</strong> 3afak, min fadlak, please, svp</p>
                              <p><strong>Ordering:</strong> bghit, atini, i want, give me</p>
                              <p><strong>Apology:</strong> smahli, sorry, pardon</p>
                            </div>
                          </div>

                          <div className="bg-gray-800/60 rounded p-3">
                            <p className="text-cyan-400 font-bold mb-1">Example Setup:</p>
                            <div className="space-y-1 font-mono">
                              <p className="text-blue-400">💬 MESSAGE: "Salam! Sbah l'khir!"</p>
                              <p className="text-gray-500 ml-4">↓</p>
                              <p className="text-yellow-400">❓ RESPONSE: Input Type = <strong>Text Input</strong></p>
                              <p className="text-gray-400 ml-6">Correct option: "Sbah l'khir!" → 🧠 Intent: <strong>Greeting</strong></p>
                              <p className="text-gray-500 ml-4">↓</p>
                              <p className="text-green-400">Result: User types "salam", "hello", or "marhba" → Accepted!</p>
                            </div>
                          </div>

                          <div className="bg-yellow-900/20 rounded p-2 border border-yellow-700/30">
                            <p className="text-yellow-400 text-xs font-bold">Important Notes</p>
                            <ul className="text-xs text-gray-400 space-y-0.5 ml-2 list-disc list-inside">
                              <li>Smart Intent <strong>only works with "Text Input"</strong> mode, not "Multiple Choice" (buttons)</li>
                              <li>Only the <strong>correct option</strong> needs an intent tag — wrong options don't need one</li>
                              <li>You can add/edit/delete intents and their phrases in the <strong>"🧠 Smart Intents" tab</strong> of the editor</li>
                              <li>The bot shows special feedback like "Mezyan! Great greeting!" when intent matches</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      {/* Best Practices */}
                      <div className="bg-gray-700/40 rounded-lg p-4 border border-gray-600/40">
                        <h3 className="text-blue-400 font-bold mb-3">Best Practices</h3>
                        <ul className="text-gray-300 space-y-2 text-xs">
                          <li><strong>Always:</strong> START → MESSAGE → RESPONSE → ... → END. Every path needs an END node.</li>
                          <li><strong>Fill both scripts:</strong> Always write Darija in Latin AND Arabic so users can switch between them.</li>
                          <li><strong>Add translations:</strong> Every MESSAGE should have an English translation for learners.</li>
                          <li><strong>Use Tips before hard questions:</strong> Place a TIP node before a RESPONSE to give the user context.</li>
                          <li><strong>Reward correct answers:</strong> Add ACHIEVEMENT nodes after correct responses to motivate users.</li>
                          <li><strong>Add vocabulary first:</strong> Place VOCABULARY nodes before you use a new word in a MESSAGE.</li>
                          <li><strong>Test your flow:</strong> After saving, play it as a user to check timing, spelling, and logic.</li>
                          <li><strong>Keep messages short:</strong> 1-2 sentences per message. Long messages are hard to read in chat bubbles.</li>
                          <li><strong>Use emotions:</strong> Set bot emotions (happy, thinking, excited) to make conversations feel natural.</li>
                        </ul>
                      </div>

                      {/* Example */}
                      <div className="bg-gradient-to-r from-purple-900/40 to-pink-900/40 rounded-lg p-4 border border-purple-700/40">
                        <h3 className="text-purple-400 font-bold mb-3">Example Flow — "Greeting at a Cafe"</h3>
                        <div className="text-gray-300 text-xs space-y-1 font-mono bg-gray-900/60 rounded p-3">
                          <p className="text-green-400">▶️ START</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-blue-400">💬 MESSAGE — Bot: "Salam! Mrhba bik f l-qhwa" (Welcome to the cafe)</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-indigo-400">📚 VOCABULARY — Word: "qhwa" = "cafe/coffee"</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-blue-400">💬 MESSAGE — Bot: "Chnou bghiti tshreb?" (What would you like to drink?)</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-purple-400">💡 TIP — "Use 'bghit' (I want) + the drink name"</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-yellow-400">❓ RESPONSE — Options: "Bghit atay" ✓ / "Bghit qhwa" ✓ / "Ma3rftsh" ✗</p>
                          <p className="text-gray-500 ml-4">↓ (correct)</p>
                          <p className="text-pink-400">🏆 ACHIEVEMENT — "Cafe Pro" +10 pts</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-blue-400">💬 MESSAGE — Bot: "Mzyan! Daba njibha lik" (Great! I'll bring it to you)</p>
                          <p className="text-gray-500 ml-4">↓</p>
                          <p className="text-rose-400">🔚 END — "Great job! You ordered a drink in Darija!"</p>
                        </div>
                      </div>
                    </div>

                    <button onClick={() => setShowHelp(false)} className="mt-5 w-full px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium">
                      Got it!
                    </button>
                  </div>
                </div>
              )}

              {/* Canvas */}
              <div 
                ref={canvasRef}
                className="flex-1 bg-gray-900 relative overflow-hidden"
                onMouseMove={handleCanvasMouseMove}
                onMouseDown={handleCanvasMouseDown}
                onMouseUp={handleCanvasMouseUp}
                onMouseLeave={handleCanvasMouseUp}
                onWheel={handleCanvasWheel}
                onClick={() => setSelectedNode(null)}
                onContextMenu={(e) => e.preventDefault()}
                style={{
                  backgroundImage: 'radial-gradient(circle, #374151 1px, transparent 1px)',
                  backgroundSize: `${20 * zoom}px ${20 * zoom}px`,
                  backgroundPosition: `${pan.x}px ${pan.y}px`,
                  cursor: draggingNode ? 'grabbing' : (isPanning ? 'grabbing' : 'grab')
                }}
              >
                {renderEdges()}
                {selectedFlow.nodes?.map(renderNode)}
                
                {/* Zoom controls and instructions */}
                <div className="absolute bottom-4 left-4 flex gap-2 items-center">
                  <button
                    onClick={() => setZoom(z => Math.max(0.3, z - 0.1))}
                    className="w-8 h-8 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  >
                    -
                  </button>
                  <span className="px-2 py-1 bg-gray-700 text-white rounded text-sm">
                    {Math.round(zoom * 100)}%
                  </span>
                  <button
                    onClick={() => setZoom(z => Math.min(2, z + 0.1))}
                    className="w-8 h-8 bg-gray-700 hover:bg-gray-600 text-white rounded"
                  >
                    +
                  </button>
                  <span className="ml-4 px-3 py-1 bg-gray-800 text-gray-400 rounded text-xs">
                    🖱️ Scroll to zoom • Middle-click to pan • Drag nodes to move
                  </span>
                </div>
                
                {/* Fullscreen toggle + Reset view */}
                <div className="absolute bottom-4 right-4 flex gap-2">
                  <button
                    onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                    className="px-3 py-1 bg-gray-700 hover:bg-gray-600 text-white rounded text-sm"
                  >
                    Reset View
                  </button>
                  <button
                    onClick={() => setIsFullscreen(!isFullscreen)}
                    className={`px-3 py-1 ${isFullscreen ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-700 hover:bg-gray-600'} text-white rounded text-sm flex items-center gap-1`}
                  >
                    {isFullscreen ? '⏏️ Exit' : '⛶ Fullscreen'}
                  </button>
                </div>
                
                {/* Fullscreen header */}
                {isFullscreen && (
                  <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                    <div className="bg-gray-800/90 px-4 py-2 rounded-lg pointer-events-auto">
                      <h3 className="text-white font-bold">{selectedFlow.name}</h3>
                      <p className="text-gray-400 text-xs">{selectedFlow.nodes?.length || 0} nodes • {selectedFlow.edges?.length || 0} connections</p>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Node editor */}
              {renderNodeEditor()}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ConversationChallengeEditor;
